/* eslint-disable quotes */
// Purvodaya Solar — translations (English default, Hindi, Marathi).
// Flat, single-source dictionary. Admin pages remain English-only.

export const translations = {
  en: {
    nav: {
      how: "How it Works",
      services: "Services",
      subsidy: "Subsidy",
      savings: "Savings",
      process: "Process",
      faq: "FAQ",
      admin: "Admin",
      cta: "Get Free Quote",
      menu: "Menu",
      residential: "Residential",
      commercial: "Commercial",
      housing: "Housing Societies",
    },
    hero: {
      badge: "MNRE Empanelled · PM Surya Ghar Ready",
      title_pre: "Power your home with",
      title_highlight: "sunlight",
      title_post1: "and slash your",
      title_post2: "electricity bill.",
      subtitle_pre:
        "Purvodaya Solar designs, installs and services rooftop solar systems for homes, farms and businesses across India — with end-to-end support for",
      subtitle_and: "and",
      subtitle_post: "subsidies.",
      cta_quote: "Get a Free Rooftop Quote",
      cta_calc: "Calculate Your Savings",
      stat1_k: "₹78,000",
      stat1_v: "Max subsidy (residential)",
      stat2_k: "25 yrs",
      stat2_v: "Panel performance",
      stat3_k: "4–5 yrs",
      stat3_v: "Typical payback",
      live_label: "Avg. monthly savings",
      live_value: "₹4,120",
      live_caption: "for a typical 3 kW home after PM Surya Ghar subsidy",
      this_month: "This year",
      saved: "average household savings",
      co2: "t CO₂ avoided yearly",
    },
    trust: {
      heading: "Working with Tier-1 panel & inverter brands",
    },
    services: {
            cta_explore: "Explore Benefits",
      cta_street_b2b: "Talk to us for B2B deployments →",
eyebrow: "Complete Solar Solutions",
      title_pre: "One partner.",
      title_hl: "Every solar need",
      title_post: "covered.",
      subtitle:
        "From rooftops to factories, EV chargers to street lights — Purvodaya Solar delivers end-to-end clean-energy solutions, tailored to your space and budget.",
      cta: "Explore this service",
      items: [
        {
          key: "rooftop",
          title: "Residential Rooftop Solar",
          desc: "Power every fan, AC, fridge, geyser and pump in your home directly from the sun — slash your monthly electricity bill by 70–95% and claim up to ₹78,000 subsidy under PM Surya Ghar.",
          highlight: "Cut bills 70–95%",
          bullets: [
            "Runs AC, geyser, pump & all appliances",
            "₹78,000 central subsidy",
            "5-year payback, 25-year warranty",
          ],
        },
        {
          key: "commercial",
          title: "Commercial & Industrial Solar",
          desc: "MW-scale rooftop & ground-mount systems for factories, offices and warehouses. Slash power costs by 60% and claim 40% accelerated depreciation.",
          highlight: "Up to 40% Tax Saving",
          bullets: [
            "CAPEX or zero-cost OPEX models",
            "40% AD in Year-1",
            "60%+ bill reduction",
          ],
        },
        {
          key: "ev",
          title: "EV Charging Solutions",
          desc: "Solar-powered AC & DC fast EV chargers for homes, offices, malls and highway plazas — pair with rooftop solar for true zero-cost driving.",
          highlight: "AC 7kW · DC 60kW+",
          bullets: [
            "AC 7 kW · DC 60 kW+ chargers",
            "Solar + grid hybrid",
            "Pay-per-use software included",
          ],
        },
        {
          key: "bess",
          title: "Battery Energy Storage (BESS)",
          desc: "Lithium-ion battery banks that store surplus solar by day and power your home or factory through cloudy hours, evenings and outages — true 24×7 clean energy.",
          highlight: "24×7 clean power",
          bullets: [
            "Stores energy for low-solar hours",
            "5 kWh home → 1 MWh+ industrial",
            "10-year warranty · auto-switchover",
          ],
        },
        {
          key: "street",
          title: "Solar Street Lighting",
          desc: "All-in-one solar street lights for factory premises, corporate parks, IT campuses, warehouses, gated societies and rural roads — dusk-to-dawn lighting with zero wiring, zero grid load and zero electricity bill.",
          highlight: "B2B & Off-grid ready",
          bullets: [
            "Factory perimeters, corporate parks & IT campuses",
            "Zero trenching — perfect for new campus phases",
            "Electrifies remote villages & panchayat roads",
            "Auto dusk-to-dawn · motion sensing · 5-yr warranty",
          ],
        },
        {
          key: "society",
          title: "Housing Society Solar",
          desc: "Group rooftop & common-area solar for apartment complexes — eligible for PM Surya Ghar Yojana benefits on individual flats plus society savings.",
          highlight: "RWA-friendly",
          bullets: [
            "₹18,000/kW society subsidy",
            "Per-flat ₹78,000 PM Surya Ghar",
            "Lifts, pumps, lobby on solar",
          ],
        },
        {
          key: "heater",
          title: "Solar Water Heaters",
          desc: "Evacuated glass-tube solar water heaters for homes, hostels & hotels — get hot water 365 days, cut geyser electricity bills entirely.",
          highlight: "100–500 LPD",
          bullets: [
            "Hot water 365 days",
            "Cuts geyser bills entirely",
            "Evacuated-tube technology",
          ],
        },
      ],
    },
    how: {
      eyebrow: "How Rooftop Solar Works",
      title_pre: "From",
      title_hl: "sunshine",
      title_mid: "to savings — in four simple steps.",
      subtitle:
        "Rooftop solar isn't magic, it's beautifully simple physics paired with smart metering. Here's what happens every day, automatically.",
      steps: [
        {
          title: "Sunlight hits the panels",
          desc:
            "High-efficiency mono PERC panels on your rooftop absorb daylight and generate DC electricity.",
        },
        {
          title: "Inverter converts to AC",
          desc:
            "Smart grid-tied inverter converts DC to AC that runs every appliance in your home — at 96% efficiency.",
        },
        {
          title: "Your home uses it first",
          desc:
            "Fans, lights, AC, pumps — they draw from the sun during the day, cutting grid usage dramatically.",
        },
        {
          title: "Net meter banks the rest",
          desc:
            "Extra units flow back to the grid. At month end, you are billed only for the net consumption.",
        },
      ],
      kpis: [
        { k: "70–90%", v: "Reduction in monthly electricity bill after installation." },
        { k: "₹0 EMI", v: "Flexible finance options available via partner banks." },
        { k: "25 years", v: "Panel output warranty (minimum 80% performance)." },
      ],
    },
    subsidy: {
      eyebrow: "Government Subsidies",
      title_pre: "Claim up to",
      title_hl: "₹78,000",
      title_post: "off — paperwork handled by us.",
      subtitle:
        "Whether it's your home or farm, the Government of India offers generous subsidies to accelerate solar adoption. Purvodaya Solar files and tracks every application from start to disbursal.",
      tab_res: "Residential · PM Surya Ghar",
      tab_agri: "Agriculture · PM-KUSUM",
      res_title: "PM Surya Ghar: Muft Bijli Yojana",
      res_desc:
        "Launched by the Govt. of India, this scheme provides central financial assistance directly to your bank account after installation of a rooftop solar plant at your home.",
      slab_labels: ["Up to 1 kW", "Up to 2 kW", "3 kW & above"],
      slab_notes: ["₹30,000 per kW", "₹30,000 per kW", "Maximum cap"],
      res_bullets: [
        "Applicable for rooftop PV systems on residential houses.",
        "Free 300 units of electricity per month for eligible families.",
        "Direct bank transfer after commissioning & inspection.",
        "Subsidy filed through official national portal by our team.",
      ],
      ghs_title: "Group Housing Societies (GHS / RWAs)",
      ghs_intro:
        "Under PMSGY, the Central Government provides ₹18,000 per kW for grid-connected rooftop solar powering common facilities — lifts, water pumps, streetlights, lobby & corridor lighting.",
      ghs_structure_label: "Subsidy structure",
      ghs_structure: [
        "₹18,000 / kW of installed common-area solar capacity.",
        "Capped at 500 kW per society — i.e. up to ₹90 Lakhs subsidy.",
        "Allowed common capacity = 3 kW × number of flats (inclusive of individual rooftop plants).",
      ],
      ghs_eligibility_label: "Eligibility checklist",
      ghs_eligibility: [
        "CHS / RWA must be legally registered with its own PAN.",
        "Active common-service electricity meter in the society's name.",
        "Dedicated society bank account for direct subsidy transfer.",
        "Only Made-in-India (DCR-compliant) panels — no imported modules.",
      ],
      // Sub-tabs inside Residential PMSGY
      sub_individual: "Individual Homes",
      sub_society: "Group Housing Societies",
      // Right-side green box variants
      ind_box_label: "Your take-home savings",
      ind_box_amount: "₹78,000",
      ind_box_caption:
        "Maximum central subsidy on a 3 kW+ residential rooftop system under PM Surya Ghar. State incentives may apply on top.",
      ind_example_title: "Example · 3 kW system",
      ind_example_rows: [
        { label: "System cost", value: "₹1,80,000" },
        { label: "Subsidy", value: "₹78,000" },
      ],
      ind_example_net_label: "You pay",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "What you also get",
      ind_extras: [
        "Up to 300 free units / month for eligible families",
        "Net-metering credit for surplus generation",
        "25-year panel performance warranty",
        "Free PM Surya Ghar portal filing by Purvodaya",
      ],

      ghs_box_label: "Society-wide savings",
      ghs_box_amount: "Up to ₹90 L",
      ghs_box_caption:
        "Maximum PMSGY central subsidy a single housing society can claim — calculated at ₹18,000 per kW of installed common-area solar, capped at 500 kW.",
      ghs_example_title: "Worked example · 100-flat society",
      ghs_example_rows: [
        { label: "Allowed common capacity (3 kW × 100 flats)", value: "300 kW" },
        { label: "Indicative project cost (₹50,000 / kW)", value: "₹1.50 Cr" },
        { label: "PMSGY subsidy (₹18,000 / kW × 300 kW)", value: "₹54 L" },
        { label: "Annual common-area bill saved", value: "~₹22 L" },
      ],
      ghs_example_net_label: "Society net outflow",
      ghs_example_net_value: "₹96 L",
      ghs_example_payback: "Payback in 4–5 years · 20+ years of free common-area power",
      ghs_documents_label: "Documents we need from your society",
      ghs_documents: [
        "Society registration certificate + PAN",
        "Latest 3 months common-area electricity bill",
        "Society bank-account passbook (front page)",
        "AGM / EGM resolution approving the project",
        "Building plan & rooftop layout (we can survey)",
      ],
      read_official: "Read official scheme",
      savings_label: "Your take-home savings",
      savings_amount: "₹78,000",
      savings_note:
        "Maximum central subsidy on a 3 kW+ residential rooftop system under PM Surya Ghar. State incentives may apply on top.",
      example_label: "Example · 3 kW system",
      example_cost: "System cost",
      example_subsidy: "Subsidy",
      example_pay: "You pay",
      agri_title: "PM-KUSUM Yojana",
      agri_desc:
        "Pradhan Mantri Kisan Urja Suraksha evam Utthaan Mahabhiyan helps farmers install solar pumps and solarise their agricultural feeders — reducing diesel dependency and providing reliable daytime power.",
      agri_components: [
        { k: "Comp-A", v: "Grid-connected solar plants on barren land" },
        { k: "Comp-B", v: "Stand-alone solar water pumps" },
        { k: "Comp-C", v: "Solarisation of existing grid pumps" },
      ],
      agri_bullets: [
        "Up to 60% central + state subsidy on solar pumps.",
        "30% bank loan support; farmer contribution only ~10%.",
        "Sell surplus power to DISCOM for additional income.",
        "Installation & DBT assistance handled by Purvodaya.",
      ],
      farmer_benefit: "Farmer's benefit",
      farmer_amount: "Up to 60% off",
      farmer_note:
        "On solar pump installation, plus an income stream by selling surplus power back to the grid.",
    },
    calc: {
      eyebrow: "Savings Calculator",
      title_pre: "See exactly",
      title_hl: "what you'll save",
      title_post: "— tap plus & minus to begin.",
      subtitle:
        "Pick the appliances you use, adjust the hours, and we'll recommend a right-sized solar system with live subsidy & payback estimates.",
      tab_res: "Residential",
      tab_agri: "Agriculture",
      tab_com: "Commercial",
      appliances: "Your appliances",
      pincode_placeholder: "Pincode",
      tariff: "Tariff ₹{rate}/unit",
      detected: "Detected",
      default_hours: "default {h}h/day",
      hours_day: "Hours/day",
      snapshot: "Your Solar Snapshot",
      daily: "Daily",
      monthly_units: "Monthly units",
      current_bill: "Current bill",
      recommended: "Recommended",
      investment: "Investment breakdown",
      system_cost: "System cost",
      pm_subsidy: "PM Surya Ghar subsidy",
      kusum_subsidy: "PM-KUSUM subsidy (~30%)",
      depreciation: "Accelerated Depreciation tax benefit (~40%)",
      com_note: "Commercial systems don't receive the PM Surya Ghar central subsidy, but qualify for ~40% Accelerated Depreciation tax benefit under Section 32 of the Income Tax Act.",
      net_investment: "Net investment",
      monthly_savings: "Monthly savings",
      payback: "Payback",
      co2_yr: "CO₂/yr",
      cta_quote: "Get my {kw} kW quote",
      disclaimer:
        "Estimates based on 4 sun-hours/day and ₹{rate}/unit tariff. Final design after free site visit.",
      appliance_labels: {
        fan: "Ceiling Fan",
        tube: "Tube / LED Light",
        ac: "Air Conditioner (1.5T)",
        fridge: "Refrigerator",
        tv: "Television",
        geyser: "Geyser",
        washer: "Washing Machine",
        microwave: "Microwave",
        computer: "Computer / Laptop",
        pump_home: "Water Pump (1 HP)",
        pump_3hp: "Water Pump (3 HP)",
        pump_5hp: "Water Pump (5 HP)",
        pump_75hp: "Water Pump (7.5 HP)",
        pump_10hp: "Submersible (10 HP)",
        lights: "Farm Lighting",
        chaffcutter: "Chaff Cutter",
      },
    },
    workflow: {
      eyebrow: "Our Process",
      title_pre: "Six steps.",
      title_hl: "Zero",
      title_post: "headaches.",
      subtitle:
        "From the first phone call to your 25th year of savings, Purvodaya owns the entire journey. Here's exactly how it unfolds.",
      activities: "Activities",
      deliverables: "Deliverables",
      outcome: "Outcome",
      steps: [
        {
          title: "Site Visit & Feasibility",
          activities: "Shadow analysis, roof inspection, load study, electricity bill review.",
          deliverables: "Feasibility report with system size & generation estimate.",
          outcome: "Confidence that your roof is solar-ready.",
        },
        {
          title: "Design & Proposal",
          activities:
            "3D rooftop layout, structural + electrical SLD, panel & inverter selection.",
          deliverables: "Detailed proposal with BOM, timeline & subsidy estimate.",
          outcome: "Transparent pricing, zero surprises.",
        },
        {
          title: "Approvals & Subsidy Filing",
          activities:
            "Net-meter application, DISCOM liaison, PM Surya Ghar / KUSUM filing.",
          deliverables: "Net metering sanction + subsidy registration.",
          outcome: "Paperwork handled end-to-end.",
        },
        {
          title: "Installation",
          activities:
            "Structure mounting, panel laying, cabling, inverter commissioning.",
          deliverables: "Fully installed, labelled & tested rooftop system.",
          outcome: "Power flowing, typically in 5–7 days.",
        },
        {
          title: "Commissioning & Go-Live",
          activities: "DISCOM inspection, net meter installation, app onboarding.",
          deliverables: "Handover pack + monitoring app login.",
          outcome: "Your meter starts spinning backwards.",
        },
        {
          title: "After-Sales & Monitoring",
          activities: "Remote performance monitoring, periodic cleaning, AMC support.",
          deliverables: "Annual performance reports, priority helpline.",
          outcome: "25 years of worry-free sunshine.",
        },
      ],
    },
    faq: {
      eyebrow: "Frequently Asked",
      title_pre: "The questions everyone asks,",
      title_hl: "answered honestly.",
      contact_label: "Still have questions?",
      contact_desc: "Share your details and a Purvodaya expert will reach out within 24 hours.",
      contact_cta: "Request a Call-Back",
      items: [
        {
          q: "How much roof space do I need for a solar system?",
          a:
            "As a rule of thumb, each 1 kW of rooftop solar needs ~80–100 sq ft of shadow-free area. A typical 3 kW home system fits easily on a 300 sq ft rooftop.",
        },
        {
          q: "Will solar work during monsoon or cloudy days?",
          a:
            "Yes. Panels still generate power in diffused light (roughly 10–25% of peak output). Net-metering lets you draw from the grid when you need more than you produce. For zero downtime, pair with a BESS battery bank.",
        },
        {
          q: "What is the PM Surya Ghar subsidy process?",
          a:
            "You register on the national portal, we file the application, install the system via an MNRE-empanelled vendor (Purvodaya is one), and after DISCOM inspection, the subsidy is transferred directly to your bank account — no middlemen.",
        },
        {
          q: "How long does installation take?",
          a:
            "A standard 3 kW residential rooftop installation takes 3–5 days on site. End-to-end — including approvals and net metering — typically 4–6 weeks.",
        },
        {
          q: "What maintenance does a solar system need?",
          a:
            "Very little. Periodic cleaning (once every 1–2 months in dusty areas) and an annual inspection. We offer a free 1-year AMC with every installation.",
        },
        {
          q: "Can farmers really earn income from PM-KUSUM?",
          a:
            "Absolutely. Under Component-A & C, surplus solar power is purchased by DISCOMs at a fixed tariff — giving farmers steady additional income alongside free daytime power for pumps.",
        },
        {
          q: "[Commercial] What is the typical payback for an industrial solar plant?",
          a:
            "For tax-paying entities claiming 40% Accelerated Depreciation in Year-1, payback is typically 3–4 years on CAPEX. On our zero-investment OPEX/PPA model, you save from month one with no upfront cost. Share your last 3-month bill — we'll model your exact ROI.",
        },
        {
          q: "[Commercial] Do you support Open Access and behind-the-meter projects?",
          a:
            "Yes. For 1 MW+ loads we design wheeling-based Open Access plants, captive C&I projects and behind-the-meter rooftop combos. Our team handles state nodal-agency liaisoning, banking arrangements and CEIG/CEA approvals end-to-end.",
        },
        {
          q: "[Solar Street Light] Can solar street lights really replace grid lighting in remote villages?",
          a:
            "Absolutely. Our all-in-one solar street lights operate completely off-grid with an integrated panel, LiFePO4 battery and motion-sensing LED. They are the fastest, cheapest way to electrify pathways, panchayat roads and remote hamlets where running grid wiring is uneconomical — installation takes under an hour per pole.",
        },
        {
          q: "[Solar Street Light] How long do solar street lights work after sunset on a cloudy day?",
          a:
            "Each unit stores enough energy for 2–3 days of autonomy. Motion-sensing dims unused light to 30%, so even on consecutive cloudy days the lamp stays operational from dusk to dawn. Want a site survey for your village or society compound? Drop your details below — we'll call you back.",
        },
        {
          q: "[Solar Water Heater] How is a solar water heater better than an electric geyser?",
          a:
            "A 200-LPD evacuated-tube solar water heater can save ₹15,000–₹25,000 per year of geyser electricity for a family of four — and gives hot water 365 days, even in winter. Payback is 2–3 years, after which it's free hot water for the next 15+ years.",
        },
        {
          q: "[Solar Water Heater] Does it work in winter, hill stations and cloudy weather?",
          a:
            "Yes — evacuated glass-tube (ETC) systems use vacuum insulation that retains heat even at sub-zero ambient temperatures, making them ideal for hill stations. For uninterrupted hot water on consecutive cloudy days, we add a small electric backup heating element. To know what size suits your household, share your details with us.",
        },
      ],
    },
    footer: {
      tag: "Power the future, brightly",
      desc:
        "MNRE-empanelled rooftop solar EPC company helping Indian homes and farms cut bills and accelerate the energy transition.",
      explore: "Explore",
      contact: "Contact",
      rights: "© {year} Purvodaya Solar. All rights reserved.",
      built: "Built with ☀ for a cleaner India.",
    },
    lead: {
      badge: "Free & No obligation",
      title_default: "Get a free rooftop quote",
      subtitle_default:
        "Share your details — a Purvodaya expert will call you within 24 hours.",
      title_calc: "Get your personalised quote",
      title_idle: "Still exploring? Let us help.",
      subtitle_idle:
        "Share your details — we'll send tailored solar savings to your phone.",
      full_name: "Full Name *",
      name_ph: "Your name",
      phone: "Phone *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "Pincode",
      email: "Email",
      email_ph: "you@example.com",
      i_need: "I need solar for",
      seg_home: "Home / Residential",
      seg_farm: "Farm / Agriculture",
      seg_commercial: "Commercial / Industrial",
      anything_else: "Anything else?",
      msg_ph: "Roof size, current bill, etc.",
      submit: "Request my free call-back",
      submitting: "Sending…",
      thanks_title: "You're on our list!",
      thanks_desc: "A solar consultant will reach out to you shortly.",
      toast_ok: "Thank you! We'll call you shortly.",
      toast_required: "Name and phone are required",
      toast_fail: "Could not send. Please try again.",
      consent:
        "By submitting, you agree to be contacted by Purvodaya Solar.",
    },
    whatsapp: { label: "Chat with us" },
    common: { language: "Language" },
    ts: {
        eyebrow: "Why Purvodaya",
        title_pre: "A serious partner for",
        title_hl: "India's solar transition",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "Energy Transition Partner",
        p1_title: "Trusted partner for India's solar-led energy transition",
        p1_desc: "Purpose-built for the country's residential, housing-society and corporate journey to clean power — engineered for long-term performance, not short-term gimmicks.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM Filer",
        p2_title: "Authorised filer for central solar subsidies",
        p2_desc: "We file PM Surya Ghar for individual homes & housing societies and PM-KUSUM for farmers — end-to-end paperwork handled in-house, no middlemen.",
        p3_eyebrow: "End-to-End EPC + 25-yr O&M",
        p3_title: "One accountable partner — design to switch-on to 25-year care",
        p3_desc: "MNRE-aligned design, Tier-1 modules, certified installation crews, DISCOM liaisoning, and a 25-year performance guarantee under one roof.",
        promise_label: "Our Promise",
        promise1: "Tier-1 modules · 25-year performance warranty",
        promise2: "MNRE-aligned engineering & safety practices",
        promise3: "Transparent quotes · no hidden costs",
        promise4: "Quarterly performance & cleaning reports",
      },
    res: {
        hero_badge: "Residential Rooftop Solar",
        hero_title_a: "Your AC, fridge, geyser —",
        hero_title_b: "powered free by the sun.",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "Get a Free Rooftop Quote",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "Max central subsidy",
        stat_reduction: "Bill reduction",
        stat_payback: "Typical payback",
        stat_warranty: "Performance warranty",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "Back to Home",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "Commercial & Industrial Solar",
        hero_title_a: "Power your business with the",
        hero_title_hl: "sun",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "Request a Business Proposal",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "Avg. energy bill reduction",
        stat_payback: "Typical payback (with AD)",
        stat_warranty: "Performance warranty",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "Back to Home",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "Housing Society Solar",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "Request a Society Visit",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "Subsidy per eligible flat",
        stat_reduction: "Common-area bill reduction",
        stat_payback: "Society payback",
        stat_warranty: "Performance warranty",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "Back to Home",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },

  hi: {
    nav: {
      services: "सेवाएँ",
      how: "यह कैसे काम करता है",
      subsidy: "सब्सिडी",
      savings: "बचत",
      process: "प्रक्रिया",
      faq: "सवाल-जवाब",
      admin: "एडमिन",
      cta: "मुफ्त क्वोट पाएं",
      menu: "मेनू",
      residential: "रिहायशी",
      commercial: "कॉमर्शियल",
      housing: "हाउसिंग सोसाइटी",
    },
    hero: {
      badge: "MNRE सूचीबद्ध · PM सूर्य घर तैयार",
      title_pre: "अपने घर को",
      title_highlight: "सूरज की रोशनी",
      title_post1: "से चलाएँ और अपना",
      title_post2: "बिजली बिल घटाएँ।",
      subtitle_pre:
        "पुर्वोदय सोलर पूरे भारत में घरों और खेतों के लिए रूफटॉप सोलर सिस्टम डिज़ाइन, इंस्टॉल और सर्विस करता है — और",
      subtitle_and: "तथा",
      subtitle_post: "सब्सिडी के लिए पूर्ण सहायता देता है।",
      cta_quote: "मुफ्त रूफटॉप क्वोट पाएं",
      cta_calc: "बचत कैलकुलेट करें",
      stat1_k: "₹78,000",
      stat1_v: "अधिकतम सब्सिडी (आवासीय)",
      stat2_k: "25 वर्ष",
      stat2_v: "पैनल प्रदर्शन",
      stat3_k: "4–5 वर्ष",
      stat3_v: "सामान्य पेबैक",
      live_label: "औसत मासिक बचत",
      live_value: "₹4,120",
      live_caption: "एक सामान्य 3 kW घर के लिए PM सूर्य घर सब्सिडी के बाद",
      this_month: "इस साल",
      saved: "औसत घरेलू बचत",
      co2: "t CO₂ की वार्षिक कमी",
    },
    trust: {
      heading: "टियर-1 पैनल और इन्वर्टर ब्रांड्स के साथ काम",
    },
    services: {
            cta_explore: "लाभ जानें",
      cta_street_b2b: "B2B परियोजनाओं के लिए संपर्क करें →",
eyebrow: "संपूर्ण सोलर समाधान",
      title_pre: "एक पार्टनर.",
      title_hl: "हर सोलर ज़रूरत",
      title_post: "पूरी.",
      subtitle: "छत से कारखाने तक, EV चार्जर से स्ट्रीट लाइट तक — पुर्वोदय सोलर आपकी जगह और बजट के अनुसार पूर्ण क्लीन एनर्जी समाधान देता है।",
      cta: "इस सेवा की जानकारी",
      items: [
        { key: "rooftop", title: "आवासीय रूफटॉप सोलर", desc: "घरों के लिए MNRE-सूचीबद्ध रूफटॉप इंस्टॉलेशन — PM सूर्य घर योजना के तहत ₹78,000 तक केंद्रीय सब्सिडी पाएं।", highlight: "PM सूर्य घर योजना" },
        { key: "commercial", title: "कमर्शियल और इंडस्ट्रियल सोलर", desc: "कारखानों, ऑफिसों और गोदामों के लिए MW स्केल के रूफटॉप व ग्राउंड-माउंट सिस्टम। 60% तक बिजली खर्च घटाएं और 40% त्वरित मूल्यह्रास लाभ लें।", highlight: "40% तक टैक्स बचत" },
        { key: "ev", title: "EV चार्जिंग समाधान", desc: "घरों, ऑफिसों, मॉल्स और हाईवे प्लाज़ा के लिए सोलर-संचालित AC व DC फास्ट EV चार्जर — रूफटॉप सोलर के साथ ज़ीरो-कॉस्ट ड्राइविंग।", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "बैटरी एनर्जी स्टोरेज", desc: "बैकअप पावर, पीक शेविंग और पूर्ण ऑफ-ग्रिड स्वतंत्रता के लिए लिथियम-आयन BESS। 5 kWh घरेलू से 1 MWh+ कमर्शियल बैंक तक।", highlight: "10 साल वारंटी" },
        { key: "street", title: "सोलर स्ट्रीट लाइटिंग", desc: "सोसायटी, परिसर, हाईवे और ग्रामीण सड़कों के लिए स्टैंड-अलोन ऑल-इन-वन सोलर स्ट्रीट लाइट — ज़ीरो वायरिंग, ज़ीरो बिजली बिल।", highlight: "ऑटो डस्क-टू-डॉन" },
        { key: "society", title: "हाउसिंग सोसायटी सोलर", desc: "अपार्टमेंट कॉम्प्लेक्स के लिए ग्रुप रूफटॉप व कॉमन-एरिया सोलर — व्यक्तिगत फ्लैट्स पर PM सूर्य घर लाभ साथ ही सोसायटी की बचत।", highlight: "RWA-फ्रेंडली" },
        { key: "heater", title: "सोलर वॉटर हीटर", desc: "घरों, छात्रावासों व होटलों के लिए एवेक्युएटेड ग्लास-ट्यूब सोलर वॉटर हीटर — 365 दिन गर्म पानी, गीज़र का बिजली बिल पूरी तरह बंद।", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "रूफटॉप सोलर कैसे काम करता है",
      title_pre: "",
      title_hl: "सूरज की रोशनी",
      title_mid: "से बचत तक — चार सरल चरणों में।",
      subtitle:
        "रूफटॉप सोलर जादू नहीं, बल्कि स्मार्ट मीटरिंग के साथ सरल भौतिकी है। रोज़ स्वचालित रूप से यह होता है:",
      steps: [
        {
          title: "सूरज की रोशनी पैनल पर पड़ती है",
          desc:
            "आपकी छत पर लगे हाई-एफिशियंसी मोनो PERC पैनल दिन की रोशनी सोखकर DC बिजली बनाते हैं।",
        },
        {
          title: "इन्वर्टर AC में बदलता है",
          desc:
            "स्मार्ट ग्रिड-टाइड इन्वर्टर DC को AC में बदलता है जो 96% दक्षता से आपके सभी उपकरण चलाता है।",
        },
        {
          title: "घर पहले इसी का उपयोग करता है",
          desc:
            "पंखे, लाइट, AC, पंप — दिन में सूरज से चलते हैं, जिससे ग्रिड पर निर्भरता बहुत कम हो जाती है।",
        },
        {
          title: "नेट मीटर बाकी बिजली बैंक करता है",
          desc:
            "अतिरिक्त यूनिट ग्रिड में वापस जाती है। महीने के अंत में केवल नेट उपभोग का बिल आता है।",
        },
      ],
      kpis: [
        { k: "70–90%", v: "इंस्टॉलेशन के बाद मासिक बिजली बिल में कमी।" },
        { k: "₹0 EMI", v: "पार्टनर बैंकों के ज़रिए लचीले फाइनेंस विकल्प।" },
        { k: "25 वर्ष", v: "पैनल आउटपुट वारंटी (न्यूनतम 80% प्रदर्शन)।" },
      ],
    },
    subsidy: {
      eyebrow: "सरकारी सब्सिडी",
      title_pre: "पाएँ",
      title_hl: "₹78,000",
      title_post: "तक की छूट — कागज़ी कार्रवाई हम संभालेंगे।",
      subtitle:
        "चाहे घर हो या खेत, भारत सरकार सोलर अपनाने के लिए उदार सब्सिडी देती है। पुर्वोदय सोलर हर आवेदन को शुरू से लेकर धनराशि मिलने तक ट्रैक करता है।",
      tab_res: "आवासीय · PM सूर्य घर",
      tab_agri: "कृषि · PM-KUSUM",
      res_title: "PM सूर्य घर: मुफ्त बिजली योजना",
      res_desc:
        "भारत सरकार द्वारा शुरू की गई इस योजना के तहत आपके घर पर रूफटॉप सोलर लगने के बाद सब्सिडी सीधे आपके बैंक खाते में आती है।",
      slab_labels: ["1 kW तक", "2 kW तक", "3 kW और अधिक"],
      slab_notes: ["₹30,000 प्रति kW", "₹30,000 प्रति kW", "अधिकतम सीमा"],
      res_bullets: [
        "आवासीय घरों पर रूफटॉप PV सिस्टम के लिए लागू।",
        "पात्र परिवारों को 300 यूनिट बिजली प्रति माह मुफ्त।",
        "कमीशनिंग और निरीक्षण के बाद सीधे बैंक ट्रांसफर।",
        "हमारी टीम राष्ट्रीय पोर्टल पर आधिकारिक आवेदन करती है।",
      ],
      read_official: "आधिकारिक योजना पढ़ें",
      savings_label: "आपकी बचत",
      savings_amount: "₹78,000",
      savings_note:
        "PM सूर्य घर के तहत 3 kW+ आवासीय रूफटॉप पर अधिकतम केंद्रीय सब्सिडी। राज्य प्रोत्साहन अतिरिक्त हो सकते हैं।",
      example_label: "उदाहरण · 3 kW सिस्टम",
      example_cost: "सिस्टम लागत",
      example_subsidy: "सब्सिडी",
      example_pay: "आप भुगतान करें",
      agri_title: "PM-KUSUM योजना",
      agri_desc:
        "प्रधानमंत्री किसान ऊर्जा सुरक्षा एवं उत्थान महाभियान किसानों को सोलर पंप लगाने और कृषि फीडरों को सोलर से चलाने में मदद करता है — डीज़ल की निर्भरता घटाकर विश्वसनीय दिन-भर की बिजली देता है।",
      agri_components: [
        { k: "कंपो-A", v: "बंजर भूमि पर ग्रिड से जुड़े सोलर प्लांट" },
        { k: "कंपो-B", v: "स्टैंड-अलोन सोलर पानी के पंप" },
        { k: "कंपो-C", v: "मौजूदा ग्रिड पंपों का सोलराइज़ेशन" },
      ],
      agri_bullets: [
        "सोलर पंप पर 60% तक केंद्र + राज्य सब्सिडी।",
        "30% बैंक लोन; किसान का योगदान सिर्फ़ ~10%।",
        "अतिरिक्त आय के लिए सरप्लस बिजली DISCOM को बेचें।",
        "इंस्टॉलेशन और DBT सहायता पुर्वोदय द्वारा।",
      ],
      farmer_benefit: "किसान का लाभ",
      farmer_amount: "60% तक छूट",
      farmer_note:
        "सोलर पंप इंस्टॉलेशन पर, साथ ही ग्रिड को अतिरिक्त बिजली बेचने से आमदनी।",
      sub_individual: "व्यक्तिगत घर",
      sub_society: "ग्रुप हाउसिंग सोसाइटियाँ",
      ind_box_label: "आपकी कुल बचत",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar के तहत 3 kW+ रिहायशी रूफटॉप सिस्टम पर अधिकतम केंद्रीय सब्सिडी। राज्य के अतिरिक्त प्रोत्साहन भी मिल सकते हैं।",
      ind_example_title: "उदाहरण · 3 kW सिस्टम",
      ind_example_rows: [
        { label: "सिस्टम लागत", value: "₹1,80,000" },
        { label: "सब्सिडी", value: "₹78,000" },
      ],
      ind_example_net_label: "आप देंगे",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "आपको और मिलेगा",
      ind_extras: [
        "पात्र परिवारों को प्रति माह 300 यूनिट तक मुफ़्त",
        "अतिरिक्त उत्पादन के लिए नेट-मीटरिंग क्रेडिट",
        "25-वर्षीय पैनल प्रदर्शन वारंटी",
        "पुर्वोदय द्वारा PM Surya Ghar पोर्टल फाइलिंग मुफ़्त",
      ],
      ghs_title: "ग्रुप हाउसिंग सोसाइटियाँ (GHS / RWAs)",
      ghs_intro: "PMSGY के तहत केंद्र सरकार लिफ़्ट, पानी के पंप, स्ट्रीटलाइट, लॉबी व कॉरिडोर लाइटिंग जैसे कॉमन फ़ैसिलिटी के लिए ग्रिड-कनेक्टेड रूफटॉप सोलर पर ₹18,000 प्रति kW प्रदान करती है।",
      ghs_structure_label: "सब्सिडी संरचना",
      ghs_structure: [
        "स्थापित कॉमन-एरिया सोलर क्षमता का ₹18,000 / kW।",
        "हर सोसाइटी के लिए अधिकतम 500 kW — यानी ₹90 लाख तक सब्सिडी।",
        "अनुमेय कॉमन क्षमता = 3 kW × फ़्लैटों की संख्या (व्यक्तिगत रूफटॉप समेत)।",
      ],
      ghs_eligibility_label: "पात्रता चेकलिस्ट",
      ghs_eligibility: [
        "CHS / RWA अपने PAN के साथ कानूनी रूप से रजिस्टर्ड हो।",
        "सोसाइटी के नाम पर सक्रिय कॉमन-सर्विस बिजली मीटर।",
        "सीधे सब्सिडी ट्रांसफ़र के लिए समर्पित सोसाइटी बैंक खाता।",
        "केवल मेक-इन-इंडिया (DCR-अनुरूप) पैनल — आयातित मॉड्यूल नहीं।",
      ],
      ghs_box_label: "सोसाइटी की कुल बचत",
      ghs_box_amount: "₹90 लाख तक",
      ghs_box_caption: "एकल हाउसिंग सोसाइटी के लिए अधिकतम PMSGY केंद्रीय सब्सिडी — ₹18,000 प्रति kW की दर से, 500 kW पर अधिकतम।",
      ghs_example_title: "गणना · 100-फ़्लैट सोसाइटी",
      ghs_example_rows: [
        { label: "अनुमेय कॉमन क्षमता (3 kW × 100 फ़्लैट)", value: "300 kW" },
        { label: "अनुमानित परियोजना लागत (₹50,000 / kW)", value: "₹1.50 करोड़" },
        { label: "PMSGY सब्सिडी (₹18,000 / kW × 300 kW)", value: "₹54 लाख" },
        { label: "वार्षिक कॉमन-एरिया बिल बचत", value: "~₹22 लाख" },
      ],
      ghs_example_net_label: "सोसाइटी का कुल खर्च",
      ghs_example_net_value: "₹96 लाख",
      ghs_example_payback: "4–5 साल में पेबैक · 20+ साल मुफ़्त कॉमन-एरिया बिजली",
      ghs_documents_label: "सोसाइटी से जो दस्तावेज़ चाहिए",
      ghs_documents: [
        "सोसाइटी रजिस्ट्रेशन सर्टिफ़िकेट + PAN",
        "पिछले 3 महीने का कॉमन-एरिया बिजली बिल",
        "सोसाइटी बैंक खाता पासबुक (पहला पन्ना)",
        "परियोजना को मंज़ूरी देने वाली AGM/EGM संकल्प",
        "बिल्डिंग प्लान व रूफटॉप लेआउट (हम सर्वे कर सकते हैं)",
      ],
    },
    calc: {
      eyebrow: "बचत कैलकुलेटर",
      title_pre: "देखें आप कितनी",
      title_hl: "बचत करेंगे",
      title_post: "— शुरू करने के लिए + और − दबाएँ।",
      subtitle:
        "अपने उपकरण चुनें, उपयोग के घंटे सेट करें — हम सही आकार का सोलर सिस्टम, लाइव सब्सिडी और पेबैक का अनुमान बताएँगे।",
      tab_res: "आवासीय",
      tab_agri: "कृषि",
      appliances: "आपके उपकरण",
      pincode_placeholder: "पिनकोड",
      tariff: "टैरिफ ₹{rate}/यूनिट",
      detected: "पहचाना गया",
      default_hours: "डिफ़ॉल्ट {h}घं/दिन",
      hours_day: "घंटे/दिन",
      snapshot: "आपका सोलर स्नैपशॉट",
      daily: "दैनिक",
      monthly_units: "मासिक यूनिट",
      current_bill: "वर्तमान बिल",
      recommended: "सिफ़ारिश",
      investment: "निवेश विवरण",
      system_cost: "सिस्टम लागत",
      pm_subsidy: "PM सूर्य घर सब्सिडी",
      kusum_subsidy: "PM-KUSUM सब्सिडी (~30%)",
      net_investment: "शुद्ध निवेश",
      monthly_savings: "मासिक बचत",
      payback: "पेबैक",
      co2_yr: "CO₂/वर्ष",
      cta_quote: "मेरा {kw} kW क्वोट पाएं",
      disclaimer:
        "अनुमान 4 धूप-घंटे/दिन और ₹{rate}/यूनिट टैरिफ पर आधारित। अंतिम डिज़ाइन मुफ्त साइट विज़िट के बाद।",
      appliance_labels: {
        fan: "छत का पंखा",
        tube: "ट्यूब / LED लाइट",
        ac: "एयर कंडीशनर (1.5T)",
        fridge: "रेफ्रिजरेटर",
        tv: "टेलीविज़न",
        geyser: "गीज़र",
        washer: "वॉशिंग मशीन",
        microwave: "माइक्रोवेव",
        computer: "कंप्यूटर / लैपटॉप",
        pump_home: "पानी का पंप (1 HP)",
        pump_3hp: "पानी का पंप (3 HP)",
        pump_5hp: "पानी का पंप (5 HP)",
        pump_75hp: "पानी का पंप (7.5 HP)",
        pump_10hp: "सबमर्सिबल (10 HP)",
        lights: "खेत की लाइटिंग",
        chaffcutter: "चॉफ़ कटर",
      },
    },
    workflow: {
      eyebrow: "हमारी प्रक्रिया",
      title_pre: "छह चरण।",
      title_hl: "शून्य",
      title_post: "परेशानी।",
      subtitle:
        "पहले कॉल से लेकर 25 साल की बचत तक, पूरी यात्रा पुर्वोदय संभालता है। यह ऐसे होता है:",
      activities: "गतिविधियाँ",
      deliverables: "डिलिवरेबल्स",
      outcome: "परिणाम",
      steps: [
        {
          title: "साइट विज़िट और व्यवहार्यता",
          activities: "छाया विश्लेषण, छत निरीक्षण, लोड अध्ययन, बिजली बिल समीक्षा।",
          deliverables: "सिस्टम साइज़ और उत्पादन अनुमान के साथ व्यवहार्यता रिपोर्ट।",
          outcome: "विश्वास कि आपकी छत सोलर के लिए तैयार है।",
        },
        {
          title: "डिज़ाइन और प्रस्ताव",
          activities: "3D रूफटॉप लेआउट, संरचनात्मक + विद्युत SLD, पैनल और इन्वर्टर चयन।",
          deliverables: "BOM, टाइमलाइन और सब्सिडी अनुमान के साथ विस्तृत प्रस्ताव।",
          outcome: "पारदर्शी मूल्य, कोई छुपा खर्च नहीं।",
        },
        {
          title: "अनुमोदन और सब्सिडी आवेदन",
          activities: "नेट-मीटर आवेदन, DISCOM संपर्क, PM सूर्य घर / KUSUM फाइलिंग।",
          deliverables: "नेट मीटरिंग स्वीकृति + सब्सिडी पंजीकरण।",
          outcome: "कागज़ी कार्रवाई पूरी तरह संभाली गई।",
        },
        {
          title: "इंस्टॉलेशन",
          activities: "स्ट्रक्चर माउंटिंग, पैनल बिछाना, केबलिंग, इन्वर्टर कमीशनिंग।",
          deliverables: "पूरी तरह इंस्टॉल, लेबल और परीक्षित रूफटॉप सिस्टम।",
          outcome: "आमतौर पर 5–7 दिनों में बिजली प्रवाह।",
        },
        {
          title: "कमीशनिंग और गो-लाइव",
          activities: "DISCOM निरीक्षण, नेट मीटर स्थापना, ऐप ऑनबोर्डिंग।",
          deliverables: "हैंडओवर पैक + मॉनिटरिंग ऐप लॉगिन।",
          outcome: "आपका मीटर उल्टा घूमने लगता है।",
        },
        {
          title: "आफ्टर-सेल्स और मॉनिटरिंग",
          activities: "दूरस्थ प्रदर्शन निगरानी, समय-समय पर सफाई, AMC समर्थन।",
          deliverables: "वार्षिक प्रदर्शन रिपोर्ट, प्राथमिकता हेल्पलाइन।",
          outcome: "25 साल की चिंता-मुक्त धूप।",
        },
      ],
    },
    faq: {
      eyebrow: "अक्सर पूछे जाने वाले प्रश्न",
      title_pre: "सभी के सवाल —",
      title_hl: "ईमानदार जवाब।",
      items: [
        {
          q: "सोलर सिस्टम के लिए कितनी छत की जगह चाहिए?",
          a:
            "थंब रूल: प्रत्येक 1 kW के लिए ~80–100 वर्ग फ़ीट छाया-मुक्त क्षेत्र। एक सामान्य 3 kW घरेलू सिस्टम 300 वर्ग फ़ीट छत पर आसानी से आ जाता है।",
        },
        {
          q: "क्या सोलर मानसून या बादल वाले दिनों में काम करता है?",
          a:
            "हाँ। पैनल बिखरी हुई रोशनी में भी (पीक आउटपुट का ~10–25%) उत्पादन करते हैं। नेट-मीटरिंग से आप ग्रिड से अतिरिक्त बिजली ले सकते हैं।",
        },
        {
          q: "PM सूर्य घर सब्सिडी प्रक्रिया क्या है?",
          a:
            "आप राष्ट्रीय पोर्टल पर पंजीकरण करते हैं, हम आवेदन करते हैं, MNRE-सूचीबद्ध वेंडर (पुर्वोदय) द्वारा इंस्टॉलेशन, और DISCOM निरीक्षण के बाद सब्सिडी सीधे आपके बैंक खाते में।",
        },
        {
          q: "इंस्टॉलेशन में कितना समय लगता है?",
          a:
            "एक मानक 3 kW आवासीय इंस्टॉलेशन 3–5 दिन लेता है। अनुमोदन और नेट मीटरिंग सहित पूरी प्रक्रिया 4–6 सप्ताह।",
        },
        {
          q: "रखरखाव की क्या ज़रूरत है?",
          a:
            "बहुत कम। धूल वाले क्षेत्रों में 1–2 महीने में सफाई और वार्षिक निरीक्षण। हम प्रत्येक इंस्टॉलेशन के साथ मुफ्त 1 साल की AMC देते हैं।",
        },
        {
          q: "क्या किसान PM-KUSUM से वाकई आय कमा सकते हैं?",
          a:
            "बिलकुल। कंपोनेंट-A और C में सरप्लस सोलर बिजली DISCOM निश्चित टैरिफ पर खरीदती है — किसानों को दिन भर मुफ्त पंप चलाने के साथ नियमित अतिरिक्त आय।",
        },
      ],
    },
    footer: {
      tag: "भविष्य को उज्जवल बनाइए",
      desc:
        "MNRE-सूचीबद्ध रूफटॉप सोलर EPC कंपनी जो भारतीय घरों और खेतों को बिल कम करने और ऊर्जा बदलाव में मदद करती है।",
      explore: "देखें",
      contact: "संपर्क",
      rights: "© {year} पुर्वोदय सोलर। सर्वाधिकार सुरक्षित।",
      built: "स्वच्छ भारत के लिए ☀ से बनाया गया।",
    },
    lead: {
      badge: "मुफ्त और बिना बाध्यता",
      title_default: "मुफ्त रूफटॉप क्वोट पाएं",
      subtitle_default:
        "अपनी जानकारी साझा करें — पुर्वोदय विशेषज्ञ 24 घंटे में कॉल करेंगे।",
      title_calc: "अपना व्यक्तिगत क्वोट पाएं",
      title_idle: "अभी भी देख रहे हैं? हम मदद करें।",
      subtitle_idle:
        "अपनी जानकारी साझा करें — हम आपके फ़ोन पर अनुकूलित सोलर बचत भेजेंगे।",
      full_name: "पूरा नाम *",
      name_ph: "आपका नाम",
      phone: "फ़ोन *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "पिनकोड",
      email: "ईमेल",
      email_ph: "you@example.com",
      i_need: "मुझे सोलर चाहिए",
      seg_home: "घर / आवासीय",
      seg_farm: "खेत / कृषि",
      seg_commercial: "कॉमर्शियल / इंडस्ट्रियल",
      anything_else: "कुछ और?",
      msg_ph: "छत का आकार, वर्तमान बिल, आदि।",
      submit: "मुझे मुफ्त कॉल-बैक चाहिए",
      submitting: "भेज रहे हैं…",
      thanks_title: "आप हमारी सूची में हैं!",
      thanks_desc: "एक सोलर सलाहकार जल्द ही आपसे संपर्क करेगा।",
      toast_ok: "धन्यवाद! हम जल्द ही कॉल करेंगे।",
      toast_required: "नाम और फ़ोन आवश्यक हैं",
      toast_fail: "भेज नहीं सके। कृपया पुनः प्रयास करें।",
      consent:
        "सबमिट करके, आप पुर्वोदय सोलर द्वारा संपर्क की सहमति देते हैं।",
    },
    whatsapp: { label: "हमसे चैट करें" },
    common: { language: "भाषा" },
    ts: {
        eyebrow: "क्यों पुर्वोदय",
        title_pre: "एक गंभीर साझेदार —",
        title_hl: "भारत के सौर परिवर्तन के लिए",
        subtitle: "पुर्वोदय सोलर रिहायशी छतों, हाउसिंग सोसाइटियों और कॉमर्शियल व इंडस्ट्रियल यूनिट्स में स्वच्छ ऊर्जा प्रदान करने के लिए बनाया गया है — डिज़ाइन, निष्पादन, सब्सिडी फाइलिंग और 25-वर्षीय संचालन की पूर्ण ज़िम्मेदारी के साथ।",
        p1_eyebrow: "एनर्जी ट्रांज़िशन पार्टनर",
        p1_title: "भारत के सौर-आधारित ऊर्जा परिवर्तन का भरोसेमंद साथी",
        p1_desc: "देश की रिहायशी, हाउसिंग सोसाइटी और कॉर्पोरेट यात्रा को स्वच्छ ऊर्जा तक ले जाने के लिए विशेष रूप से तैयार — दीर्घकालिक प्रदर्शन के लिए, क्षणिक दिखावे के लिए नहीं।",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM फाइलर",
        p2_title: "केंद्रीय सौर सब्सिडी का अधिकृत फाइलर",
        p2_desc: "हम व्यक्तिगत घरों और हाउसिंग सोसाइटियों के लिए PM Surya Ghar और किसानों के लिए PM-KUSUM दाखिल करते हैं — पूरी कागज़ी कार्यवाही हमारी टीम संभालती है, कोई बिचौलिया नहीं।",
        p3_eyebrow: "एंड-टू-एंड EPC + 25-वर्षीय O&M",
        p3_title: "एक ज़िम्मेदार साझेदार — डिज़ाइन से चालू करने तक, और 25 साल की देखभाल",
        p3_desc: "MNRE-अनुरूप डिज़ाइन, Tier-1 मॉड्यूल, प्रमाणित इंस्टॉलेशन टीम, DISCOM संपर्क, और 25-वर्षीय प्रदर्शन गारंटी — एक ही छत के नीचे।",
        promise_label: "हमारा वादा",
        promise1: "Tier-1 मॉड्यूल · 25-वर्षीय प्रदर्शन वारंटी",
        promise2: "MNRE-अनुरूप इंजीनियरिंग व सुरक्षा प्रथाएँ",
        promise3: "पारदर्शी कोटेशन · कोई छिपी हुई लागत नहीं",
        promise4: "त्रैमासिक प्रदर्शन व सफ़ाई रिपोर्ट",
      },
    res: {
        hero_badge: "रिहायशी रूफटॉप सोलर",
        hero_title_a: "आपका AC, फ्रिज, गीज़र —",
        hero_title_b: "सूरज से मुफ़्त चलाएँ।",
        hero_subtitle: "भारत में बिजली के दाम हर साल 4–6% बढ़ रहे हैं। पुर्वोदय का रूफटॉप आपके बिल को पहले दिन से 70–95% तक घटाता है — और सिर्फ़ 4–5 साल में अपनी लागत वसूल कर लेता है।",
        cta_quote: "मुफ़्त रूफटॉप कोट पाएँ",
        cta_calc: "अपनी बचत का अनुमान लगाएँ",
        stat_subsidy: "अधिकतम केंद्रीय सब्सिडी",
        stat_reduction: "बिल में कमी",
        stat_payback: "औसत पेबैक",
        stat_warranty: "प्रदर्शन वारंटी",
        app_eyebrow: "आपका बिल असल में कहाँ जाता है",
        app_title_a: "हर उपकरण,",
        app_title_b: "सूरज की रौशनी से चलेगा।",
        app_subtitle: "एक सामान्य भारतीय घर इन उपकरणों पर ₹4,000–₹8,000 प्रति माह खर्च करता है। सोलर इसे दिन के समय लगभग शून्य कर देता है — और नेट-मीटरिंग आपकी रातों को संभालती है।",
        app_consumption: "खपत",
        app_current_bill: "वर्तमान बिल",
        app_note: "अनुमान ₹7/यूनिट औसत रिहायशी दर पर आधारित। सोलर हर मद के दिन के हिस्से को पूरी तरह समाप्त कर देता है।",
        app_ac: "एयर कंडीशनर (1.5T)",
        app_ac_units: "12 यूनिट/दिन तक",
        app_fridge: "रेफ्रिजरेटर",
        app_fridge_units: "3.5 यूनिट/दिन",
        app_geyser: "गीज़र",
        app_geyser_units: "4 यूनिट/दिन",
        app_washer: "वॉशिंग मशीन",
        app_washer_units: "2 यूनिट/चक्र",
        app_tv: "TV + सेट-टॉप",
        app_tv_units: "1.5 यूनिट/दिन",
        app_fans: "सीलिंग फैन (4)",
        app_fans_units: "3 यूनिट/दिन",
        app_lights: "LED लाइट्स (10)",
        app_lights_units: "1 यूनिट/दिन",
        b_eyebrow: "आपके घर के लिए पुर्वोदय क्यों",
        b_title_a: "छह घरेलू लाभ,",
        b_title_b: "कोई शर्त नहीं।",
        b1_t: "मासिक बिल में 70–95% कटौती",
        b1_d: "आपका AC, फ्रिज, गीज़र, वॉशिंग मशीन, पंखे और लाइट्स दिन भर मुफ़्त सौर ऊर्जा से चलेंगे। अधिकांश घरों में पहले दिन से ही ₹3,000–₹8,000/माह की बचत होती है।",
        b2_t: "₹78,000 तक PM Surya Ghar सब्सिडी",
        b2_d: "भारत सरकार स्थापना के बाद आपके बैंक खाते में सीधे ₹78,000 तक ट्रांसफर करती है। हम पूरी पोर्टल आवेदन, कागज़ी कार्यवाही और DISCOM संपर्क संभालते हैं।",
        b3_t: "पात्र परिवारों के लिए 300 यूनिट/माह मुफ़्त",
        b3_d: "PM Surya Ghar मुफ़्त बिजली योजना के तहत पात्र परिवारों को उत्पादन बचत के साथ-साथ मुफ़्त बिजली का लाभ भी मिलता है — कई घरों के लिए सोलर वस्तुतः शून्य लागत।",
        b4_t: "25-वर्षीय वारंटी, 5-वर्षीय पेबैक",
        b4_d: "Tier-1 mono PERC पैनल 25-वर्षीय प्रदर्शन वारंटी के साथ और इन्वर्टर 10-वर्षीय गारंटी के साथ। 4–5 साल के पेबैक के बाद, अगले दो दशकों तक हर यूनिट लगभग मुफ़्त।",
        b5_t: "नेट-मीटरिंग — अतिरिक्त बेचें",
        b5_d: "अतिरिक्त यूनिट ग्रिड में वापस जाती हैं और आपका DISCOM आपको क्रेडिट देता है। अधिक उत्पादन कर रहे हैं? रातों और मानसून के दिनों में बिजली बनाए रखते हुए मासिक क्रेडिट कमाएँ।",
        b6_t: "अपने घर को भविष्य के लिए तैयार रखें",
        b6_d: "बाद में बैटरी (BESS), EV चार्जर या सोलर वॉटर हीटर जोड़ें — हर पुर्वोदय रूफटॉप आपकी आवश्यकता के साथ बढ़ने के लिए डिज़ाइन किया गया है।",
        s_eyebrow: "पुर्वोदय 4-चरण रोलआउट",
        s_title_a: "",
        s_title_hl: "पहले कॉल",
        s_title_b: "से चालू होने तक — 4–6 हफ़्तों में।",
        s1_t: "मुफ़्त साइट विज़िट",
        s1_d: "हमारे इंजीनियर आपकी छत का सर्वेक्षण, छाया विश्लेषण और स्वीकृत लोड चेक करते हैं।",
        s2_t: "कोट + सब्सिडी फाइलिंग",
        s2_d: "हम आपकी ओर से PM Surya Ghar दाखिल करते हैं — आप सिर्फ़ एक डिजिटल फॉर्म साइन करते हैं।",
        s3_t: "DISCOM व नेट-मीटर अनुमोदन",
        s3_d: "सभी अनुमोदन पुर्वोदय द्वारा संभाले जाते हैं — आमतौर पर 3–4 हफ़्ते।",
        s4_t: "इंस्टॉल व चालू",
        s4_d: "साइट पर 3–5 दिन। उसी शाम — आपका घर सूरज से चलेगा।",
        f_eyebrow: "घर के मालिकों के लिए FAQs",
        f_title_a: "नए ख़रीदारों के लिए",
        f_title_hl: "जल्दी जवाब।",
        f1_q: "क्या सोलर गर्मियों में मेरा AC और गीज़र चला पाएगा?",
        f1_a: "जी हाँ — और सबसे बड़ी बचत यहीं से आती है। 5 kW रूफटॉप दिन के समय आसानी से 1.5T इन्वर्टर AC, गीज़र और सभी दैनिक लोड चलाता है। हम आपके उपकरण प्रोफ़ाइल के आधार पर सिस्टम का आकार तय करते हैं।",
        f2_q: "अगर मैं 4–5 साल में घर बदल दूँ तो?",
        f2_a: "आधुनिक सोलर सिस्टम मॉड्यूलर और आपकी संपत्ति के लिए पोर्टेबल हैं। अधिकांश मामलों में सिर्फ़ 2–3 साल की बचत ही स्थानांतरण लागत से अधिक होती है। साथ ही, सोलर वाले घर आमतौर पर 3–5% बेहतर रीसेल मूल्य देते हैं।",
        f3_q: "क्या मुझे सच में ₹78,000 की बचत होगी — या यह सिर्फ़ मार्केटिंग है?",
        f3_a: "यह 3 kW+ रिहायशी सिस्टम के लिए PM Surya Ghar के तहत भारत सरकार से वास्तविक डायरेक्ट बैंक ट्रांसफ़र है। छोटे kW साइज़ को आनुपातिक सब्सिडी मिलती है। राशि कमीशनिंग के 30–60 दिनों में पहुँचती है।",
        final_a: "DISCOM को भुगतान बंद करें।",
        final_hl: "खुद को भुगतान करना शुरू करें।",
        final_sub: "अपनी जानकारी साझा करें — हमारा सोलर सलाहकार आपके घर का सिस्टम डिज़ाइन करेगा और आपकी PM Surya Ghar सब्सिडी मुफ़्त में फाइल करेगा।",
        final_cta: "मुफ़्त साइट विज़िट बुक करें",
        back_home: "होम पर वापस जाएँ",
        lead_title: "मुफ़्त रूफटॉप सोलर कोट",
        lead_subtitle: "अपनी जानकारी साझा करें — पुर्वोदय सलाहकार 24 घंटे में आपको कॉल करेगा।",
      },
    com: {
        hero_badge: "कॉमर्शियल व इंडस्ट्रियल सोलर",
        hero_title_a: "अपने बिज़नेस को",
        hero_title_hl: "सूरज",
        hero_title_b: "से चलाएँ —",
        hero_title_c: "ऊर्जा लागत घटाएँ, मार्जिन सुरक्षित करें।",
        hero_subtitle: "फ़ैक्टरियों, गोदामों, ऑफिसों और हॉस्पिटैलिटी के लिए MW-स्केल रूफटॉप व ग्राउंड-माउंट सोलर। ROI के लिए डिज़ाइन, 25 साल के लिए निर्मित।",
        cta_quote: "बिज़नेस प्रोपोज़ल मांगें",
        cta_calc: "अपनी बचत का अनुमान लगाएँ",
        stat_reduction: "औसत बिजली बिल में कमी",
        stat_payback: "विशिष्ट पेबैक (AD के साथ)",
        stat_warranty: "प्रदर्शन वारंटी",
        stat_upfront: "OPEX मॉडल में अग्रिम भुगतान",
        b_eyebrow: "बिज़नेस के लिए पुर्वोदय क्यों",
        b_title_a: "छह मापनीय लाभ,",
        b_title_hl: "वास्तविकता में।",
        b1_t: "बिजली लागत में 50–70% कटौती",
        b1_d: "महँगी ग्रिड बिजली (₹8–12/kWh) के स्थान पर स्वयं उत्पादित सोलर (₹2.5–3.5/kWh) — पहले दिन से EBITDA मार्जिन में सीधा सुधार।",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "आयकर अधिनियम की धारा 32 के तहत पहले वर्ष में संपूर्ण परियोजना लागत पर 40% AD क्लेम करें। टैक्स देने वाली संस्थाओं के लिए प्रभावी पेबैक 3–4 साल।",
        b3_t: "टैरिफ़ बढ़ोतरी से बचाव",
        b3_d: "DISCOM टैरिफ़ हर साल 4–6% बढ़ते हैं। 25-वर्षीय solar PPA आपकी उत्पादन लागत को लॉक करता है — अगले दो दशकों तक मार्जिन सुरक्षित।",
        b4_t: "ESG व RE100 रिपोर्टिंग",
        b4_d: "कार्बन-ऑफ़सेट सर्टिफिकेट, REC पात्रता और ESG-ग्रेड दस्तावेज़ — हर पुर्वोदय इंस्टॉलेशन में आपकी BRSR व सस्टेनेबिलिटी रिपोर्ट के लिए शामिल।",
        b5_t: "ज़ीरो CAPEX व OPEX मॉडल",
        b5_d: "CAPEX स्वामित्व चुनें या हमारा 25-वर्षीय OPEX/PPA मॉडल — कोई अग्रिम लागत नहीं, केवल उपभोग की गई यूनिट्स पर निश्चित डिस्काउंट टैरिफ़।",
        b6_t: "एंड-टू-एंड EPC + O&M",
        b6_d: "एक ज़िम्मेदार पॉइंट — डिज़ाइन, MNRE-empanelled निष्पादन, नेट-मीटरिंग संपर्क, प्रदर्शन गारंटी और 25-वर्षीय O&M।",
        seg_eyebrow: "हर सेक्टर के लिए तैयार",
        seg_title_a: "",
        seg_title_hl: "फ़ैक्टरी फ़्लोर्स",
        seg_title_b: "से कॉर्पोरेट कैम्पस तक।",
        seg1_t: "मैन्युफ़ैक्चरिंग व MSME",
        seg1_p1: "Captive रूफटॉप व ग्राउंड-माउंट सोलर",
        seg1_p2: "हाई-लोड CNC, कंप्रेसर, HVAC के लिए बिजली",
        seg1_p3: "1 MW+ संयंत्रों के लिए Open Access wheeling",
        seg2_t: "IT पार्क व ऑफ़िस",
        seg2_p1: "Carport + रूफटॉप हाइब्रिड सिस्टम",
        seg2_p2: "EV-तैयार चार्जिंग इन्फ्रास्ट्रक्चर",
        seg2_p3: "LEED व IGBC प्लेटिनम अनुपालन",
        seg3_t: "वेयरहाउसिंग व लॉजिस्टिक्स",
        seg3_p1: "बड़ी clear-span रूफटॉप कवरेज",
        seg3_p2: "कोल्ड-स्टोरेज कम्पैटिबल BESS",
        seg3_p3: "रेफ्रिजरेशन लोड 80%+ ऑफ़सेट",
        seg4_t: "हॉस्पिटैलिटी व रिटेल",
        seg4_p1: "गर्म पानी + बिजली कॉम्बो सिस्टम",
        seg4_p2: "ब्रांड-ग्रेड एस्थेटिक माउंटिंग",
        seg4_p3: "मेहमानों के लिए दृश्य सस्टेनेबिलिटी",
        roi_eyebrow: "एक वास्तविक ROI झलक",
        roi_title_a: "500 kW फ़ैक्टरी रूफटॉप।",
        roi_title_hl: "3.2-वर्षीय पेबैक।",
        roi_subtitle: "~80,000 यूनिट/माह खपत करने वाली टैक्स देने वाली विनिर्माण इकाई के लिए नमूना मॉडल। CAPEX मार्ग, पहले वर्ष में 40% Accelerated Depreciation, MAT-समायोजित।",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD टैक्स लाभ",
        roi3_k: "₹75 L+",
        roi3_v: "वार्षिक ऊर्जा बचत",
        roi4_k: "12,500 t",
        roi4_v: "जीवनकाल CO₂ ऑफ़सेट",
        roi_cta: "Tailored ROI मॉडल पाएँ",
        roi_year1: "वर्ष-1 कैशफ़्लो",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "बचत + AD लाभ",
        p_eyebrow: "हमारी 6-चरण प्रक्रिया",
        p_title_a: "ऑडिट से",
        p_title_hl: "25-वर्षीय संचालन तक।",
        p1_t: "साइट व टैरिफ़ ऑडिट",
        p1_d: "आपकी सुविधा के लिए मुफ़्त छाया विश्लेषण, लोड प्रोफ़ाइल और ROI मॉडल।",
        p2_t: "कस्टम प्रोपोज़ल",
        p2_d: "बैंकेबल फ़ाइनेंशियल मॉडल और AD लाभ के साथ CAPEX / OPEX विकल्प।",
        p3_t: "अनुमोदन व समन्वय",
        p3_d: "DISCOM नेट-मीटरिंग, CEIG, फ़ायर NOC — एंड-टू-एंड संभाले गए।",
        p4_t: "EPC निष्पादन",
        p4_d: "Tier-1 मॉड्यूल, MNRE-empanelled टीम, IEC-प्रमाणित BoS।",
        p5_t: "कमीशनिंग व हैंडओवर",
        p5_d: "प्रदर्शन परीक्षण, मॉनिटरिंग ऐप, आपकी टीम के लिए प्रशिक्षण।",
        p6_t: "25-वर्षीय O&M",
        p6_d: "त्रैमासिक सफ़ाई, उत्पादन गारंटी, रिमोट मॉनिटरिंग।",
        final_a: "अपनी बैलेंस शीट को",
        final_hl: "हरित बनाएँ?",
        final_sub: "अपने पिछले 3 महीनों के बिजली बिल साझा करें — हम 48 घंटों में CAPEX, OPEX और ROI के साथ साइट-विशिष्ट प्रोपोज़ल भेजेंगे।",
        final_cta: "बिज़नेस प्रोपोज़ल मांगें",
        back_home: "होम पर वापस जाएँ",
        lead_title: "बिज़नेस सोलर प्रोपोज़ल पाएँ",
        lead_subtitle: "अपनी जानकारी साझा करें — हम 48 घंटों में आपकी ज़रूरत के अनुसार CAPEX/OPEX प्रोपोज़ल भेजेंगे।",
      },
    soc: {
        hero_badge: "हाउसिंग सोसाइटी सोलर",
        hero_title_a: "एक सोसाइटी, एक छत,",
        hero_title_hl: "हज़ार सूरज से रोशन घर।",
        hero_subtitle: "अपार्टमेंट कॉम्प्लेक्स व सहकारी समितियाँ — कॉमन-एरिया बिल घटाएँ, हर फ़्लैट के लिए PM Surya Ghar सब्सिडी पाएँ, और एक हरित-प्रमाणित पता बनें।",
        cta_quote: "सोसाइटी विज़िट मांगें",
        cta_calc: "अपनी बचत का अनुमान लगाएँ",
        stat_subsidy: "हर पात्र फ़्लैट पर सब्सिडी",
        stat_reduction: "कॉमन-एरिया बिल में कमी",
        stat_payback: "सोसाइटी पेबैक",
        stat_warranty: "प्रदर्शन वारंटी",
        b_eyebrow: "RWAs के लिए बना",
        b_title_a: "आपकी सोसाइटी व निवासियों",
        b_title_hl: "के लिए छह लाभ।",
        b1_t: "कॉमन-एरिया बिल में 80–95% कटौती",
        b1_d: "लिफ़्ट, पानी के पंप, लॉबी व बेसमेंट लाइटिंग, जिम, क्लब-हाउस — सब रूफटॉप सोलर से चलेंगे। सोसाइटी मेंटेनेंस घटे, निवासी ख़ुश।",
        b2_t: "हर फ़्लैट के लिए PM Surya Ghar",
        b2_d: "हर फ़्लैट मालिक अपने रूफटॉप शेयर पर PM Surya Ghar के तहत ₹78,000 तक की केंद्रीय सब्सिडी क्लेम कर सकता है — सोसाइटी-स्तरीय एकत्रीकरण इसे आसान बनाता है।",
        b3_t: "RWA-फ़्रेंडली शासन",
        b3_d: "पारदर्शी EGM-तैयार प्रोपोज़ल, सोसाइटी संकल्प टेम्पलेट, उत्पादन का हिस्सा अकाउंटिंग और प्रति-फ़्लैट बिलिंग डैशबोर्ड।",
        b4_t: "सोलर स्ट्रीट लाइट व पाथवे लाइटिंग",
        b4_d: "सोसाइटी कंपाउंड लाइटिंग को स्टैंडअलोन सोलर लैंप से बदलें — कोई वायरिंग नहीं, कोई बिजली बिल नहीं, स्वचालित dusk-to-dawn।",
        b5_t: "टावर्स के लिए सोलर वॉटर हीटिंग",
        b5_d: "हाई-राइज़ के लिए केंद्रीय सोलर वॉटर हीटिंग सिस्टम — गीज़र चलाने की लागत के एक हिस्से में निवासियों के लिए असीमित गर्म पानी।",
        b6_t: "ESG-ग्रेड ग्रीन बिल्डिंग क्रेडिट",
        b6_d: "IGBC / GRIHA ग्रीन-सोसाइटी प्रमाणीकरण पात्रता, कार्बन-ऑफ़सेट रिपोर्ट और आपकी सोसाइटी की ब्रोशर के लिए एक सच्ची कहानी।",
        st_eyebrow: "सोसाइटियों के लिए यह कैसे काम करता है",
        st_title_a: "",
        st_title_hl: "पहली AGM",
        st_title_b: "से चालू होने तक, 60 दिन में।",
        st1_t: "प्रारंभिक सोसाइटी मीटिंग",
        st1_d: "हम मुफ़्त छाया विश्लेषण के साथ मैनेजिंग कमेटी को अवसर प्रस्तुत करते हैं।",
        st2_t: "विस्तृत प्रोपोज़ल व EGM",
        st2_d: "बैंकेबल फ़ाइनेंशियल मॉडल, मतदान टेम्पलेट, FAQs तैयार — हम EGM में उपस्थित होते हैं।",
        st3_t: "अनुमोदन व सब्सिडी",
        st3_d: "हम DISCOM, सोसाइटी NOC और हर इच्छुक फ़्लैट के लिए PM Surya Ghar पोर्टल संभालते हैं।",
        st4_t: "इंस्टॉलेशन व कमीशनिंग",
        st4_d: "चरणबद्ध, गैर-व्यवधानकारी इंस्टॉलेशन एक सामान्य सोसाइटी के लिए 30–45 दिनों में पूरा।",
        o_eyebrow: "एक पूर्ण क्लीन-एनर्जी स्टैक",
        o_title: "हम सोसाइटी की हर सोलर ज़रूरत को एक साथ देते हैं।",
        o1: "कॉमन-एरिया व व्यक्तिगत फ़्लैट रूफटॉप सोलर (PM Surya Ghar संपर्क के साथ)",
        o2: "कंपाउंड के लिए सोलर स्ट्रीट व पाथवे लाइटिंग",
        o3: "टावर्स व क्लब-हाउस के लिए केंद्रीय सोलर वॉटर हीटिंग",
        o4: "निवासियों के लिए EV चार्जिंग पॉइंट — सोलर से चलने वाले",
        o5: "आउटेज के दौरान लिफ़्ट के लिए लिथियम BESS बैकअप",
        o6: "त्रैमासिक प्रदर्शन रिपोर्ट के साथ 25-वर्षीय O&M",
        o_cta: "सोसाइटी वॉकथ्रू बुक करें",
        o_badge: "बंडल ऑफ़र",
        o_badge_title: "सोलर + लाइट्स + वॉटर हीटिंग",
        o_badge_caption: "एक सोसाइटी कॉन्ट्रैक्ट में",
        fq_eyebrow: "FAQs",
        fq_title_a: "सोसाइटी कमेटियों के लिए",
        fq_title_hl: "जल्दी जवाब।",
        fq1_q: "भुगतान कौन करता है — सोसाइटी या व्यक्तिगत फ़्लैट मालिक?",
        fq1_a: "दोनों मॉडल काम करते हैं। कॉमन-एरिया लोड सोसाइटी कोर्पस या छोटे प्रति-फ़्लैट योगदान से वित्तपोषित होता है; व्यक्तिगत फ़्लैट अतिरिक्त रूप से नेट-मीटरिंग चुन सकते हैं और स्वयं PM Surya Ghar सब्सिडी क्लेम कर सकते हैं।",
        fq2_q: "क्या यह हमारे 12-मंज़िला हाई-राइज़ में सीमित छत के साथ काम करेगा?",
        fq2_a: "जी हाँ — हम रूफटॉप, पोडियम, पार्किंग शेड व façade समाधानों को मिलाकर हाइब्रिड सिस्टम डिज़ाइन करते हैं। बहुत ऊँचे टावर्स के लिए, छोटा रूफटॉप सेटअप भी पूरी कॉमन-एरिया लोड कवर करता है।",
        fq3_q: "आप AGM/EGM अनुमोदन कैसे संभालते हैं?",
        fq3_a: "हम वितरण के लिए तैयार तकनीकी व वित्तीय प्रोपोज़ल, मतदान टेम्पलेट प्रदान करते हैं, और हमारी टीम EGM में निवासियों के सवालों के सीधे जवाब देती है।",
        fq4_q: "इंस्टॉलेशन के बाद मेंटेनेंस का क्या?",
        fq4_a: "हर परियोजना 25-वर्षीय प्रदर्शन गारंटी और 5-वर्षीय मुफ़्त O&M कॉन्ट्रैक्ट के साथ आती है — त्रैमासिक सफ़ाई, वार्षिक थर्मोग्राफ़ी और रिमोट मॉनिटरिंग।",
        final_a: "अपनी सोसाइटी को बनाएँ",
        final_hl: "भारत का अगला सोलर पता।",
        final_sub: "अपनी सोसाइटी की जानकारी साझा करें — हमारी टीम मुफ़्त वॉकथ्रू के लिए संपर्क करेगी और AGM-तैयार प्रोपोज़ल तैयार करेगी।",
        final_cta: "सोसाइटी विज़िट मांगें",
        back_home: "होम पर वापस जाएँ",
        lead_title: "सोसाइटी वॉकथ्रू मांगें",
        lead_subtitle: "अपनी जानकारी साझा करें — हमारी टीम मुफ़्त सोसाइटी विज़िट तय करेगी।",
      },

  },

  mr: {
    nav: {
      services: "सेवा",
      how: "हे कसे काम करते",
      subsidy: "सबसिडी",
      savings: "बचत",
      process: "प्रक्रिया",
      faq: "प्रश्नोत्तरे",
      admin: "अ‍ॅडमिन",
      cta: "मोफत कोट मिळवा",
      menu: "मेनू",
      residential: "रहिवासी",
      commercial: "कमर्शियल",
      housing: "हाउसिंग सोसायट्या",
    },
    hero: {
      badge: "MNRE सूचीबद्ध · PM सूर्य घर तयार",
      title_pre: "आपले घर",
      title_highlight: "सूर्यप्रकाशा",
      title_post1: "ने चालवा आणि",
      title_post2: "वीज बिल कमी करा.",
      subtitle_pre:
        "पुर्वोदय सोलर संपूर्ण भारतातील घरे आणि शेतांसाठी छतावरील सोलर सिस्टिम डिझाइन, स्थापित आणि सेवा देते —",
      subtitle_and: "आणि",
      subtitle_post: "सबसिडीसाठी संपूर्ण सहाय्यासह.",
      cta_quote: "मोफत रूफटॉप कोट मिळवा",
      cta_calc: "आपली बचत काढा",
      stat1_k: "₹78,000",
      stat1_v: "कमाल सबसिडी (निवासी)",
      stat2_k: "25 वर्षे",
      stat2_v: "पॅनल कामगिरी",
      stat3_k: "4–5 वर्षे",
      stat3_v: "सामान्य परतफेड",
      live_label: "सरासरी मासिक बचत",
      live_value: "₹4,120",
      live_caption: "सामान्य 3 kW घरासाठी PM सूर्य घर सबसिडीनंतर",
      this_month: "या वर्षात",
      saved: "सरासरी कौटुंबिक बचत",
      co2: "t CO₂ वार्षिक कमी",
    },
    trust: {
      heading: "टियर-1 पॅनल आणि इन्व्हर्टर ब्रँड्ससोबत काम",
    },
    services: {
            cta_explore: "फायदे पहा",
      cta_street_b2b: "B2B प्रकल्पांसाठी संपर्क करा →",
eyebrow: "संपूर्ण सोलर सोल्यूशन्स",
      title_pre: "एक भागीदार.",
      title_hl: "प्रत्येक सोलर गरज",
      title_post: "पूर्ण.",
      subtitle: "छतापासून कारखान्यांपर्यंत, EV चार्जरपासून स्ट्रीट लाइटपर्यंत — पुर्वोदय सोलर तुमच्या जागेला आणि बजेटला अनुरूप संपूर्ण क्लीन-एनर्जी सोल्यूशन्स देते.",
      cta: "ही सेवा एक्सप्लोर करा",
      items: [
        { key: "rooftop", title: "निवासी रूफटॉप सोलर", desc: "घरांसाठी MNRE-सूचीबद्ध रूफटॉप इन्स्टॉलेशन — PM सूर्य घर योजनेअंतर्गत ₹78,000 पर्यंत केंद्रीय सबसिडी मिळवा.", highlight: "PM सूर्य घर योजना" },
        { key: "commercial", title: "कमर्शियल व इंडस्ट्रियल सोलर", desc: "कारखाने, कार्यालये आणि गोदामांसाठी MW स्केल रूफटॉप व ग्राउंड-माउंट सिस्टिम. वीज खर्च 60% पर्यंत कमी करा आणि 40% त्वरित घसारा लाभ घ्या.", highlight: "40% पर्यंत कर बचत" },
        { key: "ev", title: "EV चार्जिंग सोल्यूशन्स", desc: "घरे, कार्यालये, मॉल्स आणि महामार्ग प्लाझासाठी सोलर-संचालित AC व DC फास्ट EV चार्जर — रूफटॉप सोलरसह शून्य-खर्च ड्रायव्हिंग.", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "बॅटरी एनर्जी स्टोरेज", desc: "बॅकअप पॉवर, पीक शेविंग आणि संपूर्ण ऑफ-ग्रिड स्वातंत्र्यासाठी लिथियम-आयन BESS. 5 kWh घरगुती ते 1 MWh+ कमर्शियल बँकपर्यंत.", highlight: "10 वर्षांची वॉरंटी" },
        { key: "street", title: "सोलर स्ट्रीट लाइटिंग", desc: "सोसायट्या, कॅम्पस, महामार्ग व ग्रामीण रस्त्यांसाठी स्टँड-अलोन ऑल-इन-वन सोलर स्ट्रीट लाइट — शून्य वायरिंग, शून्य वीज बिल.", highlight: "ऑटो डस्क-टू-डॉन" },
        { key: "society", title: "हाउसिंग सोसायटी सोलर", desc: "अपार्टमेंट कॉम्प्लेक्ससाठी ग्रुप रूफटॉप व कॉमन-एरिया सोलर — वैयक्तिक फ्लॅट्सवर PM सूर्य घर लाभ आणि सोसायटीची बचत.", highlight: "RWA-फ्रेंडली" },
        { key: "heater", title: "सोलर वॉटर हीटर", desc: "घरे, वसतिगृहे व हॉटेलसाठी इव्हॅक्युएटेड ग्लास-ट्यूब सोलर वॉटर हीटर — 365 दिवस गरम पाणी, गिझरचे बिल पूर्ण बंद.", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "छतावरील सोलर कसे काम करते",
      title_pre: "",
      title_hl: "सूर्यप्रकाशा",
      title_mid: "पासून बचतीपर्यंत — चार सोप्या पायऱ्यांमध्ये.",
      subtitle:
        "रूफटॉप सोलर ही जादू नाही, तर स्मार्ट मीटरिंगसह सोप्या भौतिकशास्त्राची सुंदर जोड आहे. दररोज स्वयंचलितपणे हेच घडते:",
      steps: [
        {
          title: "सूर्यप्रकाश पॅनलवर पडतो",
          desc:
            "आपल्या छतावरील हाय-एफिशिएन्सी मोनो PERC पॅनल दिवसाच्या प्रकाशातून DC वीज तयार करतात.",
        },
        {
          title: "इन्व्हर्टर AC मध्ये रूपांतर करतो",
          desc:
            "स्मार्ट ग्रिड-टाय इन्व्हर्टर 96% कार्यक्षमतेने DC ला AC मध्ये बदलतो जो आपल्या घरातील प्रत्येक उपकरण चालवतो.",
        },
        {
          title: "घर प्रथम हेच वापरते",
          desc:
            "पंखे, लाईट, AC, पंप — दिवसा सूर्यापासून चालतात, ग्रिडवरील अवलंबन खूप कमी होते.",
        },
        {
          title: "नेट मीटर उर्वरित बँक करतो",
          desc:
            "अतिरिक्त युनिट्स ग्रिडकडे परत जातात. महिन्याअखेरीस फक्त नेट वापराचे बिल येते.",
        },
      ],
      kpis: [
        { k: "70–90%", v: "स्थापनेनंतर मासिक वीज बिलात घट." },
        { k: "₹0 EMI", v: "भागीदार बँकांमार्फत लवचिक अर्थसहाय्य." },
        { k: "25 वर्षे", v: "पॅनल आउटपुट वॉरंटी (किमान 80% कामगिरी)." },
      ],
    },
    subsidy: {
      eyebrow: "सरकारी सबसिडी",
      title_pre: "मिळवा",
      title_hl: "₹78,000",
      title_post: " पर्यंत सवलत — कागदपत्रांची जबाबदारी आमची.",
      subtitle:
        "घर असो वा शेत, भारत सरकार सोलर अवलंबण्यासाठी उदार सबसिडी देते. पुर्वोदय सोलर प्रत्येक अर्ज सुरुवातीपासून वितरणापर्यंत हाताळतो.",
      tab_res: "निवासी · PM सूर्य घर",
      tab_agri: "शेती · PM-KUSUM",
      res_title: "PM सूर्य घर: मोफत वीज योजना",
      res_desc:
        "भारत सरकारने सुरू केलेली ही योजना, आपल्या घरावर रूफटॉप सोलर बसवल्यानंतर सबसिडी थेट आपल्या बँक खात्यात देते.",
      slab_labels: ["1 kW पर्यंत", "2 kW पर्यंत", "3 kW व अधिक"],
      slab_notes: ["₹30,000 प्रति kW", "₹30,000 प्रति kW", "कमाल मर्यादा"],
      res_bullets: [
        "निवासी घरांवरील रूफटॉप PV सिस्टिमसाठी लागू.",
        "पात्र कुटुंबांना दरमहा 300 युनिट वीज मोफत.",
        "कमिशनिंग व तपासणीनंतर थेट बँक ट्रान्सफर.",
        "आमची टीम राष्ट्रीय पोर्टलवर अधिकृत अर्ज करते.",
      ],
      read_official: "अधिकृत योजना वाचा",
      savings_label: "आपली बचत",
      savings_amount: "₹78,000",
      savings_note:
        "PM सूर्य घर अंतर्गत 3 kW+ निवासी रूफटॉपवर कमाल केंद्रीय सबसिडी. राज्य प्रोत्साहने अतिरिक्त असू शकतात.",
      example_label: "उदाहरण · 3 kW सिस्टम",
      example_cost: "सिस्टम खर्च",
      example_subsidy: "सबसिडी",
      example_pay: "आपण भरा",
      agri_title: "PM-KUSUM योजना",
      agri_desc:
        "प्रधानमंत्री किसान ऊर्जा सुरक्षा एवं उत्थान महाभियान शेतकऱ्यांना सोलर पंप बसवण्यास व कृषी फीडर सोलरायझेशन करण्यास मदत करतो — डिझेलवरील अवलंबन कमी आणि विश्वासार्ह दिवसाची वीज देतो.",
      agri_components: [
        { k: "कॉम्प-A", v: "नापीक जमिनीवर ग्रिड-जोडलेले सोलर प्लांट" },
        { k: "कॉम्प-B", v: "स्वतंत्र सोलर पाणी पंप" },
        { k: "कॉम्प-C", v: "विद्यमान ग्रिड पंपांचे सोलरायझेशन" },
      ],
      agri_bullets: [
        "सोलर पंपवर 60% पर्यंत केंद्र + राज्य सबसिडी.",
        "30% बँक कर्ज; शेतकऱ्याचा वाटा फक्त ~10%.",
        "अतिरिक्त उत्पन्नासाठी शिल्लक वीज DISCOM ला विका.",
        "स्थापना व DBT सहाय्य पुर्वोदयकडून.",
      ],
      farmer_benefit: "शेतकऱ्याचा लाभ",
      farmer_amount: "60% पर्यंत सवलत",
      farmer_note:
        "सोलर पंप स्थापनेवर, तसेच शिल्लक वीज ग्रिडला विकून उत्पन्नाचा स्रोत.",
      sub_individual: "वैयक्तिक घरे",
      sub_society: "ग्रुप हाउसिंग सोसायट्या",
      ind_box_label: "तुमची एकूण बचत",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar अंतर्गत 3 kW+ रहिवासी रूफटॉप सिस्टमवर कमाल केंद्रीय सबसिडी. वर अतिरिक्त राज्य प्रोत्साहन मिळू शकतात.",
      ind_example_title: "उदाहरण · 3 kW सिस्टम",
      ind_example_rows: [
        { label: "सिस्टम किंमत", value: "₹1,80,000" },
        { label: "सबसिडी", value: "₹78,000" },
      ],
      ind_example_net_label: "तुम्ही द्याल",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "तुम्हाला आणखी मिळेल",
      ind_extras: [
        "पात्र कुटुंबांना दरमहा 300 युनिटपर्यंत मोफत",
        "अतिरिक्त उत्पादनासाठी नेट-मीटरिंग क्रेडिट",
        "25-वर्षीय पॅनेल कामगिरी वारंटी",
        "पुर्वोदयद्वारे PM Surya Ghar पोर्टल फायलिंग मोफत",
      ],
      ghs_title: "ग्रुप हाउसिंग सोसायट्या (GHS / RWAs)",
      ghs_intro: "PMSGY अंतर्गत केंद्र सरकार लिफ्ट, पाण्याचे पंप, स्ट्रीटलाइट, लॉबी आणि कॉरिडोर लाइटिंगसारख्या कॉमन फॅसिलिटीसाठी ग्रिड-कनेक्टेड रूफटॉप सोलरवर ₹18,000 प्रति kW देते.",
      ghs_structure_label: "सबसिडी रचना",
      ghs_structure: [
        "स्थापित कॉमन-एरिया सोलर क्षमतेच्या ₹18,000 / kW.",
        "प्रत्येक सोसायटीसाठी कमाल 500 kW — म्हणजे ₹90 लाखपर्यंत सबसिडी.",
        "अनुज्ञेय कॉमन क्षमता = 3 kW × फ्लॅट्सची संख्या (वैयक्तिक रूफटॉपसह).",
      ],
      ghs_eligibility_label: "पात्रता तपासणी",
      ghs_eligibility: [
        "CHS / RWA स्वतःच्या PAN सह कायदेशीर नोंदणीकृत असावे.",
        "सोसायटीच्या नावावर सक्रिय कॉमन-सर्व्हिस वीज मीटर.",
        "थेट सबसिडी हस्तांतरणासाठी समर्पित सोसायटी बँक खाते.",
        "केवळ मेक-इन-इंडिया (DCR-अनुरूप) पॅनेल — आयात केलेले मॉड्यूल नाहीत.",
      ],
      ghs_box_label: "सोसायटीची एकूण बचत",
      ghs_box_amount: "₹90 लाखपर्यंत",
      ghs_box_caption: "एका हाउसिंग सोसायटीसाठी कमाल PMSGY केंद्रीय सबसिडी — ₹18,000 प्रति kW दराने, 500 kW वर कमाल.",
      ghs_example_title: "गणना · 100-फ्लॅट सोसायटी",
      ghs_example_rows: [
        { label: "अनुज्ञेय कॉमन क्षमता (3 kW × 100 फ्लॅट)", value: "300 kW" },
        { label: "अंदाजे प्रकल्प किंमत (₹50,000 / kW)", value: "₹1.50 कोटी" },
        { label: "PMSGY सबसिडी (₹18,000 / kW × 300 kW)", value: "₹54 लाख" },
        { label: "वार्षिक कॉमन-एरिया बिल बचत", value: "~₹22 लाख" },
      ],
      ghs_example_net_label: "सोसायटीचा एकूण खर्च",
      ghs_example_net_value: "₹96 लाख",
      ghs_example_payback: "4–5 वर्षांत पेबॅक · 20+ वर्षे मोफत कॉमन-एरिया वीज",
      ghs_documents_label: "सोसायटीकडून आवश्यक कागदपत्रे",
      ghs_documents: [
        "सोसायटी नोंदणी प्रमाणपत्र + PAN",
        "मागील 3 महिन्यांचे कॉमन-एरिया वीज बिल",
        "सोसायटी बँक खाते पासबुक (पहिले पान)",
        "प्रकल्पाला मंजुरी देणारा AGM/EGM ठराव",
        "बिल्डिंग प्लॅन आणि रूफटॉप लेआउट (आम्ही सर्वे करू शकतो)",
      ],
    },
    calc: {
      eyebrow: "बचत कॅल्क्युलेटर",
      title_pre: "तुम्ही नक्की किती",
      title_hl: "बचत कराल",
      title_post: " ते पाहा — सुरू करण्यासाठी + आणि − दाबा.",
      subtitle:
        "तुमची उपकरणे निवडा, तास समायोजित करा, आणि आम्ही थेट सबसिडी आणि परतफेडीच्या अंदाजांसह योग्य आकाराची सोलर सिस्टिम सुचवू.",
      tab_res: "निवासी",
      tab_agri: "शेती",
      appliances: "तुमची उपकरणे",
      pincode_placeholder: "पिनकोड",
      tariff: "टॅरिफ ₹{rate}/युनिट",
      detected: "ओळखले",
      default_hours: "डिफॉल्ट {h}तास/दिवस",
      hours_day: "तास/दिवस",
      snapshot: "तुमचा सोलर स्नॅपशॉट",
      daily: "दैनिक",
      monthly_units: "मासिक युनिट्स",
      current_bill: "सध्याचे बिल",
      recommended: "शिफारस",
      investment: "गुंतवणूक तपशील",
      system_cost: "सिस्टम खर्च",
      pm_subsidy: "PM सूर्य घर सबसिडी",
      kusum_subsidy: "PM-KUSUM सबसिडी (~30%)",
      net_investment: "निव्वळ गुंतवणूक",
      monthly_savings: "मासिक बचत",
      payback: "परतफेड",
      co2_yr: "CO₂/वर्ष",
      cta_quote: "माझा {kw} kW कोट मिळवा",
      disclaimer:
        "अंदाज 4 सूर्य-तास/दिवस आणि ₹{rate}/युनिट टॅरिफवर आधारित. अंतिम डिझाइन मोफत साइट भेटीनंतर.",
      appliance_labels: {
        fan: "छताचा पंखा",
        tube: "ट्यूब / LED लाईट",
        ac: "एअर कंडिशनर (1.5T)",
        fridge: "रेफ्रिजरेटर",
        tv: "टेलिव्हिजन",
        geyser: "गिझर",
        washer: "वॉशिंग मशीन",
        microwave: "मायक्रोवेव्ह",
        computer: "कॉम्प्युटर / लॅपटॉप",
        pump_home: "पाण्याचा पंप (1 HP)",
        pump_3hp: "पाण्याचा पंप (3 HP)",
        pump_5hp: "पाण्याचा पंप (5 HP)",
        pump_75hp: "पाण्याचा पंप (7.5 HP)",
        pump_10hp: "सबमर्सिबल (10 HP)",
        lights: "शेतातील लाइटिंग",
        chaffcutter: "चाफ कटर",
      },
    },
    workflow: {
      eyebrow: "आमची प्रक्रिया",
      title_pre: "सहा पायऱ्या.",
      title_hl: "शून्य",
      title_post: "त्रास.",
      subtitle:
        "पहिल्या कॉलपासून 25 वर्षांच्या बचतीपर्यंत, संपूर्ण प्रवास पुर्वोदय हाताळतो. तो असा उलगडतो:",
      activities: "क्रिया",
      deliverables: "वितरणे",
      outcome: "परिणाम",
      steps: [
        {
          title: "साइट भेट व व्यवहार्यता",
          activities: "छाया विश्लेषण, छत तपासणी, भार अभ्यास, वीज बिल पुनरावलोकन.",
          deliverables: "सिस्टम आकार व उत्पादन अंदाजासह व्यवहार्यता अहवाल.",
          outcome: "आत्मविश्वास की तुमची छत सोलरसाठी तयार आहे.",
        },
        {
          title: "डिझाइन व प्रस्ताव",
          activities: "3D रूफटॉप लेआउट, संरचनात्मक + विद्युत SLD, पॅनल व इन्व्हर्टर निवड.",
          deliverables: "BOM, वेळापत्रक व सबसिडी अंदाजासह तपशीलवार प्रस्ताव.",
          outcome: "पारदर्शक दर, कुठलेही आश्चर्य नाही.",
        },
        {
          title: "मंजुरी व सबसिडी अर्ज",
          activities: "नेट-मीटर अर्ज, DISCOM संपर्क, PM सूर्य घर / KUSUM फायलिंग.",
          deliverables: "नेट मीटरिंग मंजुरी + सबसिडी नोंदणी.",
          outcome: "कागदपत्रांची जबाबदारी पूर्णपणे सांभाळली.",
        },
        {
          title: "स्थापना",
          activities: "स्ट्रक्चर माउंटिंग, पॅनल बसवणे, केबलिंग, इन्व्हर्टर कमिशनिंग.",
          deliverables: "पूर्णपणे स्थापित, लेबल व चाचणी केलेली रूफटॉप सिस्टिम.",
          outcome: "सामान्यतः 5–7 दिवसांत वीज वाहते.",
        },
        {
          title: "कमिशनिंग व गो-लाइव्ह",
          activities: "DISCOM तपासणी, नेट मीटर बसवणे, अ‍ॅप ऑनबोर्डिंग.",
          deliverables: "हँडओव्हर पॅक + मॉनिटरिंग अ‍ॅप लॉगिन.",
          outcome: "तुमचा मीटर उलटा फिरू लागतो.",
        },
        {
          title: "विक्रीनंतर व देखरेख",
          activities: "दूरस्थ कामगिरी देखरेख, नियमित स्वच्छता, AMC आधार.",
          deliverables: "वार्षिक कामगिरी अहवाल, प्राधान्य हेल्पलाइन.",
          outcome: "25 वर्षांचा चिंता-मुक्त सूर्यप्रकाश.",
        },
      ],
    },
    faq: {
      eyebrow: "नेहमी विचारले जाणारे प्रश्न",
      title_pre: "सर्वांचे प्रश्न —",
      title_hl: "प्रामाणिक उत्तरे.",
      items: [
        {
          q: "सोलर सिस्टिमसाठी किती छताची जागा लागते?",
          a:
            "सामान्य नियम: प्रत्येक 1 kW साठी ~80–100 चौ.फूट सावली-मुक्त जागा. एक सामान्य 3 kW घरगुती सिस्टिम 300 चौ.फूट छतावर सहज बसते.",
        },
        {
          q: "पावसाळ्यात किंवा ढगाळ दिवसांत सोलर काम करते का?",
          a:
            "हो. पॅनल विखुरलेल्या प्रकाशातही (पीक आउटपुटच्या ~10–25%) वीज तयार करतात. नेट-मीटरिंगमुळे तुम्ही ग्रिडकडून अतिरिक्त घेऊ शकता.",
        },
        {
          q: "PM सूर्य घर सबसिडी प्रक्रिया काय आहे?",
          a:
            "तुम्ही राष्ट्रीय पोर्टलवर नोंदणी करता, आम्ही अर्ज करतो, MNRE-सूचीबद्ध विक्रेत्यामार्फत (पुर्वोदय एक आहे) स्थापना, आणि DISCOM तपासणीनंतर सबसिडी थेट बँक खात्यात.",
        },
        {
          q: "स्थापनेस किती वेळ लागतो?",
          a:
            "एक मानक 3 kW निवासी स्थापना 3–5 दिवस घेते. मंजुरी व नेट मीटरिंगसह संपूर्ण — सामान्यतः 4–6 आठवडे.",
        },
        {
          q: "सोलर सिस्टिमची देखभाल काय लागते?",
          a:
            "फारच कमी. धुळीच्या भागात 1–2 महिन्यांनी स्वच्छता आणि वार्षिक तपासणी. प्रत्येक स्थापनेसह आम्ही मोफत 1 वर्ष AMC देतो.",
        },
        {
          q: "शेतकरी खरंच PM-KUSUM मधून उत्पन्न मिळवू शकतात का?",
          a:
            "नक्कीच. कॉम्प-A आणि C अंतर्गत शिल्लक सोलर वीज DISCOM निश्चित दराने खरेदी करते — शेतकऱ्यांना दिवसभर मोफत पंपासह स्थिर अतिरिक्त उत्पन्न.",
        },
      ],
    },
    footer: {
      tag: "भविष्याला उजळ करा",
      desc:
        "MNRE-सूचीबद्ध रूफटॉप सोलर EPC कंपनी जी भारतीय घरे आणि शेतांना बिल कमी करण्यास व ऊर्जा बदलास मदत करते.",
      explore: "पाहा",
      contact: "संपर्क",
      rights: "© {year} पुर्वोदय सोलर. सर्व हक्क राखीव.",
      built: "स्वच्छ भारतासाठी ☀ ने बनवले.",
    },
    lead: {
      badge: "मोफत व कोणतेही बंधन नाही",
      title_default: "मोफत रूफटॉप कोट मिळवा",
      subtitle_default:
        "तुमचे तपशील शेअर करा — पुर्वोदय तज्ज्ञ 24 तासांत कॉल करतील.",
      title_calc: "तुमचा वैयक्तिक कोट मिळवा",
      title_idle: "अजून शोधत आहात? आम्ही मदत करू.",
      subtitle_idle:
        "तुमचे तपशील शेअर करा — आम्ही तुमच्या फोनवर सानुकूल सोलर बचत पाठवू.",
      full_name: "पूर्ण नाव *",
      name_ph: "तुमचे नाव",
      phone: "फोन *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "पिनकोड",
      email: "ईमेल",
      email_ph: "you@example.com",
      i_need: "मला सोलर हवे",
      seg_home: "घर / निवासी",
      seg_farm: "शेत / शेती",
      seg_commercial: "कमर्शियल / इंडस्ट्रियल",
      anything_else: "आणखी काही?",
      msg_ph: "छताचा आकार, सध्याचे बिल, इ.",
      submit: "मला मोफत कॉल-बॅक हवा",
      submitting: "पाठवत आहे…",
      thanks_title: "तुम्ही आमच्या यादीत आहात!",
      thanks_desc: "एक सोलर सल्लागार लवकरच संपर्क साधेल.",
      toast_ok: "धन्यवाद! आम्ही लवकरच कॉल करू.",
      toast_required: "नाव व फोन आवश्यक",
      toast_fail: "पाठवू शकलो नाही. कृपया पुन्हा प्रयत्न करा.",
      consent:
        "सबमिट करून, तुम्ही पुर्वोदय सोलरकडून संपर्काला संमती देता.",
    },
    whatsapp: { label: "आमच्याशी चॅट करा" },
    common: { language: "भाषा" },
    ts: {
        eyebrow: "पुर्वोदय का?",
        title_pre: "एक गंभीर भागीदार —",
        title_hl: "भारताच्या सौर संक्रमणासाठी",
        subtitle: "पुर्वोदय सोलर रहिवासी छप्पर, हाउसिंग सोसायट्या आणि कमर्शियल व इंडस्ट्रियल युनिट्ससाठी स्वच्छ ऊर्जा देण्यासाठी तयार आहे — डिझाइन, अंमलबजावणी, सबसिडी फायलिंग आणि 25 वर्षांच्या ऑपरेशनची पूर्ण जबाबदारी.",
        p1_eyebrow: "एनर्जी ट्रांझिशन पार्टनर",
        p1_title: "भारताच्या सौर-आधारित ऊर्जा संक्रमणाचा विश्वासू भागीदार",
        p1_desc: "देशाच्या रहिवासी, हाउसिंग सोसायटी आणि कॉर्पोरेट प्रवासाला स्वच्छ ऊर्जेकडे नेण्यासाठी विशेषतः तयार — दीर्घकालीन कामगिरीसाठी, क्षणिक दिखाव्यासाठी नाही.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM फायलर",
        p2_title: "केंद्रीय सौर सबसिडीचा अधिकृत फायलर",
        p2_desc: "आम्ही वैयक्तिक घरांसाठी आणि हाउसिंग सोसायट्यांसाठी PM Surya Ghar आणि शेतकऱ्यांसाठी PM-KUSUM दाखल करतो — सर्व कागदपत्रांची जबाबदारी आमची, मधले लोक नाहीत.",
        p3_eyebrow: "एंड-टू-एंड EPC + 25-वर्षीय O&M",
        p3_title: "एक जबाबदार भागीदार — डिझाइनपासून सुरू होईपर्यंत आणि 25 वर्षांची काळजी",
        p3_desc: "MNRE-अनुरूप डिझाइन, Tier-1 मॉड्यूल्स, प्रमाणित इंस्टॉलेशन टीम, DISCOM संपर्क, आणि 25-वर्षीय कामगिरी हमी — एकाच छताखाली.",
        promise_label: "आमचे वचन",
        promise1: "Tier-1 मॉड्यूल्स · 25-वर्षीय कामगिरी वारंटी",
        promise2: "MNRE-अनुरूप इंजिनियरिंग आणि सुरक्षा पद्धती",
        promise3: "पारदर्शक कोटेशन · छुपी किंमत नाही",
        promise4: "त्रैमासिक कामगिरी आणि साफसफाई अहवाल",
      },
    res: {
        hero_badge: "रहिवासी रूफटॉप सोलर",
        hero_title_a: "तुमचा AC, फ्रिज, गिझर —",
        hero_title_b: "सूर्यापासून मोफत चालवा.",
        hero_subtitle: "भारतातील वीज दर दरवर्षी 4–6% वाढतात. पुर्वोदय रूफटॉप पहिल्या दिवसापासून तुमचे बिल 70–95% कमी करते — आणि फक्त 4–5 वर्षांत स्वतःची किंमत वसूल करते.",
        cta_quote: "मोफत रूफटॉप कोट मिळवा",
        cta_calc: "तुमची बचत मोजा",
        stat_subsidy: "कमाल केंद्रीय सबसिडी",
        stat_reduction: "बिल कपात",
        stat_payback: "सरासरी पेबॅक",
        stat_warranty: "कामगिरी वारंटी",
        app_eyebrow: "तुमचे बिल खरोखर कुठे जाते",
        app_title_a: "प्रत्येक उपकरण,",
        app_title_b: "सूर्यप्रकाशाने चालेल.",
        app_subtitle: "एक सामान्य भारतीय घर या उपकरणांवर दरमहा ₹4,000–₹8,000 खर्च करते. सोलर हे दिवसा जवळजवळ शून्यावर आणते — आणि नेट-मीटरिंग तुमच्या रात्री संभाळते.",
        app_consumption: "वापर",
        app_current_bill: "सध्याचे बिल",
        app_note: "अंदाज ₹7/युनिट सरासरी रहिवासी दरावर आधारित. सोलर प्रत्येक बाबीचा दिवसाचा भाग पूर्णपणे नष्ट करते.",
        app_ac: "एअर कंडिशनर (1.5T)",
        app_ac_units: "12 युनिट/दिवसापर्यंत",
        app_fridge: "रेफ्रिजरेटर",
        app_fridge_units: "3.5 युनिट/दिवस",
        app_geyser: "गिझर",
        app_geyser_units: "4 युनिट/दिवस",
        app_washer: "वॉशिंग मशीन",
        app_washer_units: "2 युनिट/चक्र",
        app_tv: "TV + सेट-टॉप",
        app_tv_units: "1.5 युनिट/दिवस",
        app_fans: "सीलिंग फॅन (4)",
        app_fans_units: "3 युनिट/दिवस",
        app_lights: "LED लाइट्स (10)",
        app_lights_units: "1 युनिट/दिवस",
        b_eyebrow: "तुमच्या घरासाठी पुर्वोदय का",
        b_title_a: "सहा घरगुती फायदे,",
        b_title_b: "कोणत्याही अटी नाहीत.",
        b1_t: "मासिक बिलात 70–95% कपात",
        b1_d: "तुमचा AC, फ्रिज, गिझर, वॉशिंग मशीन, पंखे आणि लाइट्स दिवसभर मोफत सौरऊर्जेवर चालतील. बहुतेक घरांमध्ये पहिल्या दिवसापासून ₹3,000–₹8,000/महिना बचत.",
        b2_t: "₹78,000 पर्यंत PM Surya Ghar सबसिडी",
        b2_d: "भारत सरकार स्थापनेनंतर तुमच्या बँक खात्यात थेट ₹78,000 पर्यंत ट्रान्सफर करते. आम्ही पोर्टल अर्ज, कागदपत्रे आणि DISCOM संपर्क संभाळतो.",
        b3_t: "पात्र कुटुंबांना 300 युनिट/महिना मोफत",
        b3_d: "PM Surya Ghar मुफ्त बिजली योजनेअंतर्गत पात्र कुटुंबांना उत्पादन बचतीसह मोफत वीज लाभही मिळतो — अनेक घरांसाठी सोलर व्यावहारिकदृष्ट्या शून्य किंमत.",
        b4_t: "25-वर्षीय वारंटी, 5-वर्षीय पेबॅक",
        b4_d: "Tier-1 mono PERC पॅनेल 25-वर्षीय कामगिरी वारंटीसह आणि इन्व्हर्टर 10-वर्षीय हमीसह. 4–5 वर्षांच्या पेबॅकनंतर, पुढील दोन दशके प्रत्येक युनिट जवळजवळ मोफत.",
        b5_t: "नेट-मीटरिंग — अतिरिक्त विका",
        b5_d: "अतिरिक्त युनिट्स ग्रिडमध्ये परत जातात आणि तुमचा DISCOM तुम्हाला क्रेडिट देतो. अधिक उत्पादन करत आहात? रात्री आणि पावसाळ्यात वीज सुरू ठेवत मासिक क्रेडिट कमवा.",
        b6_t: "तुमचे घर भविष्यासाठी तयार ठेवा",
        b6_d: "नंतर बॅटरी (BESS), EV चार्जर किंवा सोलर वॉटर हीटर जोडा — प्रत्येक पुर्वोदय रूफटॉप तुमच्या गरजेनुसार वाढण्यासाठी डिझाइन केलेले आहे.",
        s_eyebrow: "पुर्वोदय 4-चरण रोलआउट",
        s_title_a: "",
        s_title_hl: "पहिल्या कॉल",
        s_title_b: "पासून सुरू होईपर्यंत — 4–6 आठवड्यात.",
        s1_t: "मोफत साइट भेट",
        s1_d: "आमचे इंजिनियर तुमच्या छपराचे सर्वेक्षण, सावली विश्लेषण आणि मंजूर लोड तपासतात.",
        s2_t: "कोट + सबसिडी फायलिंग",
        s2_d: "आम्ही तुमच्या वतीने PM Surya Ghar दाखल करतो — तुम्ही फक्त एक डिजिटल फॉर्म सही करता.",
        s3_t: "DISCOM आणि नेट-मीटर मंजुरी",
        s3_d: "सर्व मंजुऱ्या पुर्वोदय संभाळतो — सामान्यतः 3–4 आठवडे.",
        s4_t: "इन्स्टॉल आणि सुरू",
        s4_d: "साइटवर 3–5 दिवस. त्याच संध्याकाळी — तुमचे घर सूर्यावर चालेल.",
        f_eyebrow: "घरमालकांसाठी FAQs",
        f_title_a: "नवीन खरेदीदारांसाठी",
        f_title_hl: "जलद उत्तरे.",
        f1_q: "उन्हाळ्यात सोलर माझा AC आणि गिझर चालवेल का?",
        f1_a: "होय — आणि सर्वात मोठी बचत येथूनच येते. 5 kW रूफटॉप दिवसभर 1.5T इन्व्हर्टर AC, गिझर आणि सर्व दैनंदिन लोड सहज चालवते. आम्ही तुमच्या उपकरण प्रोफाइलनुसार सिस्टम आकार ठरवतो.",
        f2_q: "4–5 वर्षांत मी घर बदलले तर?",
        f2_a: "आधुनिक सोलर सिस्टम मॉड्यूलर आणि तुमच्या मालकीच्या मालमत्तेसाठी पोर्टेबल आहे. बहुतेक प्रकरणांमध्ये फक्त 2–3 वर्षांची बचतच स्थलांतर खर्चापेक्षा जास्त आहे.",
        f3_q: "मला खरंच ₹78,000 बचत होईल का?",
        f3_a: "हे 3 kW+ रहिवासी सिस्टमसाठी PM Surya Ghar अंतर्गत भारत सरकारकडून खरे थेट बँक हस्तांतरण आहे. कमी kW साइझला प्रमाणानुसार कमी सबसिडी मिळते. रक्कम कमिशनिंगच्या 30–60 दिवसांत पोहोचते.",
        final_a: "DISCOM ला पैसे देणे थांबवा.",
        final_hl: "स्वतःला पैसे देणे सुरू करा.",
        final_sub: "तुमची माहिती शेअर करा — आमचा सोलर सल्लागार तुमच्या घराची सिस्टम डिझाइन करेल आणि तुमची PM Surya Ghar सबसिडी मोफत फाइल करेल.",
        final_cta: "मोफत साइट भेट बुक करा",
        back_home: "होमवर परत जा",
        lead_title: "मोफत रूफटॉप सोलर कोट",
        lead_subtitle: "तुमची माहिती शेअर करा — पुर्वोदय सल्लागार 24 तासांत तुम्हाला कॉल करेल.",
      },
    com: {
        hero_badge: "कमर्शियल व इंडस्ट्रियल सोलर",
        hero_title_a: "तुमचा व्यवसाय",
        hero_title_hl: "सूर्यापासून",
        hero_title_b: "—",
        hero_title_c: "चालवा — ऊर्जा खर्च कमी करा, मार्जिन सुरक्षित करा.",
        hero_subtitle: "कारखाने, गोदामे, ऑफिसेस आणि हॉस्पिटॅलिटीसाठी MW-स्केल रूफटॉप आणि ग्राउंड-माउंट सोलर. ROI साठी डिझाइन, 25 वर्षांसाठी निर्मित.",
        cta_quote: "बिझनेस प्रोपोझल मागवा",
        cta_calc: "तुमची बचत मोजा",
        stat_reduction: "सरासरी वीज बिल कपात",
        stat_payback: "विशिष्ट पेबॅक (AD सह)",
        stat_warranty: "कामगिरी वारंटी",
        stat_upfront: "OPEX मॉडेलमध्ये अग्रिम भरणा",
        b_eyebrow: "व्यवसायासाठी पुर्वोदय का",
        b_title_a: "सहा मोजता येण्याजोगे फायदे,",
        b_title_hl: "प्रत्यक्षात.",
        b1_t: "वीज खर्चात 50–70% कपात",
        b1_d: "महाग ग्रिड वीज (₹8–12/kWh) ऐवजी स्वतः उत्पादित सोलर (₹2.5–3.5/kWh) — पहिल्या दिवसापासून EBITDA मार्जिनमध्ये थेट सुधारणा.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "इन्कम टॅक्स अॅक्टच्या कलम 32 अंतर्गत पहिल्या वर्षात संपूर्ण प्रकल्प किंमतीवर 40% AD क्लेम करा. कर भरणाऱ्या संस्थांसाठी प्रभावी पेबॅक 3–4 वर्षे.",
        b3_t: "टॅरिफ वाढीपासून संरक्षण",
        b3_d: "DISCOM टॅरिफ दरवर्षी 4–6% वाढतात. 25-वर्षीय solar PPA तुमची उत्पादन किंमत लॉक करते — पुढील दोन दशके मार्जिन सुरक्षित.",
        b4_t: "ESG आणि RE100 रिपोर्टिंग",
        b4_d: "कार्बन-ऑफसेट प्रमाणपत्रे, REC पात्रता आणि ESG-ग्रेड दस्तऐवज — प्रत्येक पुर्वोदय इंस्टॉलेशनमध्ये तुमच्या BRSR आणि सस्टेनेबिलिटी अहवालासाठी समाविष्ट.",
        b5_t: "झिरो CAPEX आणि OPEX मॉडेल्स",
        b5_d: "CAPEX मालकी निवडा किंवा आमचे 25-वर्षीय OPEX/PPA मॉडेल — कोणताही अग्रिम खर्च नाही, फक्त वापरलेल्या युनिट्सवर निश्चित डिस्काउंट टॅरिफ.",
        b6_t: "एंड-टू-एंड EPC + O&M",
        b6_d: "एक जबाबदार पॉइंट — डिझाइन, MNRE-empanelled अंमलबजावणी, नेट-मीटरिंग संपर्क, कामगिरी हमी आणि 25-वर्षीय O&M.",
        seg_eyebrow: "प्रत्येक क्षेत्रासाठी तयार",
        seg_title_a: "",
        seg_title_hl: "कारखान्याच्या मजल्यांपासून",
        seg_title_b: "कॉर्पोरेट कॅम्पसपर्यंत.",
        seg1_t: "मॅन्युफॅक्चरिंग आणि MSME",
        seg1_p1: "Captive रूफटॉप आणि ग्राउंड-माउंट सोलर",
        seg1_p2: "हाय-लोड CNC, कंप्रेसर, HVAC साठी वीज",
        seg1_p3: "1 MW+ संयंत्रांसाठी Open Access wheeling",
        seg2_t: "IT पार्क आणि ऑफिसेस",
        seg2_p1: "Carport + रूफटॉप हायब्रिड सिस्टम",
        seg2_p2: "EV-तयार चार्जिंग इन्फ्रास्ट्रक्चर",
        seg2_p3: "LEED आणि IGBC प्लॅटिनम अनुपालन",
        seg3_t: "वेअरहाउसिंग आणि लॉजिस्टिक्स",
        seg3_p1: "मोठी clear-span रूफटॉप कव्हरेज",
        seg3_p2: "कोल्ड-स्टोरेज सुसंगत BESS",
        seg3_p3: "रेफ्रिजरेशन लोड 80%+ ऑफसेट",
        seg4_t: "हॉस्पिटॅलिटी आणि रिटेल",
        seg4_p1: "गरम पाणी + वीज कॉम्बो सिस्टम",
        seg4_p2: "ब्रँड-ग्रेड सौंदर्यपूर्ण माउंटिंग",
        seg4_p3: "पाहुण्यांसाठी दृश्य सस्टेनेबिलिटी",
        roi_eyebrow: "एक खरी ROI झलक",
        roi_title_a: "500 kW कारखाना रूफटॉप.",
        roi_title_hl: "3.2-वर्षीय पेबॅक.",
        roi_subtitle: "~80,000 युनिट/महिना वापरणाऱ्या कर भरणाऱ्या उत्पादन युनिटसाठी नमुना मॉडेल. CAPEX मार्ग, पहिल्या वर्षात 40% Accelerated Depreciation, MAT-समायोजित.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD कर लाभ",
        roi3_k: "₹75 L+",
        roi3_v: "वार्षिक ऊर्जा बचत",
        roi4_k: "12,500 t",
        roi4_v: "जीवनभर CO₂ ऑफसेट",
        roi_cta: "Tailored ROI मॉडेल मिळवा",
        roi_year1: "वर्ष-1 कॅशफ्लो",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "बचत + AD लाभ",
        p_eyebrow: "आमची 6-चरण प्रक्रिया",
        p_title_a: "ऑडिटपासून",
        p_title_hl: "25-वर्षीय ऑपरेशन्सपर्यंत.",
        p1_t: "साइट आणि टॅरिफ ऑडिट",
        p1_d: "तुमच्या सुविधेसाठी मोफत सावली विश्लेषण, लोड प्रोफाइल आणि ROI मॉडेल.",
        p2_t: "कस्टम प्रस्ताव",
        p2_d: "बँकेबल फायनान्शियल मॉडेल आणि AD लाभासह CAPEX / OPEX पर्याय.",
        p3_t: "मंजुरी आणि समन्वय",
        p3_d: "DISCOM नेट-मीटरिंग, CEIG, फायर NOC — एंड-टू-एंड संभाळले.",
        p4_t: "EPC अंमलबजावणी",
        p4_d: "Tier-1 मॉड्यूल, MNRE-empanelled टीम, IEC-प्रमाणित BoS.",
        p5_t: "कमिशनिंग आणि हँडओव्हर",
        p5_d: "कामगिरी चाचणी, मॉनिटरिंग अॅप, तुमच्या टीमसाठी प्रशिक्षण.",
        p6_t: "25-वर्षीय O&M",
        p6_d: "त्रैमासिक साफसफाई, उत्पादन हमी, रिमोट मॉनिटरिंग.",
        final_a: "तुमचा बॅलन्स शीट",
        final_hl: "हरित बनवायला तयार?",
        final_sub: "तुमचे मागील 3 महिन्यांचे वीज बिल शेअर करा — आम्ही 48 तासांत CAPEX, OPEX आणि ROI सह साइट-विशिष्ट प्रस्ताव पाठवू.",
        final_cta: "व्यवसाय प्रस्ताव मागवा",
        back_home: "होमवर परत जा",
        lead_title: "व्यवसाय सोलर प्रस्ताव मिळवा",
        lead_subtitle: "तुमची माहिती शेअर करा — आम्ही 48 तासांत तुमच्या गरजेनुसार CAPEX/OPEX प्रस्ताव पाठवू.",
      },
    soc: {
        hero_badge: "हाउसिंग सोसायटी सोलर",
        hero_title_a: "एक सोसायटी, एक छप्पर,",
        hero_title_hl: "हजारो सूर्यप्रकाशित घरे.",
        hero_subtitle: "अपार्टमेंट कॉम्प्लेक्स आणि सहकारी सोसायट्या — कॉमन-एरिया बिल कमी करा, प्रत्येक फ्लॅटसाठी PM Surya Ghar सबसिडी मिळवा, आणि हरित-प्रमाणित पत्ता बना.",
        cta_quote: "सोसायटी भेट मागवा",
        cta_calc: "तुमची बचत मोजा",
        stat_subsidy: "प्रत्येक पात्र फ्लॅटवर सबसिडी",
        stat_reduction: "कॉमन-एरिया बिल कपात",
        stat_payback: "सोसायटी पेबॅक",
        stat_warranty: "कामगिरी वारंटी",
        b_eyebrow: "RWAs साठी तयार",
        b_title_a: "तुमच्या सोसायटी आणि रहिवाशांसाठी",
        b_title_hl: "सहा फायदे.",
        b1_t: "कॉमन-एरिया बिलात 80–95% कपात",
        b1_d: "लिफ्ट, पाण्याचे पंप, लॉबी आणि बेसमेंट लाइटिंग, जिम, क्लब-हाउस — सर्व रूफटॉप सोलरने चालतील. सोसायटी मेंटेनन्स कमी, रहिवासी आनंदी.",
        b2_t: "प्रत्येक फ्लॅटसाठी PM Surya Ghar",
        b2_d: "प्रत्येक फ्लॅट मालक PM Surya Ghar अंतर्गत त्याच्या रूफटॉप वाट्यावर ₹78,000 पर्यंत केंद्रीय सबसिडी क्लेम करू शकतो — सोसायटी-स्तरीय एकत्रीकरण सोपे करते.",
        b3_t: "RWA-अनुकूल शासन",
        b3_d: "पारदर्शक EGM-तयार प्रस्ताव, सोसायटी ठराव टेम्पलेट्स, उत्पादनाचा वाटा अकाउंटिंग आणि प्रति-फ्लॅट बिलिंग डॅशबोर्ड्स.",
        b4_t: "सोलर स्ट्रीट लाइट आणि पाथवे लाइटिंग",
        b4_d: "सोसायटी कंपाउंड लाइटिंगला स्टँडअलोन सोलर लॅम्पने बदला — कोणतीही वायरिंग नाही, वीज बिल नाही, स्वयंचलित dusk-to-dawn.",
        b5_t: "टॉवर्ससाठी सोलर वॉटर हीटिंग",
        b5_d: "हाय-राइझसाठी मध्यवर्ती सोलर वॉटर हीटिंग सिस्टम — गिझर चालवण्याच्या किंमतीच्या एका अंशात रहिवाशांसाठी अमर्याद गरम पाणी.",
        b6_t: "ESG-ग्रेड ग्रीन बिल्डिंग क्रेडिट्स",
        b6_d: "IGBC / GRIHA ग्रीन-सोसायटी प्रमाणन पात्रता, कार्बन-ऑफसेट अहवाल आणि तुमच्या सोसायटीच्या ब्रोशरसाठी एक खरी कथा.",
        st_eyebrow: "सोसायट्यांसाठी हे कसे काम करते",
        st_title_a: "",
        st_title_hl: "पहिल्या AGM",
        st_title_b: "पासून सुरू होईपर्यंत, 60 दिवसांत.",
        st1_t: "प्रारंभिक सोसायटी मीटिंग",
        st1_d: "आम्ही मोफत सावली विश्लेषणासह व्यवस्थापकीय समितीला संधी सादर करतो.",
        st2_t: "सविस्तर प्रस्ताव आणि EGM",
        st2_d: "बँकेबल फायनान्शियल मॉडेल, मतदान टेम्पलेट्स, FAQs तयार — आम्ही EGM ला उपस्थित राहतो.",
        st3_t: "मंजुरी आणि सबसिडी",
        st3_d: "आम्ही DISCOM, सोसायटी NOC आणि प्रत्येक इच्छुक फ्लॅटसाठी PM Surya Ghar पोर्टल संभाळतो.",
        st4_t: "इंस्टॉलेशन आणि कमिशनिंग",
        st4_d: "टप्प्याटप्प्याने, अव्यत्यय इंस्टॉलेशन सामान्य सोसायटीसाठी 30–45 दिवसांत पूर्ण.",
        o_eyebrow: "एक संपूर्ण क्लीन-एनर्जी स्टॅक",
        o_title: "आम्ही सोसायटीच्या प्रत्येक सोलर गरजेला एकत्र देतो.",
        o1: "कॉमन-एरिया आणि वैयक्तिक फ्लॅट रूफटॉप सोलर (PM Surya Ghar संपर्कासह)",
        o2: "कंपाउंडसाठी सोलर स्ट्रीट आणि पाथवे लाइटिंग",
        o3: "टॉवर्स आणि क्लब-हाउससाठी मध्यवर्ती सोलर वॉटर हीटिंग",
        o4: "रहिवाशांसाठी EV चार्जिंग पॉइंट्स — सोलरवर चालणारे",
        o5: "आउटेजदरम्यान लिफ्टसाठी लिथियम BESS बॅकअप",
        o6: "त्रैमासिक कामगिरी अहवालासह 25-वर्षीय O&M",
        o_cta: "सोसायटी वॉकथ्रू बुक करा",
        o_badge: "बंडल ऑफर",
        o_badge_title: "सोलर + लाइट्स + वॉटर हीटिंग",
        o_badge_caption: "एका सोसायटी कॉन्ट्रॅक्टमध्ये",
        fq_eyebrow: "FAQs",
        fq_title_a: "सोसायटी समित्यांसाठी",
        fq_title_hl: "जलद उत्तरे.",
        fq1_q: "पैसे कोण देतो — सोसायटी की वैयक्तिक फ्लॅट मालक?",
        fq1_a: "दोन्ही मॉडेल काम करतात. कॉमन-एरिया लोड सोसायटी कॉर्पस किंवा लहान प्रति-फ्लॅट योगदानाने वित्तपोषित होतो; वैयक्तिक फ्लॅट अतिरिक्त नेट-मीटरिंग निवडू शकतात आणि स्वतः PM Surya Ghar सबसिडी क्लेम करू शकतात.",
        fq2_q: "मर्यादित छपरासह आमच्या 12-मजली हाय-राइझमध्ये हे काम करेल का?",
        fq2_a: "होय — आम्ही रूफटॉप, पोडियम, पार्किंग शेड आणि façade सोल्यूशन्स एकत्र करून हायब्रिड सिस्टम डिझाइन करतो. खूप उंच टॉवर्ससाठी, लहान रूफटॉप अॅरे देखील पूर्ण कॉमन-एरिया लोड कव्हर करते.",
        fq3_q: "तुम्ही AGM/EGM मंजुऱ्या कशा संभाळता?",
        fq3_a: "आम्ही वितरणासाठी तयार तांत्रिक आणि आर्थिक प्रस्ताव, मतदान टेम्पलेट्स पुरवतो, आणि आमची टीम EGM ला रहिवाशांच्या प्रश्नांची थेट उत्तरे देण्यासाठी उपस्थित राहते.",
        fq4_q: "इंस्टॉलेशननंतर मेंटेनन्सचे काय?",
        fq4_a: "प्रत्येक प्रकल्प 25-वर्षीय कामगिरी हमी आणि 5-वर्षीय मोफत O&M कॉन्ट्रॅक्टसह येतो — त्रैमासिक साफसफाई, वार्षिक थर्मोग्राफी आणि रिमोट मॉनिटरिंग.",
        final_a: "तुमची सोसायटी बनवू",
        final_hl: "भारताचा पुढचा सोलर पत्ता.",
        final_sub: "तुमच्या सोसायटीचे तपशील शेअर करा — आमची टीम मोफत वॉकथ्रूसाठी संपर्क करेल आणि AGM-तयार प्रस्ताव तयार करेल.",
        final_cta: "सोसायटी भेट मागवा",
        back_home: "होमवर परत जा",
        lead_title: "सोसायटी वॉकथ्रू मागवा",
        lead_subtitle: "तुमची माहिती शेअर करा — आमची टीम मोफत सोसायटी भेट ठरवेल.",
      },

  },

  ta: {
    nav: {
      services: "சேவைகள்",
      how: "இது எப்படி செயல்படுகிறது",
      subsidy: "மானியம்",
      savings: "சேமிப்பு",
      process: "செயல்முறை",
      faq: "கேள்வி-பதில்",
      admin: "நிர்வாகி",
      cta: "இலவச மதிப்பீடு பெறுங்கள்",
      menu: "மெனு",
      residential: "வீட்டுவசதி",
      commercial: "வணிக",
      housing: "வீட்டுவசதி சங்கங்கள்",
    },
    hero: {
      badge: "MNRE பதிவுசெய்யப்பட்டது · PM சூர்ய கர் தயார்",
      title_pre: "உங்கள் வீட்டை",
      title_highlight: "சூரிய ஒளியால்",
      title_post1: "இயக்கி, உங்கள்",
      title_post2: "மின்சார பில்லைக் குறையுங்கள்.",
      subtitle_pre:
        "பூர்வோதய சோலார் இந்தியா முழுவதும் வீடுகள் மற்றும் பண்ணைகளுக்கான மேற்கூரை சூரிய மின் அமைப்புகளை வடிவமைத்து நிறுவுகிறது —",
      subtitle_and: "மற்றும்",
      subtitle_post: "மானியங்களுக்கு முழு ஆதரவுடன்.",
      cta_quote: "இலவச மேற்கூரை மதிப்பீடு பெறுங்கள்",
      cta_calc: "உங்கள் சேமிப்பைக் கணக்கிடுங்கள்",
      stat1_k: "₹78,000",
      stat1_v: "அதிகபட்ச மானியம் (குடியிருப்பு)",
      stat2_k: "25 ஆண்டுகள்",
      stat2_v: "பேனல் செயல்திறன்",
      stat3_k: "4–5 ஆண்டுகள்",
      stat3_v: "வழக்கமான திருப்பிச் செலுத்தல்",
      live_label: "சராசரி மாத சேமிப்பு",
      live_value: "₹4,120",
      live_caption: "சாதாரண 3 kW வீட்டுக்கு PM சூர்ய கர் மானியத்திற்குப் பிறகு",
      this_month: "இந்த ஆண்டு",
      saved: "சராசரி குடும்ப சேமிப்பு",
      co2: "t CO₂ ஆண்டுக்கு குறைப்பு",
    },
    trust: { heading: "முதல்-நிலை பேனல் & இன்வெர்ட்டர் பிராண்டுகளுடன் பணியாற்றுகிறோம்" },
    services: {
            cta_explore: "நன்மைகளை அறிக",
      cta_street_b2b: "B2B வரிசைப்படுத்தலுக்கு பேசுங்கள் →",
eyebrow: "முழுமையான சூரிய தீர்வுகள்",
      title_pre: "ஒரு கூட்டாளர்.",
      title_hl: "எல்லா சூரிய தேவைகளும்",
      title_post: "உள்ளடக்கப்பட்டுள்ளன.",
      subtitle: "கூரைகள் முதல் தொழிற்சாலைகள் வரை, EV சார்ஜர்கள் முதல் தெரு விளக்குகள் வரை — பூர்வோதய சோலார் உங்கள் இடம் மற்றும் பட்ஜெட்டுக்கு ஏற்ற முழு சுத்த-ஆற்றல் தீர்வுகளை வழங்குகிறது.",
      cta: "இந்த சேவையை ஆராயுங்கள்",
      items: [
        { key: "rooftop", title: "குடியிருப்பு கூரை சூரிய சக்தி", desc: "வீடுகளுக்கான MNRE-பதிவுசெய்யப்பட்ட கூரை நிறுவல்கள் — PM சூர்ய கர் யோஜனாவின் கீழ் ₹78,000 வரை மத்திய மானியம் பெறுங்கள்.", highlight: "PM சூர்ய கர் யோஜனா" },
        { key: "commercial", title: "வணிக மற்றும் தொழில் சூரிய சக்தி", desc: "தொழிற்சாலைகள், அலுவலகங்கள் & கிடங்குகளுக்கான MW அளவிலான கூரை & நில-பொருத்தல் அமைப்புகள். மின் செலவை 60% குறைத்து 40% துரிதப்படுத்தப்பட்ட தேய்மான வரிச்சலுகை பெறுங்கள்.", highlight: "40% வரை வரி சேமிப்பு" },
        { key: "ev", title: "EV சார்ஜிங் தீர்வுகள்", desc: "வீடுகள், அலுவலகங்கள், மால்கள் & நெடுஞ்சாலை பிளாசாக்களுக்கான சூரிய-இயக்கப்பட்ட AC & DC விரைவு EV சார்ஜர்கள் — பூஜ்ஜிய-செலவு ஓட்டுதலுக்கு கூரை சூரியத்துடன் இணைக்கவும்.", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "பேட்டரி ஆற்றல் சேமிப்பு", desc: "காப்பு சக்தி, பீக் ஷேவிங் & முழு ஆஃப்-கிரிட் சுதந்திரத்திற்கான லித்தியம்-அயன் BESS. 5 kWh வீட்டு பேக் முதல் 1 MWh+ வணிக வங்கி வரை.", highlight: "10 ஆண்டு உத்தரவாதம்" },
        { key: "street", title: "சூரிய தெரு விளக்கு", desc: "சங்கங்கள், வளாகங்கள், நெடுஞ்சாலைகள் & கிராமப்புற சாலைகளுக்கான தனித்-நிற்கும் அனைத்து-ஒன்றில் சூரிய தெரு விளக்குகள் — பூஜ்ஜிய வயரிங், பூஜ்ஜிய மின் பில்.", highlight: "ஆட்டோ டஸ்க்-டு-டான்" },
        { key: "society", title: "வீட்டுச் சங்க சூரிய சக்தி", desc: "அபார்ட்மென்ட் வளாகங்களுக்கான குழு கூரை & பொது-பகுதி சூரிய சக்தி — தனிப்பட்ட பிளாட்களுக்கு PM சூர்ய கர் சலுகைகள் + சங்க சேமிப்பு.", highlight: "RWA-நட்பு" },
        { key: "heater", title: "சூரிய நீர் சூடாக்கி", desc: "வீடுகள், விடுதிகள் & ஹோட்டல்களுக்கான வெற்றிட கண்ணாடி-குழாய் சூரிய நீர் சூடாக்கிகள் — 365 நாள் சூடான நீர், கீசர் மின் பில் முழுவதும் நிறுத்தம்.", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "மேற்கூரை சூரிய சக்தி எப்படி வேலை செய்கிறது",
      title_pre: "",
      title_hl: "சூரிய ஒளியிலிருந்து",
      title_mid: "சேமிப்புக்கு — நான்கு எளிய படிகளில்.",
      subtitle:
        "மேற்கூரை சூரிய சக்தி மந்திரம் அல்ல, ஸ்மார்ட் மீட்டரிங்குடன் எளிய இயற்பியல். ஒவ்வொரு நாளும் தானாகவே நடக்கிறது:",
      steps: [
        { title: "சூரிய ஒளி பேனல்களில் படுகிறது", desc: "உயர் திறன் கொண்ட மோனோ PERC பேனல்கள் பகல் ஒளியை உறிஞ்சி DC மின்சாரம் உருவாக்குகின்றன." },
        { title: "இன்வெர்ட்டர் AC ஆக மாற்றுகிறது", desc: "96% திறனில் DC ஐ AC ஆக மாற்றும் ஸ்மார்ட் கிரிட்-இணைப்பு இன்வெர்ட்டர்." },
        { title: "உங்கள் வீடு முதலில் அதைப் பயன்படுத்துகிறது", desc: "விசிறி, விளக்குகள், AC, பம்புகள் — பகல் முழுவதும் சூரியனில் இருந்து, கிரிட் பயன்பாட்டைக் கடுமையாகக் குறைக்கிறது." },
        { title: "நெட் மீட்டர் மீதமுள்ளதை சேமிக்கிறது", desc: "கூடுதல் அலகுகள் கிரிடுக்கு திரும்பி செல்கின்றன. மாத இறுதியில் நிகர நுகர்வுக்கு மட்டுமே பில்." },
      ],
      kpis: [
        { k: "70–90%", v: "நிறுவலுக்குப் பிறகு மாதாந்திர மின்சார பில் குறைப்பு." },
        { k: "₹0 EMI", v: "கூட்டு வங்கிகள் மூலம் நெகிழ்வான நிதி விருப்பங்கள்." },
        { k: "25 ஆண்டுகள்", v: "பேனல் வெளியீட்டு உத்தரவாதம் (குறைந்தது 80% செயல்திறன்)." },
      ],
    },
    subsidy: {
      eyebrow: "அரசு மானியங்கள்",
      title_pre: "பெறுங்கள்",
      title_hl: "₹78,000",
      title_post: " வரை தள்ளுபடி — ஆவணங்கள் எங்கள் பொறுப்பு.",
      subtitle:
        "வீடாக இருந்தாலும் பண்ணையாக இருந்தாலும், இந்திய அரசு சூரிய சக்தி தத்தெடுப்புக்கு தாராளமான மானியங்களை வழங்குகிறது. பூர்வோதய ஒவ்வொரு விண்ணப்பத்தையும் துவக்கத்திலிருந்து பணம் பெறும் வரை கண்காணிக்கிறது.",
      tab_res: "குடியிருப்பு · PM சூர்ய கர்",
      tab_agri: "விவசாயம் · PM-KUSUM",
      res_title: "PM சூர்ய கர்: இலவச மின்சார திட்டம்",
      res_desc:
        "இந்திய அரசால் தொடங்கப்பட்ட இந்த திட்டம், உங்கள் வீட்டில் மேற்கூரை சூரிய நிறுவலுக்குப் பிறகு மானியத்தை நேரடியாக உங்கள் வங்கிக் கணக்கிற்கு வழங்குகிறது.",
      slab_labels: ["1 kW வரை", "2 kW வரை", "3 kW & அதற்கு மேல்"],
      slab_notes: ["₹30,000 ஒரு kW க்கு", "₹30,000 ஒரு kW க்கு", "அதிகபட்ச வரம்பு"],
      res_bullets: [
        "குடியிருப்பு வீடுகளில் மேற்கூரை PV அமைப்புகளுக்கு பொருந்தும்.",
        "தகுதியான குடும்பங்களுக்கு மாதம் 300 அலகு இலவச மின்சாரம்.",
        "கமிஷனிங் & ஆய்வுக்குப் பிறகு நேரடி வங்கி மாற்றம்.",
        "எங்கள் குழு அதிகாரப்பூர்வ தேசிய போர்ட்டலில் விண்ணப்பிக்கும்.",
      ],
      read_official: "அதிகாரப்பூர்வ திட்டம்",
      savings_label: "உங்கள் சேமிப்பு",
      savings_amount: "₹78,000",
      savings_note:
        "PM சூர்ய கர் கீழ் 3 kW+ குடியிருப்பு மேற்கூரை அமைப்பில் அதிகபட்ச மத்திய மானியம். மாநில ஊக்கத்தொகைகள் கூடுதலாக இருக்கலாம்.",
      example_label: "உதாரணம் · 3 kW அமைப்பு",
      example_cost: "அமைப்பு செலவு",
      example_subsidy: "மானியம்",
      example_pay: "நீங்கள் செலுத்துங்கள்",
      agri_title: "PM-KUSUM திட்டம்",
      agri_desc:
        "விவசாயிகளுக்கு சூரிய பம்புகளை நிறுவவும், விவசாய ஃபீடர்களை சூரியமயமாக்கவும் உதவுகிறது — டீசல் சார்புநிலையைக் குறைத்து நம்பகமான பகல் மின்சக்தியை வழங்குகிறது.",
      agri_components: [
        { k: "கூ-A", v: "தரிசு நிலத்தில் கிரிட்-இணைக்கப்பட்ட சூரிய திட்டங்கள்" },
        { k: "கூ-B", v: "சுதந்திர சூரிய நீர் பம்புகள்" },
        { k: "கூ-C", v: "ஏற்கனவே உள்ள கிரிட் பம்புகளை சூரியமயமாக்குதல்" },
      ],
      agri_bullets: [
        "சூரிய பம்புகளில் 60% வரை மத்திய + மாநில மானியம்.",
        "30% வங்கிக் கடன்; விவசாயி பங்களிப்பு ~10% மட்டுமே.",
        "கூடுதல் வருமானத்திற்காக உபரி மின்சாரத்தை DISCOM க்கு விற்கலாம்.",
        "நிறுவல் & DBT உதவி பூர்வோதய மூலம்.",
      ],
      farmer_benefit: "விவசாயியின் பயன்",
      farmer_amount: "60% வரை தள்ளுபடி",
      farmer_note:
        "சூரிய பம்பு நிறுவலில், கூடுதலாக உபரி மின்சாரத்தை கிரிட்டுக்கு விற்பதன் மூலம் வருமான ஆதாரம்.",
      sub_individual: "தனிப்பட்ட வீடுகள்",
      sub_society: "குழு ஹவுசிங் சொசைட்டிகள்",
      ind_box_label: "உங்கள் மொத்த சேமிப்பு",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar கீழ் 3 kW+ வீட்டு ரூஃப்டாப் சிஸ்டத்திற்கான அதிகபட்ச மத்திய மானியம். மாநில ஊக்கத்தொகைகள் கூடுதலாக கிடைக்கலாம்.",
      ind_example_title: "உதாரணம் · 3 kW சிஸ்டம்",
      ind_example_rows: [
        { label: "சிஸ்டம் விலை", value: "₹1,80,000" },
        { label: "மானியம்", value: "₹78,000" },
      ],
      ind_example_net_label: "நீங்கள் செலுத்துவது",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "நீங்கள் இன்னும் பெறுவது",
      ind_extras: [
        "தகுதியான குடும்பங்களுக்கு மாதம் 300 யூனிட் இலவசம்",
        "உபரி உற்பத்திக்கு நெட்-மீட்டரிங் கிரெடிட்",
        "25-ஆண்டு பேனல் செயல்திறன் வாரண்டி",
        "Purvodaya மூலம் இலவச PM Surya Ghar போர்ட்டல் தாக்கல்",
      ],
      ghs_title: "குழு ஹவுசிங் சொசைட்டிகள் (GHS / RWAs)",
      ghs_intro: "PMSGY கீழ் மத்திய அரசு லிஃப்ட், தண்ணீர் பம்ப், தெரு விளக்கு, லாபி & கோரிடார் விளக்குகள் போன்ற பொது வசதிகளுக்கான கிரிட்-இணைக்கப்பட்ட ரூஃப்டாப் சோலருக்கு ₹18,000 / kW வழங்குகிறது.",
      ghs_structure_label: "மானிய அமைப்பு",
      ghs_structure: [
        "நிறுவிய பொது-பகுதி சோலார் திறனின் ₹18,000 / kW.",
        "ஒரு சொசைட்டிக்கு அதிகபட்சம் 500 kW — அதாவது ₹90 லட்சம் வரை மானியம்.",
        "அனுமதிக்கப்பட்ட பொது திறன் = 3 kW × ஃபிளாட்களின் எண்ணிக்கை.",
      ],
      ghs_eligibility_label: "தகுதி பட்டியல்",
      ghs_eligibility: [
        "CHS / RWA தனது PAN உடன் சட்டப்பூர்வமாக பதிவு செய்யப்பட்டிருக்க வேண்டும்.",
        "சொசைட்டியின் பெயரில் செயலில் உள்ள பொது-சேவை மின்சார மீட்டர்.",
        "நேரடி மானிய மாற்றத்திற்கான பிரத்யேக சொசைட்டி வங்கி கணக்கு.",
        "மேக்-இன்-இந்தியா (DCR-இணக்க) பேனல்கள் மட்டுமே.",
      ],
      ghs_box_label: "சொசைட்டி-வாரியான சேமிப்பு",
      ghs_box_amount: "₹90 லட்சம் வரை",
      ghs_box_caption: "ஒற்றை ஹவுசிங் சொசைட்டிக்கான அதிகபட்ச PMSGY மானியம் — ₹18,000 / kW விகிதத்தில், 500 kW க்கு உச்சம்.",
      ghs_example_title: "கணக்கீடு · 100-ஃபிளாட் சொசைட்டி",
      ghs_example_rows: [
        { label: "அனுமதிக்கப்பட்ட திறன் (3 kW × 100)", value: "300 kW" },
        { label: "திட்டச் செலவு (₹50,000 / kW)", value: "₹1.50 கோடி" },
        { label: "PMSGY மானியம்", value: "₹54 லட்சம்" },
        { label: "வருடாந்திர பொது பகுதி பில் சேமிப்பு", value: "~₹22 லட்சம்" },
      ],
      ghs_example_net_label: "சொசைட்டியின் நிகர செலவு",
      ghs_example_net_value: "₹96 லட்சம்",
      ghs_example_payback: "4–5 ஆண்டுகளில் பேபேக் · 20+ ஆண்டுகள் இலவச பொது மின்சாரம்",
      ghs_documents_label: "சொசைட்டியிடம் இருந்து தேவையான ஆவணங்கள்",
      ghs_documents: [
        "சொசைட்டி பதிவு சான்றிதழ் + PAN",
        "கடந்த 3 மாதங்களின் பொது மின்சார பில்",
        "சொசைட்டி வங்கி பாஸ்புக்",
        "திட்டத்திற்கான AGM/EGM தீர்மானம்",
        "கட்டிட திட்டம் & ரூஃப்டாப் தளவமைப்பு",
      ],
    },
    calc: {
      eyebrow: "சேமிப்பு கால்குலேட்டர்",
      title_pre: "நீங்கள் எவ்வளவு",
      title_hl: "சேமிப்பீர்கள்",
      title_post: " என்பதைப் பாருங்கள் — துவங்க + மற்றும் − அழுத்துங்கள்.",
      subtitle:
        "உங்கள் சாதனங்களைத் தேர்ந்தெடுத்து, மணி நேரங்களை சரிசெய்யவும் — நாங்கள் சரியான அளவுள்ள சூரிய அமைப்பை, நேரடி மானியம் & திருப்பச் செலுத்தல் மதிப்பீட்டுடன் பரிந்துரைப்போம்.",
      tab_res: "குடியிருப்பு",
      tab_agri: "விவசாயம்",
      appliances: "உங்கள் சாதனங்கள்",
      pincode_placeholder: "பின்கோடு",
      tariff: "கட்டணம் ₹{rate}/அலகு",
      detected: "கண்டறியப்பட்டது",
      default_hours: "இயல்புநிலை {h}மணி/நாள்",
      hours_day: "மணி/நாள்",
      snapshot: "உங்கள் சூரிய ஸ்னாப்ஷாட்",
      daily: "தினசரி",
      monthly_units: "மாத அலகுகள்",
      current_bill: "தற்போதைய பில்",
      recommended: "பரிந்துரை",
      investment: "முதலீட்டு விவரங்கள்",
      system_cost: "அமைப்பு செலவு",
      pm_subsidy: "PM சூர்ய கர் மானியம்",
      kusum_subsidy: "PM-KUSUM மானியம் (~30%)",
      net_investment: "நிகர முதலீடு",
      monthly_savings: "மாத சேமிப்பு",
      payback: "திருப்பச் செலுத்தல்",
      co2_yr: "CO₂/ஆண்டு",
      cta_quote: "எனது {kw} kW மதிப்பீடு",
      disclaimer:
        "மதிப்பீடுகள் 4 சூரிய மணி/நாள் மற்றும் ₹{rate}/அலகு கட்டணத்தை அடிப்படையாகக் கொண்டவை. இறுதி வடிவமைப்பு இலவச தள வருகைக்குப் பிறகு.",
      appliance_labels: {
        fan: "கூரை விசிறி",
        tube: "டியூப் / LED விளக்கு",
        ac: "ஏர் கண்டிஷனர் (1.5T)",
        fridge: "குளிர்சாதன பெட்டி",
        tv: "தொலைக்காட்சி",
        geyser: "கீசர்",
        washer: "துணி துவைக்கும் இயந்திரம்",
        microwave: "மைக்ரோவேவ்",
        computer: "கணினி / லேப்டாப்",
        pump_home: "நீர் பம்ப் (1 HP)",
        pump_3hp: "நீர் பம்ப் (3 HP)",
        pump_5hp: "நீர் பம்ப் (5 HP)",
        pump_75hp: "நீர் பம்ப் (7.5 HP)",
        pump_10hp: "நீர்மூழ்கி (10 HP)",
        lights: "பண்ணை விளக்குகள்",
        chaffcutter: "சாஃப் கட்டர்",
      },
    },
    workflow: {
      eyebrow: "எங்கள் செயல்முறை",
      title_pre: "ஆறு படிகள்.",
      title_hl: "பூஜ்ஜிய",
      title_post: "தலைவலி.",
      subtitle:
        "முதல் அழைப்பிலிருந்து 25 ஆண்டுகளின் சேமிப்பு வரை, முழு பயணத்தையும் பூர்வோதய கையாளுகிறது. இதோ:",
      activities: "செயல்பாடுகள்",
      deliverables: "வழங்குதல்கள்",
      outcome: "விளைவு",
      steps: [
        { title: "தள வருகை & சாத்தியக்கூறு", activities: "நிழல் பகுப்பாய்வு, கூரை ஆய்வு, சுமை ஆய்வு, மின்சார பில் மதிப்பாய்வு.", deliverables: "அமைப்பு அளவு & உற்பத்தி மதிப்பீட்டுடன் சாத்தியக்கூறு அறிக்கை.", outcome: "உங்கள் கூரை சூரியத்திற்கு தயார் என்ற நம்பிக்கை." },
        { title: "வடிவமைப்பு & திட்டம்", activities: "3D மேற்கூரை அமைப்பு, கட்டமைப்பு + மின்சார SLD, பேனல் & இன்வெர்ட்டர் தேர்வு.", deliverables: "BOM, காலக்கெடு & மானிய மதிப்பீட்டுடன் விரிவான திட்டம்.", outcome: "வெளிப்படையான விலை, ஆச்சரியங்கள் இல்லை." },
        { title: "ஒப்புதல்கள் & மானிய விண்ணப்பம்", activities: "நெட்-மீட்டர் விண்ணப்பம், DISCOM தொடர்பு, PM சூர்ய கர் / KUSUM தாக்கல்.", deliverables: "நெட் மீட்டரிங் ஒப்புதல் + மானிய பதிவு.", outcome: "ஆவணங்கள் முழுவதும் கையாளப்பட்டது." },
        { title: "நிறுவல்", activities: "கட்டமைப்பு ஏற்றம், பேனல் அமைத்தல், கேபிளிங், இன்வெர்ட்டர் கமிஷனிங்.", deliverables: "முழுமையாக நிறுவப்பட்ட, லேபிளிடப்பட்ட & சோதிக்கப்பட்ட மேற்கூரை அமைப்பு.", outcome: "வழக்கமாக 5–7 நாட்களில் மின்சாரம்." },
        { title: "கமிஷனிங் & லைவ்", activities: "DISCOM ஆய்வு, நெட் மீட்டர் நிறுவல், ஆப் ஆன்போர்டிங்.", deliverables: "ஹேண்ட்ஓவர் பேக் + மானிட்டரிங் ஆப் உள்நுழைவு.", outcome: "உங்கள் மீட்டர் பின்னோக்கி சுழலத் தொடங்கும்." },
        { title: "விற்பனைக்குப் பிறகு & கண்காணிப்பு", activities: "தொலை செயல்திறன் கண்காணிப்பு, குறிப்பிட்ட கால சுத்தம், AMC ஆதரவு.", deliverables: "வருடாந்திர செயல்திறன் அறிக்கைகள், முன்னுரிமை ஹெல்ப்லைன்.", outcome: "25 ஆண்டுகள் கவலையற்ற சூரிய ஒளி." },
      ],
    },
    faq: {
      eyebrow: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
      title_pre: "அனைவரின் கேள்விகள் —",
      title_hl: "நேர்மையான பதில்கள்.",
      items: [
        { q: "சூரிய அமைப்புக்கு எவ்வளவு கூரை இடம் தேவை?", a: "பொதுவான விதி: ஒரு 1 kW க்கு ~80–100 சதுர அடி நிழலில்லாத இடம். ஒரு சாதாரண 3 kW வீட்டு அமைப்பு 300 சதுர அடி கூரையில் எளிதாக பொருந்தும்." },
        { q: "மழைக்காலம் அல்லது மேக நாட்களில் சூரிய சக்தி வேலை செய்யுமா?", a: "ஆம். பேனல்கள் சிதறிய ஒளியிலும் (உச்ச வெளியீட்டின் ~10–25%) உற்பத்தி செய்கின்றன. நெட்-மீட்டரிங் மூலம் கிரிட்டிலிருந்து கூடுதல் எடுக்கலாம்." },
        { q: "PM சூர்ய கர் மானிய செயல்முறை என்ன?", a: "நீங்கள் தேசிய போர்ட்டலில் பதிவு செய்கிறீர்கள், நாங்கள் விண்ணப்பிக்கிறோம், MNRE-பதிவுசெய்யப்பட்ட வியாபாரி (பூர்வோதய) மூலம் நிறுவல், மற்றும் DISCOM ஆய்வுக்குப் பிறகு நேரடி வங்கி மாற்றம்." },
        { q: "நிறுவலுக்கு எவ்வளவு நேரம் ஆகும்?", a: "ஒரு நிலையான 3 kW குடியிருப்பு நிறுவல் 3–5 நாட்கள் எடுக்கும். ஒப்புதல்கள் & நெட் மீட்டரிங் உட்பட முழு செயல்முறை 4–6 வாரங்கள்." },
        { q: "சூரிய அமைப்புக்கு என்ன பராமரிப்பு தேவை?", a: "மிகக் குறைவு. தூசு நிறைந்த பகுதிகளில் 1–2 மாதங்களில் சுத்தம் & வருடாந்திர ஆய்வு. ஒவ்வொரு நிறுவலுடன் 1 வருட இலவச AMC." },
        { q: "விவசாயிகள் உண்மையில் PM-KUSUM இலிருந்து வருமானம் ஈட்ட முடியுமா?", a: "நிச்சயமாக. கூ-A & C கீழ் உபரி சூரிய மின்சாரத்தை DISCOM ஒரு நிலையான கட்டணத்தில் வாங்குகிறது — பம்புகளுக்கான இலவச பகல் மின்சாரத்துடன் நிலையான கூடுதல் வருமானம்." },
      ],
    },
    footer: {
      tag: "எதிர்காலத்தை பிரகாசமாக்குங்கள்",
      desc: "இந்திய வீடுகள் மற்றும் பண்ணைகளுக்கு பில்களைக் குறைக்கவும் ஆற்றல் மாற்றத்தை துரிதப்படுத்தவும் உதவும் MNRE-பதிவுசெய்யப்பட்ட மேற்கூரை சூரிய EPC நிறுவனம்.",
      explore: "ஆராயுங்கள்",
      contact: "தொடர்பு",
      rights: "© {year} பூர்வோதய சோலார். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
      built: "தூய்மையான இந்தியாவுக்காக ☀ உடன் உருவாக்கப்பட்டது.",
    },
    lead: {
      badge: "இலவசம் & கட்டுப்பாடு இல்லை",
      title_default: "இலவச மேற்கூரை மதிப்பீடு பெறுங்கள்",
      subtitle_default: "உங்கள் விவரங்களை பகிருங்கள் — ஒரு பூர்வோதய நிபுணர் 24 மணி நேரத்திற்குள் அழைப்பார்.",
      title_calc: "உங்கள் தனிப்பயன் மதிப்பீடு பெறுங்கள்",
      title_idle: "இன்னும் பார்க்கிறீர்களா? நாங்கள் உதவ விரும்புகிறோம்.",
      subtitle_idle: "உங்கள் விவரங்களை பகிருங்கள் — உங்கள் போனுக்கு தனிப்பயன் சூரிய சேமிப்பை அனுப்புவோம்.",
      full_name: "முழு பெயர் *",
      name_ph: "உங்கள் பெயர்",
      phone: "போன் *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "பின்கோடு",
      email: "மின்னஞ்சல்",
      email_ph: "you@example.com",
      i_need: "எனக்கு சூரிய தேவை",
      seg_home: "வீடு / குடியிருப்பு",
      seg_farm: "பண்ணை / விவசாயம்",
      seg_commercial: "வணிக / தொழில்",
      anything_else: "வேறு ஏதும்?",
      msg_ph: "கூரை அளவு, தற்போதைய பில், முதலியன.",
      submit: "எனக்கு இலவச திரும்ப அழைப்பு",
      submitting: "அனுப்புகிறது…",
      thanks_title: "நீங்கள் எங்கள் பட்டியலில் உள்ளீர்கள்!",
      thanks_desc: "ஒரு சூரிய ஆலோசகர் விரைவில் உங்களைத் தொடர்பு கொள்வார்.",
      toast_ok: "நன்றி! விரைவில் அழைப்போம்.",
      toast_required: "பெயர் & போன் தேவை",
      toast_fail: "அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
      consent: "சமர்ப்பிப்பதன் மூலம், பூர்வோதய சோலார் உங்களைத் தொடர்பு கொள்ள சம்மதிக்கிறீர்கள்.",
    },
    whatsapp: { label: "எங்களுடன் உரையாடுங்கள்" },
    common: { language: "மொழி" },
    ts: {
        eyebrow: "ஏன் புர்வோதயா",
        title_pre: "ஒரு தீவிர பங்காளி —",
        title_hl: "இந்தியாவின் சூரிய மாற்றத்திற்காக",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "எனர்ஜி ட்ரான்ஸிஷன் பார்ட்னர்",
        p1_title: "இந்தியாவின் சூரிய சக்தி மாற்றத்தின் நம்பகமான பங்காளி",
        p1_desc: "நாட்டின் வீட்டு, ஹவுசிங் சொசைட்டி மற்றும் கார்ப்பரேட் சுத்த சக்தி பயணத்திற்காக சிறப்பாக கட்டமைக்கப்பட்டது — நீண்டகால செயல்திறனுக்காக, தற்காலிக காட்சிக்காக அல்ல.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM பைலர்",
        p2_title: "மத்திய சூரிய மானியங்களின் அங்கீகரிக்கப்பட்ட பைலர்",
        p2_desc: "நாங்கள் தனிநபர் வீடுகள் & ஹவுசிங் சொசைட்டிகளுக்கு PM Surya Ghar மற்றும் விவசாயிகளுக்கு PM-KUSUM தாக்கல் செய்கிறோம் — அனைத்து காகித வேலைகளும் எங்கள் குழுவே நிர்வகிக்கிறது.",
        p3_eyebrow: "முழுமையான EPC + 25-ஆண்டு O&M",
        p3_title: "ஒரு பொறுப்பான பங்காளி — வடிவமைப்பிலிருந்து இயக்கம் வரை மற்றும் 25 ஆண்டுகள் பராமரிப்பு",
        p3_desc: "MNRE-இணக்க வடிவமைப்பு, Tier-1 மாட்யூல்ஸ், சான்றளிக்கப்பட்ட நிறுவல் குழுக்கள், DISCOM தொடர்பு, மற்றும் 25-ஆண்டு செயல்திறன் உத்தரவாதம் — அனைத்தும் ஒரே கூரையின் கீழ்.",
        promise_label: "எங்கள் வாக்குறுதி",
        promise1: "Tier-1 மாட்யூல்ஸ் · 25 ஆண்டு செயல்பாட்டு உத்தரவாதம்",
        promise2: "MNRE-இணக்க இன்ஜினியரிங் & பாதுகாப்பு நடைமுறைகள்",
        promise3: "வெளிப்படையான மதிப்பீடு · மறைக்கப்பட்ட கட்டணம் இல்லை",
        promise4: "காலாண்டு செயல்திறன் & சுத்தம் செய்தல் அறிக்கைகள்",
      },
    res: {
        hero_badge: "வீட்டு ரூஃப்டாப் சோலார்",
        hero_title_a: "உங்கள் AC, ஃப்ரிட்ஜ், கீசர் —",
        hero_title_b: "சூரியனிலிருந்து இலவசமாக.",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "இலவச ரூஃப்டாப் கோட் பெறுக",
        cta_calc: "உங்கள் சேமிப்பை கணக்கிடுக",
        stat_subsidy: "அதிகபட்ச மத்திய மானியம்",
        stat_reduction: "பில் குறைப்பு",
        stat_payback: "சாதாரண பேபேக்",
        stat_warranty: "செயல்பாட்டு உத்தரவாதம்",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "முகப்புக்குத் திரும்பு",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "வணிக மற்றும் தொழில் சோலார்",
        hero_title_a: "Power your business with the",
        hero_title_hl: "சூரியனால்",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "வணிக முன்மொழிவை கோருக",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "பில் குறைப்பு",
        stat_payback: "சாதாரண பேபேக்",
        stat_warranty: "செயல்பாட்டு உத்தரவாதம்",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "முகப்புக்குத் திரும்பு",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "வீட்டுவசதி சங்க சோலார்",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "சங்க வருகையை கோருக",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "அதிகபட்ச மத்திய மானியம்",
        stat_reduction: "பில் குறைப்பு",
        stat_payback: "சாதாரண பேபேக்",
        stat_warranty: "செயல்பாட்டு உத்தரவாதம்",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "முகப்புக்குத் திரும்பு",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },

  te: {
    nav: {
      services: "సేవలు",
      how: "ఇది ఎలా పనిచేస్తుంది",
      subsidy: "సబ్సిడీ",
      savings: "ఆదా",
      process: "ప్రక్రియ",
      faq: "ప్రశ్నలు",
      admin: "అడ్మిన్",
      cta: "ఉచిత కోట్ పొందండి",
      menu: "మెనూ",
      residential: "నివాస",
      commercial: "వాణిజ్య",
      housing: "హౌసింగ్ సొసైటీలు",
    },
    hero: {
      badge: "MNRE నమోదిత · PM సూర్య ఘర్ సిద్ధం",
      title_pre: "మీ ఇంటిని",
      title_highlight: "సూర్యకాంతి",
      title_post1: "తో నడపండి, మీ",
      title_post2: "విద్యుత్ బిల్లును తగ్గించండి.",
      subtitle_pre:
        "పూర్వోదయ సోలార్ భారతదేశం అంతటా ఇళ్ళు, పొలాల కోసం రూఫ్‌టాప్ సౌర వ్యవస్థలను డిజైన్, ఇన్‌స్టాల్ చేస్తుంది —",
      subtitle_and: "మరియు",
      subtitle_post: "సబ్సిడీలకు సంపూర్ణ మద్దతుతో.",
      cta_quote: "ఉచిత రూఫ్‌టాప్ కోట్ పొందండి",
      cta_calc: "మీ ఆదా లెక్కించండి",
      stat1_k: "₹78,000",
      stat1_v: "గరిష్ట సబ్సిడీ (నివాస)",
      stat2_k: "25 సం.",
      stat2_v: "ప్యానెల్ పనితీరు",
      stat3_k: "4–5 సం.",
      stat3_v: "సాధారణ తిరిగి చెల్లింపు",
      live_label: "సగటు మాసిక ఆదా",
      live_value: "₹4,120",
      live_caption: "సాధారణ 3 kW ఇంటికి PM సూర్య ఘర్ సబ్సిడీ తర్వాత",
      this_month: "ఈ సంవత్సరం",
      saved: "సగటు గృహ ఆదా",
      co2: "t CO₂ వార్షిక తగ్గింపు",
    },
    trust: { heading: "టైర్-1 ప్యానెల్ & ఇన్వర్టర్ బ్రాండ్‌లతో పని" },
    services: {
            cta_explore: "ప్రయోజనాలను అన్వేషించండి",
      cta_street_b2b: "B2B ప్రాజెక్టుల కోసం మాట్లాడండి →",
eyebrow: "సంపూర్ణ సోలార్ సొల్యూషన్స్",
      title_pre: "ఒక భాగస్వామి.",
      title_hl: "ప్రతి సోలార్ అవసరం",
      title_post: "కవర్ చేయబడింది.",
      subtitle: "పైకప్పుల నుంచి కర్మాగారాల వరకు, EV చార్జర్ల నుంచి వీధి దీపాల వరకు — పూర్వోదయ సోలార్ మీ స్థలం, బడ్జెట్‌కు అనుగుణంగా పూర్తి క్లీన్-ఎనర్జీ సొల్యూషన్స్ అందిస్తుంది.",
      cta: "ఈ సేవను అన్వేషించండి",
      items: [
        { key: "rooftop", title: "నివాస రూఫ్‌టాప్ సోలార్", desc: "ఇళ్ళ కోసం MNRE-నమోదిత రూఫ్‌టాప్ ఇన్‌స్టాలేషన్‌లు — PM సూర్య ఘర్ యోజన కింద ₹78,000 వరకు కేంద్ర సబ్సిడీ.", highlight: "PM సూర్య ఘర్ యోజన" },
        { key: "commercial", title: "వాణిజ్య & పారిశ్రామిక సోలార్", desc: "కర్మాగారాలు, కార్యాలయాలు, గోదాముల కోసం MW స్థాయి రూఫ్‌టాప్ & గ్రౌండ్-మౌంట్ సిస్టమ్‌లు. విద్యుత్ ఖర్చు 60% తగ్గించి 40% యాక్సిలరేటెడ్ డిప్రెసియేషన్ పొందండి.", highlight: "40% వరకు పన్ను ఆదా" },
        { key: "ev", title: "EV చార్జింగ్ సొల్యూషన్స్", desc: "ఇళ్ళు, కార్యాలయాలు, మాల్స్ & హైవే ప్లాజాల కోసం సోలార్-ఆధారిత AC & DC ఫాస్ట్ EV చార్జర్లు — రూఫ్‌టాప్ సోలార్‌తో జీరో-కాస్ట్ డ్రైవింగ్.", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "బ్యాటరీ ఎనర్జీ స్టోరేజ్", desc: "బ్యాకప్ పవర్, పీక్ షేవింగ్, పూర్తి ఆఫ్-గ్రిడ్ స్వాతంత్ర్యానికి లిథియం-అయాన్ BESS. 5 kWh గృహ నుంచి 1 MWh+ వాణిజ్య బ్యాంక్ వరకు.", highlight: "10 సం. వారంటీ" },
        { key: "street", title: "సోలార్ స్ట్రీట్ లైటింగ్", desc: "సంఘాలు, క్యాంపస్‌లు, హైవేలు & గ్రామీణ రోడ్ల కోసం స్టాండ్-అలోన్ ఆల్-ఇన్-వన్ సోలార్ స్ట్రీట్ లైట్లు — జీరో వైరింగ్, జీరో విద్యుత్ బిల్లు.", highlight: "ఆటో డస్క్-టు-డాన్" },
        { key: "society", title: "హౌసింగ్ సొసైటీ సోలార్", desc: "అపార్ట్‌మెంట్ కాంప్లెక్స్‌ల కోసం గ్రూప్ రూఫ్‌టాప్ & కామన్-ఏరియా సోలార్ — వ్యక్తిగత ఫ్లాట్‌లకు PM సూర్య ఘర్ ప్రయోజనాలు + సొసైటీ ఆదా.", highlight: "RWA-ఫ్రెండ్లీ" },
        { key: "heater", title: "సోలార్ వాటర్ హీటర్", desc: "ఇళ్ళు, హాస్టళ్లు, హోటళ్ల కోసం ఎవాక్యుయేటెడ్ గ్లాస్-ట్యూబ్ సోలార్ వాటర్ హీటర్లు — 365 రోజులు వేడి నీరు, గీజర్ బిల్లు పూర్తిగా ఆపండి.", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "రూఫ్‌టాప్ సౌర ఎలా పనిచేస్తుంది",
      title_pre: "",
      title_hl: "సూర్యకాంతి నుండి",
      title_mid: "ఆదా వరకు — నాలుగు సులువైన దశల్లో.",
      subtitle: "రూఫ్‌టాప్ సౌర మాయ కాదు, స్మార్ట్ మీటరింగ్‌తో సులువైన భౌతికశాస్త్రం. ప్రతి రోజు ఇది స్వయంచాలకంగా జరుగుతుంది:",
      steps: [
        { title: "సూర్యకాంతి ప్యానెల్‌లపై పడుతుంది", desc: "అధిక సామర్థ్యం కలిగిన మోనో PERC ప్యానెల్‌లు పగటి వెలుతురును గ్రహించి DC విద్యుత్తును తయారు చేస్తాయి." },
        { title: "ఇన్వర్టర్ AC గా మారుస్తుంది", desc: "96% సామర్థ్యంతో DCని ACగా మార్చే స్మార్ట్ గ్రిడ్-టైడ్ ఇన్వర్టర్." },
        { title: "మీ ఇల్లు ముందుగా దానిని ఉపయోగిస్తుంది", desc: "ఫ్యాన్‌లు, లైట్లు, AC, పంపులు — పగలు సూర్యుడి నుండి, గ్రిడ్ వాడకం గణనీయంగా తగ్గుతుంది." },
        { title: "నెట్ మీటర్ మిగిలినవి నిల్వ చేస్తుంది", desc: "అధిక యూనిట్లు గ్రిడ్‌కి తిరిగి వెళ్తాయి. నెల చివరిలో నికర వినియోగానికి మాత్రమే బిల్లు." },
      ],
      kpis: [
        { k: "70–90%", v: "ఇన్‌స్టాలేషన్ తర్వాత నెలవారీ విద్యుత్ బిల్లులో తగ్గింపు." },
        { k: "₹0 EMI", v: "భాగస్వామి బ్యాంకుల ద్వారా సౌకర్యవంతమైన ఆర్థిక సహాయం." },
        { k: "25 సం.", v: "ప్యానెల్ అవుట్‌పుట్ వారంటీ (కనీసం 80%)." },
      ],
    },
    subsidy: {
      eyebrow: "ప్రభుత్వ సబ్సిడీలు",
      title_pre: "పొందండి",
      title_hl: "₹78,000",
      title_post: " వరకు తగ్గింపు — పేపర్‌వర్క్ మేము చూస్తాం.",
      subtitle: "ఇల్లు అయినా పొలం అయినా, భారత ప్రభుత్వం సౌర స్వీకరణకు ఉదారమైన సబ్సిడీలు ఇస్తుంది. పూర్వోదయ ప్రతి దరఖాస్తును ప్రారంభం నుంచి నిధులు వచ్చే వరకు ట్రాక్ చేస్తుంది.",
      tab_res: "నివాస · PM సూర్య ఘర్",
      tab_agri: "వ్యవసాయ · PM-KUSUM",
      res_title: "PM సూర్య ఘర్: ఉచిత విద్యుత్ యోజన",
      res_desc: "భారత ప్రభుత్వం ప్రారంభించిన ఈ పథకం, మీ ఇంటిపై రూఫ్‌టాప్ సౌర ఇన్‌స్టాల్ అయ్యాక సబ్సిడీని నేరుగా మీ బ్యాంకు ఖాతాకు అందిస్తుంది.",
      slab_labels: ["1 kW వరకు", "2 kW వరకు", "3 kW & ఎక్కువ"],
      slab_notes: ["₹30,000 ప్రతి kW", "₹30,000 ప్రతి kW", "గరిష్ట పరిమితి"],
      res_bullets: [
        "నివాస ఇళ్లపై రూఫ్‌టాప్ PV వ్యవస్థలకు వర్తిస్తుంది.",
        "అర్హత ఉన్న కుటుంబాలకు నెలకు 300 యూనిట్ల ఉచిత విద్యుత్.",
        "కమిషనింగ్ & తనిఖీ తర్వాత నేరుగా బ్యాంకు బదిలీ.",
        "మా బృందం అధికారిక జాతీయ పోర్టల్‌లో దరఖాస్తు చేస్తుంది.",
      ],
      read_official: "అధికారిక పథకం చదవండి",
      savings_label: "మీ ఆదా",
      savings_amount: "₹78,000",
      savings_note: "PM సూర్య ఘర్ కింద 3 kW+ నివాస రూఫ్‌టాప్‌పై గరిష్ట కేంద్ర సబ్సిడీ. రాష్ట్ర ప్రోత్సాహకాలు అదనంగా ఉండవచ్చు.",
      example_label: "ఉదాహరణ · 3 kW వ్యవస్థ",
      example_cost: "వ్యవస్థ ఖర్చు",
      example_subsidy: "సబ్సిడీ",
      example_pay: "మీరు చెల్లించండి",
      agri_title: "PM-KUSUM యోజన",
      agri_desc: "రైతులకు సౌర పంపుల ఇన్‌స్టాలేషన్ మరియు వ్యవసాయ ఫీడర్‌ల సౌరీకరణలో సహాయం చేస్తుంది — డీజిల్ ఆధారపడటాన్ని తగ్గించి నమ్మదగిన పగటి విద్యుత్ అందిస్తుంది.",
      agri_components: [
        { k: "కాం-A", v: "బంజరు భూమిపై గ్రిడ్-కనెక్ట్ సౌర ప్లాంట్‌లు" },
        { k: "కాం-B", v: "స్టాండ్-అలోన్ సౌర వాటర్ పంపులు" },
        { k: "కాం-C", v: "ఉన్న గ్రిడ్ పంపుల సౌరీకరణ" },
      ],
      agri_bullets: [
        "సౌర పంపులపై 60% వరకు కేంద్ర + రాష్ట్ర సబ్సిడీ.",
        "30% బ్యాంకు రుణం; రైతు వాటా కేవలం ~10%.",
        "అదనపు ఆదాయానికి మిగిలిన విద్యుత్‌ను DISCOMకు అమ్మండి.",
        "ఇన్‌స్టాలేషన్ & DBT సహాయం పూర్వోదయ ద్వారా.",
      ],
      farmer_benefit: "రైతు ప్రయోజనం",
      farmer_amount: "60% వరకు తగ్గింపు",
      farmer_note: "సౌర పంపు ఇన్‌స్టాలేషన్‌పై, అదనంగా మిగిలిన విద్యుత్‌ను గ్రిడ్‌కు అమ్మడం ద్వారా ఆదాయం.",
      sub_individual: "వ్యక్తిగత గృహాలు",
      sub_society: "గ్రూప్ హౌసింగ్ సొసైటీలు",
      ind_box_label: "మీ మొత్తం పొదుపు",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar కింద 3 kW+ నివాస రూఫ్‌టాప్ సిస్టమ్‌పై గరిష్ట కేంద్ర సబ్సిడీ. రాష్ట్ర ప్రోత్సాహకాలు అదనంగా ఉండవచ్చు.",
      ind_example_title: "ఉదాహరణ · 3 kW సిస్టమ్",
      ind_example_rows: [
        { label: "సిస్టమ్ ఖర్చు", value: "₹1,80,000" },
        { label: "సబ్సిడీ", value: "₹78,000" },
      ],
      ind_example_net_label: "మీరు చెల్లించేది",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "మీకు అదనంగా లభించేవి",
      ind_extras: [
        "అర్హత ఉన్న కుటుంబాలకు నెలకు 300 యూనిట్లు ఉచితం",
        "అదనపు ఉత్పత్తికి నెట్-మీటరింగ్ క్రెడిట్",
        "25-సంవత్సర ప్యానెల్ పనితీరు వారెంటీ",
        "Purvodaya ద్వారా ఉచిత PM Surya Ghar పోర్టల్ ఫైలింగ్",
      ],
      ghs_title: "గ్రూప్ హౌసింగ్ సొసైటీలు (GHS / RWAs)",
      ghs_intro: "PMSGY కింద కేంద్ర ప్రభుత్వం లిఫ్ట్‌లు, నీటి పంపులు, స్ట్రీట్‌లైట్లు, లాబీ మరియు కారిడార్ లైటింగ్ వంటి కామన్ సదుపాయాలకు గ్రిడ్-కనెక్టెడ్ రూఫ్‌టాప్ సోలార్‌కు ₹18,000 / kW ఇస్తుంది.",
      ghs_structure_label: "సబ్సిడీ నిర్మాణం",
      ghs_structure: [
        "ఏర్పాటు చేసిన కామన్-ఏరియా సోలార్ సామర్థ్యంలో ₹18,000 / kW.",
        "ఒక సొసైటీకి గరిష్టంగా 500 kW — అంటే ₹90 లక్షల వరకు సబ్సిడీ.",
        "అనుమతించబడిన కామన్ సామర్థ్యం = 3 kW × ఫ్లాట్ల సంఖ్య.",
      ],
      ghs_eligibility_label: "అర్హత చెక్‌లిస్ట్",
      ghs_eligibility: [
        "CHS / RWA దాని PAN తో చట్టబద్ధంగా నమోదు చేయబడాలి.",
        "సొసైటీ పేరిట క్రియాశీల కామన్-సర్వీస్ విద్యుత్ మీటర్.",
        "నేరుగా సబ్సిడీ బదిలీకి ప్రత్యేక సొసైటీ బ్యాంక్ ఖాతా.",
        "మేక్-ఇన్-ఇండియా (DCR-అనుగుణ) ప్యానెల్స్ మాత్రమే.",
      ],
      ghs_box_label: "సొసైటీ-వ్యాప్త పొదుపు",
      ghs_box_amount: "₹90 లక్షల వరకు",
      ghs_box_caption: "ఒక హౌసింగ్ సొసైటీకి గరిష్ట PMSGY సబ్సిడీ — ₹18,000 / kW రేటుతో, 500 kW వద్ద గరిష్టంగా.",
      ghs_example_title: "ఉదాహరణ · 100-ఫ్లాట్ సొసైటీ",
      ghs_example_rows: [
        { label: "అనుమతించబడిన సామర్థ్యం (3 kW × 100)", value: "300 kW" },
        { label: "ప్రాజెక్ట్ ఖర్చు (₹50,000 / kW)", value: "₹1.50 కోట్లు" },
        { label: "PMSGY సబ్సిడీ", value: "₹54 లక్షలు" },
        { label: "వార్షిక బిల్ పొదుపు", value: "~₹22 లక్షలు" },
      ],
      ghs_example_net_label: "సొసైటీ నికర ఖర్చు",
      ghs_example_net_value: "₹96 లక్షలు",
      ghs_example_payback: "4–5 సంవత్సరాలలో పేబ్యాక్ · 20+ సంవత్సరాలు ఉచిత విద్యుత్",
      ghs_documents_label: "సొసైటీ నుండి అవసరమైన పత్రాలు",
      ghs_documents: [
        "సొసైటీ నమోదు సర్టిఫికేట్ + PAN",
        "గత 3 నెలల కామన్-ఏరియా విద్యుత్ బిల్లు",
        "సొసైటీ బ్యాంక్ పాస్‌బుక్",
        "ప్రాజెక్ట్‌ను ఆమోదించే AGM/EGM తీర్మానం",
        "భవన ప్రణాళిక & రూఫ్‌టాప్ లేఅవుట్",
      ],
    },
    calc: {
      eyebrow: "ఆదా కాలిక్యులేటర్",
      title_pre: "మీరు ఎంత",
      title_hl: "ఆదా చేస్తారో",
      title_post: " చూడండి — మొదలు పెట్టడానికి + మరియు − నొక్కండి.",
      subtitle: "మీ ఉపకరణాలను ఎంచుకోండి, గంటలు సర్దుబాటు చేయండి — మేము సరైన పరిమాణ సౌర వ్యవస్థను, ప్రత్యక్ష సబ్సిడీ & తిరిగి చెల్లింపు అంచనాలతో సిఫార్సు చేస్తాము.",
      tab_res: "నివాస",
      tab_agri: "వ్యవసాయ",
      appliances: "మీ ఉపకరణాలు",
      pincode_placeholder: "పిన్‌కోడ్",
      tariff: "టారిఫ్ ₹{rate}/యూనిట్",
      detected: "గుర్తించబడింది",
      default_hours: "డిఫాల్ట్ {h}గం/రోజు",
      hours_day: "గంటలు/రోజు",
      snapshot: "మీ సౌర స్నాప్‌షాట్",
      daily: "రోజువారీ",
      monthly_units: "నెలవారీ యూనిట్లు",
      current_bill: "ప్రస్తుత బిల్లు",
      recommended: "సిఫార్సు",
      investment: "పెట్టుబడి వివరాలు",
      system_cost: "వ్యవస్థ ఖర్చు",
      pm_subsidy: "PM సూర్య ఘర్ సబ్సిడీ",
      kusum_subsidy: "PM-KUSUM సబ్సిడీ (~30%)",
      net_investment: "నికర పెట్టుబడి",
      monthly_savings: "నెలవారీ ఆదా",
      payback: "తిరిగి చెల్లింపు",
      co2_yr: "CO₂/సం.",
      cta_quote: "నా {kw} kW కోట్",
      disclaimer: "అంచనాలు 4 సూర్య-గంటలు/రోజు మరియు ₹{rate}/యూనిట్ టారిఫ్ ఆధారంగా. తుది డిజైన్ ఉచిత సైట్ సందర్శన తర్వాత.",
      appliance_labels: {
        fan: "సీలింగ్ ఫ్యాన్",
        tube: "ట్యూబ్ / LED లైట్",
        ac: "ఎయిర్ కండిషనర్ (1.5T)",
        fridge: "రెఫ్రిజిరేటర్",
        tv: "టెలివిజన్",
        geyser: "గీజర్",
        washer: "వాషింగ్ మెషీన్",
        microwave: "మైక్రోవేవ్",
        computer: "కంప్యూటర్ / ల్యాప్‌టాప్",
        pump_home: "వాటర్ పంప్ (1 HP)",
        pump_3hp: "వాటర్ పంప్ (3 HP)",
        pump_5hp: "వాటర్ పంప్ (5 HP)",
        pump_75hp: "వాటర్ పంప్ (7.5 HP)",
        pump_10hp: "సబ్‌మర్సిబుల్ (10 HP)",
        lights: "వ్యవసాయ లైటింగ్",
        chaffcutter: "చాఫ్ కట్టర్",
      },
    },
    workflow: {
      eyebrow: "మా ప్రక్రియ",
      title_pre: "ఆరు దశలు.",
      title_hl: "సున్నా",
      title_post: "తలనొప్పి.",
      subtitle: "మొదటి కాల్ నుంచి 25 సం. ఆదా వరకు, మొత్తం ప్రయాణాన్ని పూర్వోదయ నిర్వహిస్తుంది. ఇలా జరుగుతుంది:",
      activities: "కార్యకలాపాలు",
      deliverables: "డెలివరీలు",
      outcome: "ఫలితం",
      steps: [
        { title: "సైట్ సందర్శన & సాధ్యత", activities: "నీడ విశ్లేషణ, పైకప్పు తనిఖీ, లోడ్ అధ్యయనం, విద్యుత్ బిల్లు సమీక్ష.", deliverables: "వ్యవస్థ పరిమాణం & ఉత్పత్తి అంచనాతో సాధ్యత నివేదిక.", outcome: "మీ పైకప్పు సౌరానికి సిద్ధంగా ఉందన్న నమ్మకం." },
        { title: "డిజైన్ & ప్రతిపాదన", activities: "3D రూఫ్‌టాప్ లేఅవుట్, నిర్మాణ + విద్యుత్ SLD, ప్యానెల్ & ఇన్వర్టర్ ఎంపిక.", deliverables: "BOM, టైమ్‌లైన్ & సబ్సిడీ అంచనాతో వివరణాత్మక ప్రతిపాదన.", outcome: "పారదర్శక ధర, ఆశ్చర్యాలు లేవు." },
        { title: "అనుమతులు & సబ్సిడీ దరఖాస్తు", activities: "నెట్-మీటర్ దరఖాస్తు, DISCOM అనుసంధానం, PM సూర్య ఘర్ / KUSUM ఫైలింగ్.", deliverables: "నెట్ మీటరింగ్ అనుమతి + సబ్సిడీ నమోదు.", outcome: "కాగితాలు పూర్తిగా నిర్వహించబడ్డాయి." },
        { title: "ఇన్‌స్టాలేషన్", activities: "నిర్మాణ మౌంటింగ్, ప్యానెల్ అమరిక, కేబుల్, ఇన్వర్టర్ కమిషనింగ్.", deliverables: "పూర్తిగా ఇన్‌స్టాల్ చేయబడి, లేబుల్ & పరీక్షించబడిన రూఫ్‌టాప్ వ్యవస్థ.", outcome: "సాధారణంగా 5–7 రోజుల్లో విద్యుత్." },
        { title: "కమిషనింగ్ & గో-లైవ్", activities: "DISCOM తనిఖీ, నెట్ మీటర్ ఇన్‌స్టాలేషన్, యాప్ ఆన్‌బోర్డింగ్.", deliverables: "హ్యాండోవర్ ప్యాక్ + మానిటరింగ్ యాప్ లాగిన్.", outcome: "మీ మీటర్ వెనుకకు తిరగడం ప్రారంభిస్తుంది." },
        { title: "విక్రయానంతర & పర్యవేక్షణ", activities: "రిమోట్ పనితీరు పర్యవేక్షణ, కాలానుగుణ శుభ్రపరచడం, AMC మద్దతు.", deliverables: "వార్షిక పనితీరు నివేదికలు, ప్రాధాన్యత హెల్ప్‌లైన్.", outcome: "25 సం. చింతలేని సూర్యకాంతి." },
      ],
    },
    faq: {
      eyebrow: "తరచుగా అడిగే ప్రశ్నలు",
      title_pre: "అందరి ప్రశ్నలు —",
      title_hl: "నిజమైన సమాధానాలు.",
      items: [
        { q: "సౌర వ్యవస్థకు ఎంత పైకప్పు స్థలం అవసరం?", a: "సాధారణ నియమం: ప్రతి 1 kW కి ~80–100 చ.అ. నీడ లేని ప్రాంతం. 3 kW గృహ వ్యవస్థ 300 చ.అ. పైకప్పులో సులభంగా వస్తుంది." },
        { q: "వర్షాకాలం లేదా మేఘావృత రోజుల్లో సౌర పని చేస్తుందా?", a: "అవును. ప్యానెల్‌లు పంపిణీ చేయబడిన కాంతిలో కూడా (పీక్‌లో ~10–25%) ఉత్పత్తి చేస్తాయి. నెట్-మీటరింగ్ ద్వారా గ్రిడ్ నుంచి తీసుకోవచ్చు." },
        { q: "PM సూర్య ఘర్ సబ్సిడీ ప్రక్రియ ఏమిటి?", a: "మీరు జాతీయ పోర్టల్‌లో నమోదు చేస్తారు, మేము దరఖాస్తు చేస్తాము, MNRE-నమోదిత వెండర్ (పూర్వోదయ) ద్వారా ఇన్‌స్టాల్, DISCOM తనిఖీ తర్వాత నేరుగా బ్యాంకు బదిలీ." },
        { q: "ఇన్‌స్టాలేషన్‌కు ఎంత సమయం పడుతుంది?", a: "ప్రామాణిక 3 kW నివాస ఇన్‌స్టాలేషన్ 3–5 రోజులు. అనుమతులతో సహా మొత్తం 4–6 వారాలు." },
        { q: "సౌర వ్యవస్థకు ఏ మెయింటెనెన్స్ అవసరం?", a: "చాలా తక్కువ. ధూళి ప్రాంతాల్లో 1–2 నెలలకు శుభ్రపరచడం & వార్షిక తనిఖీ. ప్రతి ఇన్‌స్టాలేషన్‌తో ఉచిత 1-సం. AMC." },
        { q: "రైతులు నిజంగా PM-KUSUM నుంచి ఆదాయం పొందగలరా?", a: "ఖచ్చితంగా. కాం-A & C కింద మిగిలిన సౌర విద్యుత్‌ను DISCOM స్థిర టారిఫ్‌పై కొనుగోలు చేస్తుంది — పంపులకు ఉచిత పగటి విద్యుత్‌తో పాటు స్థిర అదనపు ఆదాయం." },
      ],
    },
    footer: {
      tag: "భవిష్యత్‌ను ప్రకాశవంతం చేయండి",
      desc: "భారతీయ ఇళ్ళు, పొలాలు బిల్లులను తగ్గించడంలో, ఇంధన పరివర్తనను వేగవంతం చేయడంలో సహాయపడే MNRE-నమోదిత రూఫ్‌టాప్ సౌర EPC కంపెనీ.",
      explore: "అన్వేషించండి",
      contact: "సంప్రదింపు",
      rights: "© {year} పూర్వోదయ సోలార్. అన్ని హక్కులు రిజర్వ్.",
      built: "స్వచ్ఛ భారత్ కోసం ☀ తో నిర్మించబడింది.",
    },
    lead: {
      badge: "ఉచితం & ఎటువంటి బాధ్యత లేదు",
      title_default: "ఉచిత రూఫ్‌టాప్ కోట్ పొందండి",
      subtitle_default: "మీ వివరాలు పంచుకోండి — ఒక పూర్వోదయ నిపుణుడు 24 గంటల్లో కాల్ చేస్తాడు.",
      title_calc: "మీ వ్యక్తిగత కోట్ పొందండి",
      title_idle: "ఇంకా చూస్తున్నారా? మేము సహాయం చేస్తాము.",
      subtitle_idle: "మీ వివరాలు పంచుకోండి — మీ ఫోన్‌కు అనుకూలీకృత సౌర ఆదాను పంపుతాము.",
      full_name: "పూర్తి పేరు *",
      name_ph: "మీ పేరు",
      phone: "ఫోన్ *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "పిన్‌కోడ్",
      email: "ఇమెయిల్",
      email_ph: "you@example.com",
      i_need: "నాకు సౌర కావాలి",
      seg_home: "ఇల్లు / నివాస",
      seg_farm: "పొలం / వ్యవసాయ",
      seg_commercial: "వాణిజ్య / పారిశ్రామిక",
      anything_else: "మరేదైనా?",
      msg_ph: "పైకప్పు పరిమాణం, ప్రస్తుత బిల్లు, మొదలైనవి.",
      submit: "నాకు ఉచిత కాల్-బ్యాక్ కావాలి",
      submitting: "పంపుతోంది…",
      thanks_title: "మీరు మా జాబితాలో ఉన్నారు!",
      thanks_desc: "ఒక సౌర సలహాదారు త్వరలో మిమ్మల్ని సంప్రదిస్తారు.",
      toast_ok: "ధన్యవాదాలు! త్వరలో కాల్ చేస్తాము.",
      toast_required: "పేరు & ఫోన్ అవసరం",
      toast_fail: "పంపలేకపోయాము. దయచేసి మళ్ళీ ప్రయత్నించండి.",
      consent: "సమర్పించడం ద్వారా, మీరు పూర్వోదయ సోలార్‌తో సంప్రదించబడటానికి అంగీకరిస్తున్నారు.",
    },
    whatsapp: { label: "మాతో చాట్ చేయండి" },
    common: { language: "భాష" },
    ts: {
        eyebrow: "పుర్వోదయ ఎందుకు",
        title_pre: "ఒక గంభీరమైన భాగస్వామి —",
        title_hl: "భారతదేశం సౌర మార్పిడి కోసం",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "ఎనర్జీ ట్రాన్సిషన్ పార్ట్‌నర్",
        p1_title: "భారతదేశం సౌర-ఆధారిత ఇంధన మార్పిడిలో నమ్మకమైన భాగస్వామి",
        p1_desc: "దేశం యొక్క నివాస, హౌసింగ్ సొసైటీ మరియు కార్పొరేట్ స్వచ్ఛ ఇంధన ప్రయాణం కోసం ప్రత్యేకంగా రూపొందించబడింది — దీర్ఘకాలిక పనితీరు కోసం, తాత్కాలిక ప్రదర్శన కోసం కాదు.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM ఫైలర్",
        p2_title: "కేంద్ర సౌర సబ్సిడీలకు అధికారిక ఫైలర్",
        p2_desc: "మేము వ్యక్తిగత గృహాలు & హౌసింగ్ సొసైటీలకు PM Surya Ghar మరియు రైతులకు PM-KUSUM ఫైల్ చేస్తాము — అన్ని పేపర్‌వర్క్‌ను మా బృందం నిర్వహిస్తుంది, మధ్యవర్తులు లేరు.",
        p3_eyebrow: "ఎండ్-టు-ఎండ్ EPC + 25-సంవత్సర O&M",
        p3_title: "ఒక బాధ్యతగల భాగస్వామి — డిజైన్ నుండి ఆన్ చేయడం వరకు మరియు 25 సంవత్సరాల సంరక్షణ",
        p3_desc: "MNRE-అనుగుణ డిజైన్, Tier-1 మాడ్యూల్స్, ధృవీకరించబడిన సంస్థాపన బృందాలు, DISCOM లైజనింగ్, మరియు 25-సంవత్సర పనితీరు హామీ — ఒకే గొడుగు కింద.",
        promise_label: "మా వాగ్దానం",
        promise1: "Tier-1 మాడ్యూల్స్ · 25-సంవత్సర పనితీరు వారెంటీ",
        promise2: "MNRE-అనుగుణ ఇంజినీరింగ్ & భద్రతా పద్ధతులు",
        promise3: "పారదర్శక కోట్‌లు · దాగిన ఖర్చు లేదు",
        promise4: "త్రైమాసిక పనితీరు & శుభ్రపరిచే నివేదికలు",
      },
    res: {
        hero_badge: "నివాస రూఫ్‌టాప్ సోలార్",
        hero_title_a: "మీ AC, ఫ్రిజ్, గీజర్ —",
        hero_title_b: "సూర్యునితో ఉచితంగా.",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "ఉచిత రూఫ్‌టాప్ కోట్ పొందండి",
        cta_calc: "మీ పొదుపును అంచనా వేయండి",
        stat_subsidy: "గరిష్ట కేంద్ర సబ్సిడీ",
        stat_reduction: "బిల్లు తగ్గింపు",
        stat_payback: "సాధారణ పేబ్యాక్",
        stat_warranty: "పనితీరు వారెంటీ",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "హోమ్‌కి తిరిగి",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "వాణిజ్య & పారిశ్రామిక సోలార్",
        hero_title_a: "Power your business with the",
        hero_title_hl: "సూర్యుని",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "వ్యాపార ప్రతిపాదనను అభ్యర్థించండి",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "బిల్లు తగ్గింపు",
        stat_payback: "సాధారణ పేబ్యాక్",
        stat_warranty: "పనితీరు వారెంటీ",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "హోమ్‌కి తిరిగి",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "హౌసింగ్ సొసైటీ సోలార్",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "సొసైటీ సందర్శనను అభ్యర్థించండి",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "గరిష్ట కేంద్ర సబ్సిడీ",
        stat_reduction: "బిల్లు తగ్గింపు",
        stat_payback: "సాధారణ పేబ్యాక్",
        stat_warranty: "పనితీరు వారెంటీ",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "హోమ్‌కి తిరిగి",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },

  bn: {
    nav: {
      services: "পরিষেবাসমূহ",
      how: "এটি কীভাবে কাজ করে",
      subsidy: "সাবসিডি",
      savings: "সঞ্চয়",
      process: "প্রক্রিয়া",
      faq: "প্রশ্নোত্তর",
      admin: "অ্যাডমিন",
      cta: "বিনামূল্যে কোট নিন",
      menu: "মেনু",
      residential: "আবাসিক",
      commercial: "বাণিজ্যিক",
      housing: "হাউসিং সোসাইটি",
    },
    hero: {
      badge: "MNRE তালিকাভুক্ত · PM সূর্য ঘর প্রস্তুত",
      title_pre: "আপনার বাড়িকে",
      title_highlight: "সূর্যের আলো",
      title_post1: "দিয়ে চালান এবং",
      title_post2: "বিদ্যুৎ বিল কমান।",
      subtitle_pre:
        "পূর্বোদয় সোলার সারা ভারতের বাড়ি ও খামারের জন্য ছাদের সোলার সিস্টেম ডিজাইন, ইনস্টল এবং সার্ভিস করে —",
      subtitle_and: "এবং",
      subtitle_post: "সাবসিডির জন্য পূর্ণ সহায়তা সহ।",
      cta_quote: "বিনামূল্যে ছাদের কোট নিন",
      cta_calc: "আপনার সঞ্চয় হিসাব করুন",
      stat1_k: "₹78,000",
      stat1_v: "সর্বোচ্চ সাবসিডি (আবাসিক)",
      stat2_k: "25 বছর",
      stat2_v: "প্যানেল পারফরম্যান্স",
      stat3_k: "4–5 বছর",
      stat3_v: "সাধারণ ফেরত সময়",
      live_label: "গড় মাসিক সঞ্চয়",
      live_value: "₹4,120",
      live_caption: "সাধারণ 3 kW বাড়ির জন্য PM সূর্য ঘর সাবসিডির পরে",
      this_month: "এই বছরে",
      saved: "গড় পারিবারিক সঞ্চয়",
      co2: "t CO₂ বার্ষিক হ্রাস",
    },
    trust: { heading: "টায়ার-১ প্যানেল ও ইনভার্টার ব্র্যান্ডের সাথে কাজ" },
    services: {
            cta_explore: "সুবিধা জানুন",
      cta_street_b2b: "B2B মোতায়েনের জন্য কথা বলুন →",
eyebrow: "সম্পূর্ণ সোলার সমাধান",
      title_pre: "একটি অংশীদার.",
      title_hl: "প্রতিটি সোলার প্রয়োজন",
      title_post: "কভার.",
      subtitle: "ছাদ থেকে কারখানা, EV চার্জার থেকে স্ট্রিট লাইট — পূর্বোদয় সোলার আপনার জায়গা ও বাজেট অনুযায়ী সম্পূর্ণ ক্লিন-এনার্জি সমাধান প্রদান করে।",
      cta: "এই পরিষেবা দেখুন",
      items: [
        { key: "rooftop", title: "আবাসিক ছাদের সোলার", desc: "বাড়ির জন্য MNRE-তালিকাভুক্ত ছাদের ইনস্টলেশন — PM সূর্য ঘর যোজনার অধীনে ₹78,000 পর্যন্ত কেন্দ্রীয় সাবসিডি।", highlight: "PM সূর্য ঘর যোজনা" },
        { key: "commercial", title: "বাণিজ্যিক ও শিল্প সোলার", desc: "কারখানা, অফিস, গুদামের জন্য MW স্কেলের ছাদ ও গ্রাউন্ড-মাউন্ট সিস্টেম। বিদ্যুৎ খরচ 60% কমান এবং 40% ত্বরিত অবচয় সুবিধা নিন।", highlight: "৪০% পর্যন্ত কর সাশ্রয়" },
        { key: "ev", title: "EV চার্জিং সমাধান", desc: "বাড়ি, অফিস, মল ও হাইওয়ে প্লাজার জন্য সোলার-চালিত AC ও DC ফাস্ট EV চার্জার — ছাদের সোলারের সাথে শূন্য-খরচে ড্রাইভিং।", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "ব্যাটারি এনার্জি স্টোরেজ", desc: "ব্যাকআপ পাওয়ার, পিক শেভিং, পূর্ণ অফ-গ্রিড স্বাধীনতার জন্য লিথিয়াম-আয়ন BESS। 5 kWh গৃহ থেকে 1 MWh+ বাণিজ্যিক ব্যাঙ্ক পর্যন্ত।", highlight: "১০ বছরের ওয়ারেন্টি" },
        { key: "street", title: "সোলার স্ট্রিট লাইটিং", desc: "সমিতি, ক্যাম্পাস, হাইওয়ে ও গ্রামীণ রাস্তার জন্য স্ট্যান্ড-অ্যালোন অল-ইন-ওয়ান সোলার স্ট্রিট লাইট — শূন্য তার, শূন্য বিল।", highlight: "অটো ডাস্ক-টু-ডন" },
        { key: "society", title: "হাউজিং সোসাইটি সোলার", desc: "অ্যাপার্টমেন্ট কমপ্লেক্সের জন্য গ্রুপ ছাদ ও কমন-এরিয়া সোলার — ব্যক্তিগত ফ্ল্যাটে PM সূর্য ঘর সুবিধা সহ সমিতির সঞ্চয়।", highlight: "RWA-ফ্রেন্ডলি" },
        { key: "heater", title: "সোলার ওয়াটার হিটার", desc: "বাড়ি, হোস্টেল ও হোটেলের জন্য ইভ্যাকুয়েটেড গ্লাস-টিউব সোলার ওয়াটার হিটার — ৩৬৫ দিন গরম জল, গিজার বিল সম্পূর্ণ বন্ধ।", highlight: "১০০–৫০০ LPD" },
      ],
    },
    how: {
      eyebrow: "ছাদের সোলার কীভাবে কাজ করে",
      title_pre: "",
      title_hl: "সূর্যের আলো",
      title_mid: "থেকে সঞ্চয় — চারটি সহজ ধাপে।",
      subtitle:
        "ছাদের সোলার জাদু নয়, স্মার্ট মিটারিং সহ সহজ পদার্থবিদ্যা। প্রতিদিন স্বয়ংক্রিয়ভাবে এটি ঘটে:",
      steps: [
        { title: "সূর্যের আলো প্যানেলে পড়ে", desc: "উচ্চ দক্ষতা মোনো PERC প্যানেল দিনের আলো শোষণ করে DC বিদ্যুৎ তৈরি করে।" },
        { title: "ইনভার্টার AC-তে রূপান্তর করে", desc: "96% দক্ষতায় DC কে AC তে রূপান্তরকারী স্মার্ট গ্রিড-টাই ইনভার্টার।" },
        { title: "আপনার বাড়ি প্রথমে এটি ব্যবহার করে", desc: "পাখা, আলো, AC, পাম্প — দিনের বেলা সূর্য থেকে, গ্রিড ব্যবহার বেশ কমে যায়।" },
        { title: "নেট মিটার বাকিটা সঞ্চয় করে", desc: "অতিরিক্ত ইউনিট গ্রিডে ফিরে যায়। মাসের শেষে শুধু নেট ব্যবহারের বিল।" },
      ],
      kpis: [
        { k: "70–90%", v: "ইনস্টলেশনের পরে মাসিক বিদ্যুৎ বিলে হ্রাস।" },
        { k: "₹0 EMI", v: "পার্টনার ব্যাঙ্কের মাধ্যমে নমনীয় অর্থায়ন।" },
        { k: "25 বছর", v: "প্যানেল আউটপুট ওয়ারেন্টি (ন্যূনতম 80%)।" },
      ],
    },
    subsidy: {
      eyebrow: "সরকারি সাবসিডি",
      title_pre: "পান",
      title_hl: "₹78,000",
      title_post: " পর্যন্ত ছাড় — কাগজপত্র আমরা সামলাই।",
      subtitle: "বাড়ি হোক বা খামার, ভারত সরকার সোলার গ্রহণে উদার সাবসিডি দেয়। পূর্বোদয় প্রতিটি আবেদন শুরু থেকে অর্থপ্রদান পর্যন্ত ট্র্যাক করে।",
      tab_res: "আবাসিক · PM সূর্য ঘর",
      tab_agri: "কৃষি · PM-KUSUM",
      res_title: "PM সূর্য ঘর: বিনামূল্যে বিদ্যুৎ প্রকল্প",
      res_desc: "ভারত সরকার চালু করা এই প্রকল্পে, আপনার বাড়িতে ছাদের সোলার ইনস্টলের পরে সাবসিডি সরাসরি আপনার ব্যাঙ্ক অ্যাকাউন্টে আসে।",
      slab_labels: ["1 kW পর্যন্ত", "2 kW পর্যন্ত", "3 kW ও তার বেশি"],
      slab_notes: ["₹30,000 প্রতি kW", "₹30,000 প্রতি kW", "সর্বোচ্চ সীমা"],
      res_bullets: [
        "আবাসিক বাড়ির ছাদের PV সিস্টেমের জন্য প্রযোজ্য।",
        "যোগ্য পরিবারের জন্য প্রতি মাসে 300 ইউনিট বিনামূল্যে।",
        "কমিশনিং ও পরিদর্শনের পরে সরাসরি ব্যাঙ্ক ট্রান্সফার।",
        "আমাদের দল জাতীয় পোর্টালে অফিসিয়াল আবেদন করে।",
      ],
      read_official: "অফিসিয়াল প্রকল্প পড়ুন",
      savings_label: "আপনার সঞ্চয়",
      savings_amount: "₹78,000",
      savings_note: "PM সূর্য ঘরের অধীনে 3 kW+ আবাসিক ছাদে সর্বোচ্চ কেন্দ্রীয় সাবসিডি। রাজ্য প্রণোদনা অতিরিক্ত হতে পারে।",
      example_label: "উদাহরণ · 3 kW সিস্টেম",
      example_cost: "সিস্টেম খরচ",
      example_subsidy: "সাবসিডি",
      example_pay: "আপনি দেবেন",
      agri_title: "PM-KUSUM প্রকল্প",
      agri_desc: "কৃষকদের সোলার পাম্প ইনস্টল ও কৃষি ফিডার সোলারাইজেশনে সাহায্য করে — ডিজেল নির্ভরতা কমায় এবং নির্ভরযোগ্য দিনের বিদ্যুৎ দেয়।",
      agri_components: [
        { k: "কম্প-A", v: "অনুর্বর জমিতে গ্রিড-সংযুক্ত সোলার প্ল্যান্ট" },
        { k: "কম্প-B", v: "স্ট্যান্ড-অ্যালোন সোলার জল পাম্প" },
        { k: "কম্প-C", v: "বিদ্যমান গ্রিড পাম্পের সোলারাইজেশন" },
      ],
      agri_bullets: [
        "সোলার পাম্পে 60% পর্যন্ত কেন্দ্র + রাজ্য সাবসিডি।",
        "30% ব্যাঙ্ক ঋণ; কৃষকের অবদান মাত্র ~10%।",
        "অতিরিক্ত আয়ের জন্য উদ্বৃত্ত বিদ্যুৎ DISCOM-কে বিক্রি করুন।",
        "ইনস্টলেশন ও DBT সহায়তা পূর্বোদয় দ্বারা।",
      ],
      farmer_benefit: "কৃষকের সুবিধা",
      farmer_amount: "60% পর্যন্ত ছাড়",
      farmer_note: "সোলার পাম্প ইনস্টলে, এবং উদ্বৃত্ত বিদ্যুৎ গ্রিডে বিক্রি করে আয়ের উৎস।",
      sub_individual: "ব্যক্তিগত বাড়ি",
      sub_society: "গ্রুপ হাউসিং সোসাইটি",
      ind_box_label: "আপনার মোট সঞ্চয়",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar এর অধীনে 3 kW+ আবাসিক রুফটপ সিস্টেমে সর্বাধিক কেন্দ্রীয় ভর্তুকি। রাজ্যের প্রণোদনা অতিরিক্ত প্রযোজ্য হতে পারে।",
      ind_example_title: "উদাহরণ · 3 kW সিস্টেম",
      ind_example_rows: [
        { label: "সিস্টেম খরচ", value: "₹1,80,000" },
        { label: "ভর্তুকি", value: "₹78,000" },
      ],
      ind_example_net_label: "আপনি দিচ্ছেন",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "আপনি আরও পাবেন",
      ind_extras: [
        "যোগ্য পরিবারের জন্য মাসে 300 ইউনিট বিনামূল্যে",
        "অতিরিক্ত উৎপাদনের জন্য নেট-মিটারিং ক্রেডিট",
        "25-বছরের প্যানেল পারফরম্যান্স ওয়ারেন্টি",
        "Purvodaya দ্বারা বিনামূল্যে PM Surya Ghar পোর্টাল ফাইলিং",
      ],
      ghs_title: "গ্রুপ হাউসিং সোসাইটি (GHS / RWAs)",
      ghs_intro: "PMSGY এর অধীনে কেন্দ্রীয় সরকার লিফট, জলের পাম্প, স্ট্রিটলাইট, লবি ও করিডোর লাইটিং এর মতো সাধারণ সুবিধার জন্য গ্রিড-সংযুক্ত রুফটপ সোলারে ₹18,000 / kW প্রদান করে।",
      ghs_structure_label: "ভর্তুকি কাঠামো",
      ghs_structure: [
        "স্থাপিত কমন-এরিয়া সোলার ক্ষমতার ₹18,000 / kW।",
        "প্রতি সোসাইটির জন্য সর্বাধিক 500 kW — অর্থাৎ ₹90 লক্ষ পর্যন্ত ভর্তুকি।",
        "অনুমোদিত ক্ষমতা = 3 kW × ফ্ল্যাটের সংখ্যা।",
      ],
      ghs_eligibility_label: "যোগ্যতা চেকলিস্ট",
      ghs_eligibility: [
        "CHS / RWA নিজস্ব PAN সহ আইনত নিবন্ধিত হতে হবে।",
        "সোসাইটির নামে সক্রিয় সাধারণ-পরিষেবা বিদ্যুৎ মিটার।",
        "সরাসরি ভর্তুকি স্থানান্তরের জন্য নিবেদিত সোসাইটি ব্যাংক অ্যাকাউন্ট।",
        "শুধুমাত্র মেক-ইন-ইন্ডিয়া (DCR-অনুরূপ) প্যানেল।",
      ],
      ghs_box_label: "সোসাইটি-ব্যাপী সঞ্চয়",
      ghs_box_amount: "₹90 লক্ষ পর্যন্ত",
      ghs_box_caption: "একটি হাউসিং সোসাইটির সর্বাধিক PMSGY ভর্তুকি — ₹18,000 / kW হারে, 500 kW এ সর্বোচ্চ।",
      ghs_example_title: "হিসাব · 100-ফ্ল্যাট সোসাইটি",
      ghs_example_rows: [
        { label: "অনুমোদিত ক্ষমতা (3 kW × 100)", value: "300 kW" },
        { label: "প্রকল্পের খরচ (₹50,000 / kW)", value: "₹1.50 কোটি" },
        { label: "PMSGY ভর্তুকি", value: "₹54 লক্ষ" },
        { label: "বার্ষিক বিল সঞ্চয়", value: "~₹22 লক্ষ" },
      ],
      ghs_example_net_label: "সোসাইটির নিট খরচ",
      ghs_example_net_value: "₹96 লক্ষ",
      ghs_example_payback: "4–5 বছরে পেব্যাক · 20+ বছর বিনামূল্যে বিদ্যুৎ",
      ghs_documents_label: "সোসাইটি থেকে প্রয়োজনীয় নথি",
      ghs_documents: [
        "সোসাইটি নিবন্ধন সার্টিফিকেট + PAN",
        "গত 3 মাসের কমন-এরিয়া বিদ্যুৎ বিল",
        "সোসাইটি ব্যাংক পাসবুক",
        "প্রকল্প অনুমোদনের AGM/EGM প্রস্তাব",
        "বিল্ডিং প্ল্যান ও রুফটপ লেআউট",
      ],
    },
    calc: {
      eyebrow: "সঞ্চয় ক্যালকুলেটর",
      title_pre: "আপনি কত",
      title_hl: "সঞ্চয় করবেন",
      title_post: " দেখুন — শুরু করতে + এবং − চাপুন।",
      subtitle: "আপনার যন্ত্রপাতি নির্বাচন করুন, ঘণ্টা সমন্বয় করুন — আমরা সঠিক আকারের সোলার সিস্টেম, লাইভ সাবসিডি ও ফেরত অনুমান সহ সুপারিশ করব।",
      tab_res: "আবাসিক",
      tab_agri: "কৃষি",
      appliances: "আপনার যন্ত্রপাতি",
      pincode_placeholder: "পিনকোড",
      tariff: "ট্যারিফ ₹{rate}/ইউনিট",
      detected: "সনাক্ত",
      default_hours: "ডিফল্ট {h}ঘ/দিন",
      hours_day: "ঘণ্টা/দিন",
      snapshot: "আপনার সোলার স্ন্যাপশট",
      daily: "দৈনিক",
      monthly_units: "মাসিক ইউনিট",
      current_bill: "বর্তমান বিল",
      recommended: "সুপারিশ",
      investment: "বিনিয়োগ বিবরণ",
      system_cost: "সিস্টেম খরচ",
      pm_subsidy: "PM সূর্য ঘর সাবসিডি",
      kusum_subsidy: "PM-KUSUM সাবসিডি (~30%)",
      net_investment: "নিট বিনিয়োগ",
      monthly_savings: "মাসিক সঞ্চয়",
      payback: "ফেরত",
      co2_yr: "CO₂/বছর",
      cta_quote: "আমার {kw} kW কোট",
      disclaimer: "অনুমান 4 সূর্য-ঘণ্টা/দিন এবং ₹{rate}/ইউনিট ট্যারিফ ভিত্তিক। চূড়ান্ত ডিজাইন বিনামূল্যে সাইট ভিজিটের পরে।",
      appliance_labels: {
        fan: "সিলিং ফ্যান",
        tube: "টিউব / LED লাইট",
        ac: "এয়ার কন্ডিশনার (1.5T)",
        fridge: "রেফ্রিজারেটর",
        tv: "টেলিভিশন",
        geyser: "গিজার",
        washer: "ওয়াশিং মেশিন",
        microwave: "মাইক্রোওয়েভ",
        computer: "কম্পিউটার / ল্যাপটপ",
        pump_home: "জল পাম্প (1 HP)",
        pump_3hp: "জল পাম্প (3 HP)",
        pump_5hp: "জল পাম্প (5 HP)",
        pump_75hp: "জল পাম্প (7.5 HP)",
        pump_10hp: "সাবমার্সিবল (10 HP)",
        lights: "খামারের আলো",
        chaffcutter: "চ্যাফ কাটার",
      },
    },
    workflow: {
      eyebrow: "আমাদের প্রক্রিয়া",
      title_pre: "ছয়টি ধাপ।",
      title_hl: "শূন্য",
      title_post: "ঝামেলা।",
      subtitle: "প্রথম কল থেকে 25 বছরের সঞ্চয় পর্যন্ত, পুরো যাত্রা পূর্বোদয় পরিচালনা করে। এইভাবে ঘটে:",
      activities: "কার্যাবলী",
      deliverables: "ডেলিভারেবল",
      outcome: "ফলাফল",
      steps: [
        { title: "সাইট ভিজিট ও সম্ভাব্যতা", activities: "ছায়া বিশ্লেষণ, ছাদ পরিদর্শন, লোড অধ্যয়ন, বিদ্যুৎ বিল পর্যালোচনা।", deliverables: "সিস্টেম আকার ও উৎপাদন অনুমান সহ সম্ভাব্যতা রিপোর্ট।", outcome: "আপনার ছাদ সোলারের জন্য প্রস্তুত এই আত্মবিশ্বাস।" },
        { title: "ডিজাইন ও প্রস্তাব", activities: "3D ছাদ লেআউট, কাঠামোগত + বৈদ্যুতিক SLD, প্যানেল ও ইনভার্টার নির্বাচন।", deliverables: "BOM, টাইমলাইন ও সাবসিডি অনুমান সহ বিস্তারিত প্রস্তাব।", outcome: "স্বচ্ছ মূল্য, কোনো চমক নেই।" },
        { title: "অনুমোদন ও সাবসিডি আবেদন", activities: "নেট-মিটার আবেদন, DISCOM যোগাযোগ, PM সূর্য ঘর / KUSUM ফাইলিং।", deliverables: "নেট মিটারিং অনুমোদন + সাবসিডি নিবন্ধন।", outcome: "কাগজপত্র সম্পূর্ণ সামলানো।" },
        { title: "ইনস্টলেশন", activities: "কাঠামো মাউন্টিং, প্যানেল স্থাপন, কেবলিং, ইনভার্টার কমিশনিং।", deliverables: "সম্পূর্ণ ইনস্টল, লেবেল ও পরীক্ষিত ছাদ সিস্টেম।", outcome: "সাধারণত 5–7 দিনে বিদ্যুৎ।" },
        { title: "কমিশনিং ও গো-লাইভ", activities: "DISCOM পরিদর্শন, নেট মিটার ইনস্টল, অ্যাপ অনবোর্ডিং।", deliverables: "হ্যান্ডওভার প্যাক + মনিটরিং অ্যাপ লগইন।", outcome: "আপনার মিটার পিছনে ঘুরতে শুরু করে।" },
        { title: "বিক্রয়োত্তর ও পর্যবেক্ষণ", activities: "দূরবর্তী পারফরম্যান্স পর্যবেক্ষণ, পর্যায়ক্রমে পরিষ্কার, AMC সমর্থন।", deliverables: "বার্ষিক পারফরম্যান্স রিপোর্ট, অগ্রাধিকার হেল্পলাইন।", outcome: "25 বছরের চিন্তামুক্ত সূর্যের আলো।" },
      ],
    },
    faq: {
      eyebrow: "ঘন ঘন জিজ্ঞাসিত প্রশ্ন",
      title_pre: "সবার প্রশ্ন —",
      title_hl: "সৎ উত্তর।",
      items: [
        { q: "সোলার সিস্টেমের জন্য কত ছাদের জায়গা লাগে?", a: "সাধারণ নিয়ম: প্রতি 1 kW এর জন্য ~80–100 বর্গ ফুট ছায়াহীন এলাকা। 3 kW হোম সিস্টেম 300 বর্গ ফুট ছাদে সহজে আঁটে।" },
        { q: "বর্ষাকালে বা মেঘলা দিনে সোলার কি কাজ করবে?", a: "হ্যাঁ। প্যানেল বিক্ষিপ্ত আলোতেও (পিকের ~10–25%) উৎপাদন করে। নেট-মিটারিং দিয়ে গ্রিড থেকে নিতে পারবেন।" },
        { q: "PM সূর্য ঘর সাবসিডি প্রক্রিয়া কী?", a: "আপনি জাতীয় পোর্টালে নিবন্ধন করেন, আমরা আবেদন করি, MNRE-তালিকাভুক্ত ভেন্ডর (পূর্বোদয়) দ্বারা ইনস্টল, DISCOM পরিদর্শনের পরে সরাসরি ব্যাঙ্ক ট্রান্সফার।" },
        { q: "ইনস্টলেশনে কত সময় লাগে?", a: "মানক 3 kW আবাসিক ইনস্টলেশন 3–5 দিন নেয়। অনুমোদন সহ সম্পূর্ণ 4–6 সপ্তাহ।" },
        { q: "সোলার সিস্টেমের কী রক্ষণাবেক্ষণ দরকার?", a: "খুব কম। ধুলো এলাকায় 1–2 মাসে পরিষ্কার ও বার্ষিক পরিদর্শন। প্রতিটি ইনস্টলেশনের সাথে 1 বছরের বিনামূল্যে AMC।" },
        { q: "কৃষকরা কি সত্যিই PM-KUSUM থেকে আয় করতে পারেন?", a: "অবশ্যই। কম্প-A ও C এর অধীনে উদ্বৃত্ত সোলার বিদ্যুৎ DISCOM নির্দিষ্ট ট্যারিফে কেনে — পাম্পের জন্য বিনামূল্যে দিনের বিদ্যুৎ সহ স্থির অতিরিক্ত আয়।" },
      ],
    },
    footer: {
      tag: "ভবিষ্যতকে উজ্জ্বল করুন",
      desc: "ভারতীয় বাড়ি ও খামারকে বিল কমাতে এবং শক্তি পরিবর্তনকে ত্বরান্বিত করতে সাহায্য করা MNRE-তালিকাভুক্ত ছাদের সোলার EPC কোম্পানি।",
      explore: "দেখুন",
      contact: "যোগাযোগ",
      rights: "© {year} পূর্বোদয় সোলার। সমস্ত অধিকার সংরক্ষিত।",
      built: "পরিচ্ছন্ন ভারতের জন্য ☀ দিয়ে তৈরি।",
    },
    lead: {
      badge: "বিনামূল্যে ও কোনো বাধ্যবাধকতা নেই",
      title_default: "বিনামূল্যে ছাদের কোট নিন",
      subtitle_default: "আপনার বিবরণ শেয়ার করুন — একজন পূর্বোদয় বিশেষজ্ঞ 24 ঘণ্টায় কল করবেন।",
      title_calc: "আপনার ব্যক্তিগত কোট নিন",
      title_idle: "এখনও খুঁজছেন? আমরা সাহায্য করি।",
      subtitle_idle: "আপনার বিবরণ শেয়ার করুন — আপনার ফোনে কাস্টম সোলার সঞ্চয় পাঠাব।",
      full_name: "পূর্ণ নাম *",
      name_ph: "আপনার নাম",
      phone: "ফোন *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "পিনকোড",
      email: "ইমেল",
      email_ph: "you@example.com",
      i_need: "আমার সোলার দরকার",
      seg_home: "বাড়ি / আবাসিক",
      seg_farm: "খামার / কৃষি",
      seg_commercial: "বাণিজ্যিক / শিল্প",
      anything_else: "আর কিছু?",
      msg_ph: "ছাদের আকার, বর্তমান বিল, ইত্যাদি।",
      submit: "আমার বিনামূল্যে কল-ব্যাক চাই",
      submitting: "পাঠানো হচ্ছে…",
      thanks_title: "আপনি আমাদের তালিকায় আছেন!",
      thanks_desc: "একজন সোলার পরামর্শদাতা শীঘ্রই যোগাযোগ করবেন।",
      toast_ok: "ধন্যবাদ! শীঘ্রই কল করব।",
      toast_required: "নাম ও ফোন দরকার",
      toast_fail: "পাঠাতে পারিনি। আবার চেষ্টা করুন।",
      consent: "জমা দিয়ে, আপনি পূর্বোদয় সোলারের সাথে যোগাযোগে সম্মত হচ্ছেন।",
    },
    whatsapp: { label: "আমাদের সাথে চ্যাট করুন" },
    common: { language: "ভাষা" },
    ts: {
        eyebrow: "কেন পুর্বোদয়",
        title_pre: "একটি গুরুত্বপূর্ণ অংশীদার —",
        title_hl: "ভারতের সৌর রূপান্তরের জন্য",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "এনার্জি ট্রানজিশন পার্টনার",
        p1_title: "ভারতের সৌর-চালিত শক্তি রূপান্তরের নির্ভরযোগ্য অংশীদার",
        p1_desc: "দেশের আবাসিক, হাউসিং সোসাইটি এবং কর্পোরেট পরিচ্ছন্ন শক্তি যাত্রার জন্য বিশেষভাবে তৈরি — দীর্ঘমেয়াদী পারফরম্যান্সের জন্য, ক্ষণস্থায়ী ছকের জন্য নয়।",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM ফাইলার",
        p2_title: "কেন্দ্রীয় সৌর ভর্তুকির অনুমোদিত ফাইলার",
        p2_desc: "আমরা ব্যক্তিগত বাড়ি ও হাউসিং সোসাইটির জন্য PM Surya Ghar এবং কৃষকদের জন্য PM-KUSUM ফাইল করি — সমস্ত কাগজপত্র আমাদের দল পরিচালনা করে, কোনো মধ্যস্বত্বভোগী নেই।",
        p3_eyebrow: "এন্ড-টু-এন্ড EPC + 25-বছরের O&M",
        p3_title: "একজন দায়িত্বশীল অংশীদার — ডিজাইন থেকে সুইচ-অন এবং 25 বছরের যত্ন",
        p3_desc: "MNRE-সম্মত ডিজাইন, Tier-1 মডিউল, সার্টিফাইড ইনস্টলেশন টিম, DISCOM যোগাযোগ এবং 25-বছরের পারফরম্যান্স গ্যারান্টি — সব এক ছাদের নিচে।",
        promise_label: "আমাদের প্রতিশ্রুতি",
        promise1: "Tier-1 মডিউল · 25-বছরের পারফরম্যান্স ওয়ারেন্টি",
        promise2: "MNRE-সম্মত ইঞ্জিনিয়ারিং ও সুরক্ষা চর্চা",
        promise3: "স্বচ্ছ কোটেশন · কোনো লুকানো খরচ নেই",
        promise4: "ত্রৈমাসিক পারফরম্যান্স ও পরিষ্কারের রিপোর্ট",
      },
    res: {
        hero_badge: "আবাসিক রুফটপ সোলার",
        hero_title_a: "আপনার AC, ফ্রিজ, গিজার —",
        hero_title_b: "সূর্য থেকে বিনামূল্যে চালান।",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "বিনামূল্যে রুফটপ কোট পান",
        cta_calc: "আপনার সঞ্চয় অনুমান করুন",
        stat_subsidy: "সর্বোচ্চ কেন্দ্রীয় ভর্তুকি",
        stat_reduction: "বিল হ্রাস",
        stat_payback: "সাধারণ পেব্যাক",
        stat_warranty: "পারফরম্যান্স ওয়ারেন্টি",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "হোমে ফিরে যান",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "বাণিজ্যিক ও শিল্প সোলার",
        hero_title_a: "Power your business with the",
        hero_title_hl: "সূর্যের",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "ব্যবসায়িক প্রস্তাব চান",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "বিল হ্রাস",
        stat_payback: "সাধারণ পেব্যাক",
        stat_warranty: "পারফরম্যান্স ওয়ারেন্টি",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "হোমে ফিরে যান",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "হাউসিং সোসাইটি সোলার",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "সোসাইটি পরিদর্শন চান",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "সর্বোচ্চ কেন্দ্রীয় ভর্তুকি",
        stat_reduction: "বিল হ্রাস",
        stat_payback: "সাধারণ পেব্যাক",
        stat_warranty: "পারফরম্যান্স ওয়ারেন্টি",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "হোমে ফিরে যান",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },

  kn: {
    nav: {
      services: "ಸೇವೆಗಳು",
      how: "ಇದು ಹೇಗೆ ಕಾರ್ಯನಿರ್ವಹಿಸುತ್ತದೆ",
      subsidy: "ಸಬ್ಸಿಡಿ",
      savings: "ಉಳಿತಾಯ",
      process: "ಪ್ರಕ್ರಿಯೆ",
      faq: "ಪ್ರಶ್ನೋತ್ತರಗಳು",
      admin: "ಅಡ್ಮಿನ್",
      cta: "ಉಚಿತ ಕೋಟ್ ಪಡೆಯಿರಿ",
      menu: "ಮೆನು",
      residential: "ವಸತಿ",
      commercial: "ವಾಣಿಜ್ಯ",
      housing: "ಹೌಸಿಂಗ್ ಸೊಸೈಟಿಗಳು",
    },
    hero: {
      badge: "MNRE ನೋಂದಾಯಿತ · PM ಸೂರ್ಯ ಘರ್ ಸಿದ್ಧ",
      title_pre: "ನಿಮ್ಮ ಮನೆಯನ್ನು",
      title_highlight: "ಸೂರ್ಯಕಿರಣ",
      title_post1: "ದಿಂದ ಚಾಲಾಯಿಸಿ,",
      title_post2: "ವಿದ್ಯುತ್ ಬಿಲ್ಲನ್ನು ಇಳಿಸಿ.",
      subtitle_pre:
        "ಪೂರ್ವೋದಯ ಸೋಲಾರ್ ಭಾರತದಾದ್ಯಂತ ಮನೆ ಮತ್ತು ಜಮೀನುಗಳಿಗೆ ಛಾವಣಿಯ ಸೌರ ವ್ಯವಸ್ಥೆಗಳನ್ನು ವಿನ್ಯಾಸ, ಸ್ಥಾಪನೆ ಮಾಡುತ್ತದೆ —",
      subtitle_and: "ಮತ್ತು",
      subtitle_post: "ಸಬ್ಸಿಡಿಗಾಗಿ ಸಂಪೂರ್ಣ ಬೆಂಬಲದೊಂದಿಗೆ.",
      cta_quote: "ಉಚಿತ ಛಾವಣಿ ಕೋಟ್ ಪಡೆಯಿರಿ",
      cta_calc: "ನಿಮ್ಮ ಉಳಿತಾಯ ಲೆಕ್ಕಾಚಾರ",
      stat1_k: "₹78,000",
      stat1_v: "ಗರಿಷ್ಠ ಸಬ್ಸಿಡಿ (ವಸತಿ)",
      stat2_k: "25 ವರ್ಷ",
      stat2_v: "ಪ್ಯಾನೆಲ್ ಕಾರ್ಯಕ್ಷಮತೆ",
      stat3_k: "4–5 ವರ್ಷ",
      stat3_v: "ಸಾಮಾನ್ಯ ಮರುಪಾವತಿ",
      live_label: "ಸರಾಸರಿ ಮಾಸಿಕ ಉಳಿತಾಯ",
      live_value: "₹4,120",
      live_caption: "ಸಾಮಾನ್ಯ 3 kW ಮನೆಗೆ PM ಸೂರ್ಯ ಘರ್ ಸಬ್ಸಿಡಿಯ ನಂತರ",
      this_month: "ಈ ವರ್ಷ",
      saved: "ಸರಾಸರಿ ಕುಟುಂಬದ ಉಳಿತಾಯ",
      co2: "t CO₂ ವಾರ್ಷಿಕ ಕಡಿತ",
    },
    trust: { heading: "ಟಿಯರ್-1 ಪ್ಯಾನೆಲ್ ಮತ್ತು ಇನ್ವರ್ಟರ್ ಬ್ರಾಂಡ್‌ಗಳೊಂದಿಗೆ" },
    services: {
            cta_explore: "ಅನುಕೂಲಗಳನ್ನು ತಿಳಿಯಿರಿ",
      cta_street_b2b: "B2B ನಿಯೋಜನೆಗಳಿಗಾಗಿ ಮಾತನಾಡಿ →",
eyebrow: "ಸಂಪೂರ್ಣ ಸೌರ ಪರಿಹಾರಗಳು",
      title_pre: "ಒಬ್ಬ ಪಾಲುದಾರ.",
      title_hl: "ಪ್ರತಿಯೊಂದು ಸೌರ ಅಗತ್ಯ",
      title_post: "ಒಳಗೊಂಡಿದೆ.",
      subtitle: "ಛಾವಣಿಗಳಿಂದ ಕಾರ್ಖಾನೆಗಳವರೆಗೆ, EV ಚಾರ್ಜರ್‌ಗಳಿಂದ ಬೀದಿ ದೀಪಗಳವರೆಗೆ — ಪೂರ್ವೋದಯ ಸೋಲಾರ್ ನಿಮ್ಮ ಸ್ಥಳ ಮತ್ತು ಬಜೆಟ್‌ಗೆ ಸರಿಹೊಂದುವ ಸಂಪೂರ್ಣ ಶುದ್ಧ-ಶಕ್ತಿ ಪರಿಹಾರಗಳನ್ನು ನೀಡುತ್ತದೆ.",
      cta: "ಈ ಸೇವೆಯನ್ನು ಅನ್ವೇಷಿಸಿ",
      items: [
        { key: "rooftop", title: "ವಸತಿ ಛಾವಣಿ ಸೌರ", desc: "ಮನೆಗಳಿಗಾಗಿ MNRE-ನೋಂದಾಯಿತ ಛಾವಣಿ ಸ್ಥಾಪನೆಗಳು — PM ಸೂರ್ಯ ಘರ್ ಯೋಜನೆ ಅಡಿಯಲ್ಲಿ ₹78,000 ವರೆಗೆ ಕೇಂದ್ರ ಸಬ್ಸಿಡಿ.", highlight: "PM ಸೂರ್ಯ ಘರ್ ಯೋಜನೆ" },
        { key: "commercial", title: "ವಾಣಿಜ್ಯ ಮತ್ತು ಕೈಗಾರಿಕಾ ಸೌರ", desc: "ಕಾರ್ಖಾನೆಗಳು, ಕಚೇರಿಗಳು, ಗೋದಾಮುಗಳಿಗೆ MW ಪ್ರಮಾಣದ ಛಾವಣಿ ಮತ್ತು ನೆಲ-ಮೌಂಟ್ ವ್ಯವಸ್ಥೆಗಳು. ವಿದ್ಯುತ್ ವೆಚ್ಚವನ್ನು 60% ಕಡಿಮೆ ಮಾಡಿ ಮತ್ತು 40% ತ್ವರಿತ ಸವಕಳಿ ಪಡೆಯಿರಿ.", highlight: "40% ವರೆಗೆ ತೆರಿಗೆ ಉಳಿತಾಯ" },
        { key: "ev", title: "EV ಚಾರ್ಜಿಂಗ್ ಪರಿಹಾರಗಳು", desc: "ಮನೆಗಳು, ಕಚೇರಿಗಳು, ಮಾಲ್‌ಗಳು ಮತ್ತು ಹೆದ್ದಾರಿ ಪ್ಲಾಜಾಗಳಿಗೆ ಸೌರ-ಚಾಲಿತ AC ಮತ್ತು DC ಫಾಸ್ಟ್ EV ಚಾರ್ಜರ್‌ಗಳು — ಶೂನ್ಯ-ವೆಚ್ಚದ ಡ್ರೈವಿಂಗ್‌ಗಾಗಿ ಛಾವಣಿ ಸೌರದೊಂದಿಗೆ ಜೋಡಿಸಿ.", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "ಬ್ಯಾಟರಿ ಶಕ್ತಿ ಸಂಗ್ರಹ", desc: "ಬ್ಯಾಕಪ್ ಶಕ್ತಿ, ಪೀಕ್ ಶೇವಿಂಗ್ ಮತ್ತು ಸಂಪೂರ್ಣ ಆಫ್-ಗ್ರಿಡ್ ಸ್ವಾತಂತ್ರ್ಯಕ್ಕಾಗಿ ಲಿಥಿಯಂ-ಅಯಾನ್ BESS. 5 kWh ಗೃಹ ಪ್ಯಾಕ್‌ಗಳಿಂದ 1 MWh+ ವಾಣಿಜ್ಯ ಬ್ಯಾಂಕ್‌ಗಳವರೆಗೆ.", highlight: "10 ವರ್ಷ ವಾರಂಟಿ" },
        { key: "street", title: "ಸೌರ ಬೀದಿ ದೀಪ", desc: "ಸಂಘಗಳು, ಕ್ಯಾಂಪಸ್‌ಗಳು, ಹೆದ್ದಾರಿಗಳು ಮತ್ತು ಗ್ರಾಮೀಣ ರಸ್ತೆಗಳಿಗೆ ಸ್ಟ್ಯಾಂಡ್-ಅಲೋನ್ ಆಲ್-ಇನ್-ಒನ್ ಸೌರ ಬೀದಿ ದೀಪಗಳು — ಶೂನ್ಯ ವೈರಿಂಗ್, ಶೂನ್ಯ ಬಿಲ್.", highlight: "ಆಟೋ ಡಸ್ಕ್-ಟು-ಡಾನ್" },
        { key: "society", title: "ವಸತಿ ಸಂಘ ಸೌರ", desc: "ಅಪಾರ್ಟ್‌ಮೆಂಟ್ ಸಂಕೀರ್ಣಗಳಿಗೆ ಗುಂಪು ಛಾವಣಿ ಮತ್ತು ಸಾಮಾನ್ಯ-ಪ್ರದೇಶ ಸೌರ — ವೈಯಕ್ತಿಕ ಫ್ಲಾಟ್‌ಗಳಿಗೆ PM ಸೂರ್ಯ ಘರ್ ಪ್ರಯೋಜನಗಳು + ಸಂಘದ ಉಳಿತಾಯ.", highlight: "RWA-ಸ್ನೇಹಿ" },
        { key: "heater", title: "ಸೌರ ನೀರಿನ ಹೀಟರ್", desc: "ಮನೆಗಳು, ಹಾಸ್ಟೆಲ್‌ಗಳು ಮತ್ತು ಹೋಟೆಲ್‌ಗಳಿಗೆ ಎವಾಕ್ಯುಯೇಟೆಡ್ ಗ್ಲಾಸ್-ಟ್ಯೂಬ್ ಸೌರ ನೀರಿನ ಹೀಟರ್‌ಗಳು — 365 ದಿನ ಬಿಸಿ ನೀರು, ಗೀಸರ್ ಬಿಲ್ ಸಂಪೂರ್ಣ ಸ್ಥಗಿತ.", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "ಛಾವಣಿಯ ಸೌರ ಹೇಗೆ ಕಾರ್ಯನಿರ್ವಹಿಸುತ್ತದೆ",
      title_pre: "",
      title_hl: "ಸೂರ್ಯಕಿರಣದಿಂದ",
      title_mid: "ಉಳಿತಾಯದವರೆಗೆ — ನಾಲ್ಕು ಸರಳ ಹಂತಗಳಲ್ಲಿ.",
      subtitle: "ಛಾವಣಿಯ ಸೌರ ಮಾಯಾಜಾಲವಲ್ಲ, ಸ್ಮಾರ್ಟ್ ಮೀಟರಿಂಗ್ ಜೊತೆ ಸರಳ ಭೌತಶಾಸ್ತ್ರ. ಪ್ರತಿ ದಿನ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಇದೆಲ್ಲ ನಡೆಯುತ್ತದೆ:",
      steps: [
        { title: "ಸೂರ್ಯಕಿರಣ ಪ್ಯಾನೆಲ್‌ಗಳ ಮೇಲೆ ಬೀಳುತ್ತದೆ", desc: "ಹೆಚ್ಚು ದಕ್ಷ ಮೋನೋ PERC ಪ್ಯಾನೆಲ್‌ಗಳು ಹಗಲಿನ ಬೆಳಕನ್ನು ಹೀರಿ DC ವಿದ್ಯುತ್ ಉತ್ಪಾದಿಸುತ್ತವೆ." },
        { title: "ಇನ್ವರ್ಟರ್ AC ಗೆ ಪರಿವರ್ತಿಸುತ್ತದೆ", desc: "96% ದಕ್ಷತೆಯಲ್ಲಿ DC ಯನ್ನು AC ಗೆ ಪರಿವರ್ತಿಸುವ ಸ್ಮಾರ್ಟ್ ಗ್ರಿಡ್-ಟೈಡ್ ಇನ್ವರ್ಟರ್." },
        { title: "ನಿಮ್ಮ ಮನೆ ಮೊದಲು ಇದನ್ನು ಬಳಸುತ್ತದೆ", desc: "ಫ್ಯಾನ್, ದೀಪಗಳು, AC, ಪಂಪ್‌ಗಳು — ಹಗಲಿನಲ್ಲಿ ಸೂರ್ಯನಿಂದ, ಗ್ರಿಡ್ ಬಳಕೆ ನಾಟಕೀಯವಾಗಿ ಕಡಿಮೆಯಾಗುತ್ತದೆ." },
        { title: "ನೆಟ್ ಮೀಟರ್ ಉಳಿದದ್ದನ್ನು ಶೇಖರಿಸುತ್ತದೆ", desc: "ಹೆಚ್ಚುವರಿ ಯೂನಿಟ್‌ಗಳು ಗ್ರಿಡ್‌ಗೆ ಹಿಂತಿರುಗುತ್ತವೆ. ತಿಂಗಳ ಕೊನೆಯಲ್ಲಿ ನಿವ್ವಳ ಬಳಕೆಗೆ ಮಾತ್ರ ಬಿಲ್." },
      ],
      kpis: [
        { k: "70–90%", v: "ಸ್ಥಾಪನೆಯ ನಂತರ ಮಾಸಿಕ ವಿದ್ಯುತ್ ಬಿಲ್‌ನಲ್ಲಿ ಇಳಿಕೆ." },
        { k: "₹0 EMI", v: "ಪಾಲುದಾರ ಬ್ಯಾಂಕ್‌ಗಳ ಮೂಲಕ ಹೊಂದಿಕೊಳ್ಳುವ ಆರ್ಥಿಕ ನೆರವು." },
        { k: "25 ವರ್ಷ", v: "ಪ್ಯಾನೆಲ್ ಔಟ್‌ಪುಟ್ ವಾರಂಟಿ (ಕನಿಷ್ಠ 80%)." },
      ],
    },
    subsidy: {
      eyebrow: "ಸರ್ಕಾರಿ ಸಬ್ಸಿಡಿಗಳು",
      title_pre: "ಪಡೆಯಿರಿ",
      title_hl: "₹78,000",
      title_post: "ವರೆಗೆ ರಿಯಾಯಿತಿ — ದಾಖಲೆ ಕಾರ್ಯ ನಮ್ಮದು.",
      subtitle: "ಮನೆಯಾಗಲಿ ಅಥವಾ ಜಮೀನಾಗಲಿ, ಭಾರತ ಸರ್ಕಾರ ಸೌರ ಅಳವಡಿಕೆಗೆ ಉದಾರ ಸಬ್ಸಿಡಿಗಳನ್ನು ನೀಡುತ್ತದೆ. ಪೂರ್ವೋದಯ ಪ್ರತಿ ಅರ್ಜಿಯನ್ನು ಪ್ರಾರಂಭದಿಂದ ಹಣ ಲಭ್ಯವಾಗುವವರೆಗೆ ಮೇಲ್ವಿಚಾರಣೆ ಮಾಡುತ್ತದೆ.",
      tab_res: "ವಸತಿ · PM ಸೂರ್ಯ ಘರ್",
      tab_agri: "ಕೃಷಿ · PM-KUSUM",
      res_title: "PM ಸೂರ್ಯ ಘರ್: ಉಚಿತ ವಿದ್ಯುತ್ ಯೋಜನೆ",
      res_desc: "ಭಾರತ ಸರ್ಕಾರ ಪ್ರಾರಂಭಿಸಿದ ಈ ಯೋಜನೆ, ನಿಮ್ಮ ಮನೆಯಲ್ಲಿ ಛಾವಣಿಯ ಸೌರ ಸ್ಥಾಪನೆಯ ನಂತರ ಸಬ್ಸಿಡಿಯನ್ನು ನೇರವಾಗಿ ನಿಮ್ಮ ಬ್ಯಾಂಕ್ ಖಾತೆಗೆ ನೀಡುತ್ತದೆ.",
      slab_labels: ["1 kW ವರೆಗೆ", "2 kW ವರೆಗೆ", "3 kW ಮತ್ತು ಅಧಿಕ"],
      slab_notes: ["₹30,000 ಪ್ರತಿ kW", "₹30,000 ಪ್ರತಿ kW", "ಗರಿಷ್ಠ ಮಿತಿ"],
      res_bullets: [
        "ವಸತಿ ಮನೆಗಳ ಛಾವಣಿ PV ವ್ಯವಸ್ಥೆಗಳಿಗೆ ಅನ್ವಯವಾಗುತ್ತದೆ.",
        "ಅರ್ಹ ಕುಟುಂಬಗಳಿಗೆ ತಿಂಗಳಿಗೆ 300 ಯೂನಿಟ್ ಉಚಿತ ವಿದ್ಯುತ್.",
        "ಕಮಿಷನಿಂಗ್ ಮತ್ತು ಪರಿಶೀಲನೆಯ ನಂತರ ನೇರ ಬ್ಯಾಂಕ್ ವರ್ಗಾವಣೆ.",
        "ನಮ್ಮ ತಂಡ ಅಧಿಕೃತ ರಾಷ್ಟ್ರೀಯ ಪೋರ್ಟಲ್‌ನಲ್ಲಿ ಅರ್ಜಿ ಸಲ್ಲಿಸುತ್ತದೆ.",
      ],
      read_official: "ಅಧಿಕೃತ ಯೋಜನೆ ಓದಿ",
      savings_label: "ನಿಮ್ಮ ಉಳಿತಾಯ",
      savings_amount: "₹78,000",
      savings_note: "PM ಸೂರ್ಯ ಘರ್ ಅಡಿಯಲ್ಲಿ 3 kW+ ವಸತಿ ಛಾವಣಿಯ ಮೇಲೆ ಗರಿಷ್ಠ ಕೇಂದ್ರ ಸಬ್ಸಿಡಿ. ರಾಜ್ಯ ಪ್ರೋತ್ಸಾಹಕಗಳು ಹೆಚ್ಚುವರಿ ಇರಬಹುದು.",
      example_label: "ಉದಾಹರಣೆ · 3 kW ವ್ಯವಸ್ಥೆ",
      example_cost: "ವ್ಯವಸ್ಥೆ ವೆಚ್ಚ",
      example_subsidy: "ಸಬ್ಸಿಡಿ",
      example_pay: "ನೀವು ಪಾವತಿಸಿ",
      agri_title: "PM-KUSUM ಯೋಜನೆ",
      agri_desc: "ರೈತರಿಗೆ ಸೌರ ಪಂಪ್‌ಗಳ ಸ್ಥಾಪನೆ ಮತ್ತು ಕೃಷಿ ಫೀಡರ್‌ಗಳ ಸೌರೀಕರಣಕ್ಕೆ ಸಹಾಯ — ಡೀಸೆಲ್ ಅವಲಂಬನೆ ಕಡಿಮೆ ಮಾಡಿ ವಿಶ್ವಾಸಾರ್ಹ ಹಗಲಿನ ವಿದ್ಯುತ್.",
      agri_components: [
        { k: "ಕಾಂ-A", v: "ಬೀಡು ಭೂಮಿಯಲ್ಲಿ ಗ್ರಿಡ್-ಸಂಪರ್ಕಿತ ಸೌರ ಸ್ಥಾವರಗಳು" },
        { k: "ಕಾಂ-B", v: "ಸ್ವತಂತ್ರ ಸೌರ ನೀರಿನ ಪಂಪ್‌ಗಳು" },
        { k: "ಕಾಂ-C", v: "ಇರುವ ಗ್ರಿಡ್ ಪಂಪ್‌ಗಳ ಸೌರೀಕರಣ" },
      ],
      agri_bullets: [
        "ಸೌರ ಪಂಪ್‌ಗಳ ಮೇಲೆ 60%ವರೆಗೆ ಕೇಂದ್ರ + ರಾಜ್ಯ ಸಬ್ಸಿಡಿ.",
        "30% ಬ್ಯಾಂಕ್ ಸಾಲ; ರೈತನ ಕೊಡುಗೆ ಕೇವಲ ~10%.",
        "ಹೆಚ್ಚುವರಿ ಆದಾಯಕ್ಕಾಗಿ ಹೆಚ್ಚುವರಿ ವಿದ್ಯುತ್ತನ್ನು DISCOM ಗೆ ಮಾರಿ.",
        "ಸ್ಥಾಪನೆ ಮತ್ತು DBT ಸಹಾಯ ಪೂರ್ವೋದಯ‌ನಿಂದ.",
      ],
      farmer_benefit: "ರೈತನ ಪ್ರಯೋಜನ",
      farmer_amount: "60%ವರೆಗೆ ರಿಯಾಯಿತಿ",
      farmer_note: "ಸೌರ ಪಂಪ್ ಸ್ಥಾಪನೆಯ ಮೇಲೆ, ಹೆಚ್ಚುವರಿ ವಿದ್ಯುತ್ತನ್ನು ಗ್ರಿಡ್‌ಗೆ ಮಾರುವ ಮೂಲಕ ಆದಾಯ.",
      sub_individual: "ವೈಯಕ್ತಿಕ ಮನೆಗಳು",
      sub_society: "ಗುಂಪು ಹೌಸಿಂಗ್ ಸೊಸೈಟಿಗಳು",
      ind_box_label: "ನಿಮ್ಮ ಒಟ್ಟು ಉಳಿತಾಯ",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar ಅಡಿಯಲ್ಲಿ 3 kW+ ವಸತಿ ರೂಫ್‌ಟಾಪ್ ಸಿಸ್ಟಮ್‌ಗೆ ಗರಿಷ್ಠ ಕೇಂದ್ರೀಯ ಸಬ್ಸಿಡಿ. ರಾಜ್ಯದ ಪ್ರೋತ್ಸಾಹಗಳು ಹೆಚ್ಚುವರಿಯಾಗಿ ಲಭಿಸಬಹುದು.",
      ind_example_title: "ಉದಾಹರಣೆ · 3 kW ಸಿಸ್ಟಮ್",
      ind_example_rows: [
        { label: "ಸಿಸ್ಟಮ್ ವೆಚ್ಚ", value: "₹1,80,000" },
        { label: "ಸಬ್ಸಿಡಿ", value: "₹78,000" },
      ],
      ind_example_net_label: "ನೀವು ಪಾವತಿಸುವುದು",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "ನೀವು ಇನ್ನು ಪಡೆಯುವುದು",
      ind_extras: [
        "ಅರ್ಹ ಕುಟುಂಬಗಳಿಗೆ ತಿಂಗಳಿಗೆ 300 ಯೂನಿಟ್ ಉಚಿತ",
        "ಹೆಚ್ಚುವರಿ ಉತ್ಪಾದನೆಗೆ ನೆಟ್-ಮೀಟರಿಂಗ್ ಕ್ರೆಡಿಟ್",
        "25-ವರ್ಷದ ಪ್ಯಾನೆಲ್ ಪರ್ಫಾರ್ಮೆನ್ಸ್ ವಾರಂಟಿ",
        "Purvodaya ಮೂಲಕ ಉಚಿತ PM Surya Ghar ಪೋರ್ಟಲ್ ಫೈಲಿಂಗ್",
      ],
      ghs_title: "ಗುಂಪು ಹೌಸಿಂಗ್ ಸೊಸೈಟಿಗಳು (GHS / RWAs)",
      ghs_intro: "PMSGY ಅಡಿಯಲ್ಲಿ ಕೇಂದ್ರ ಸರ್ಕಾರ ಲಿಫ್ಟ್, ನೀರಿನ ಪಂಪ್, ಸ್ಟ್ರೀಟ್‌ಲೈಟ್, ಲಾಬಿ ಮತ್ತು ಕಾರಿಡಾರ್ ಲೈಟಿಂಗ್ ನಂತಹ ಸಾಮಾನ್ಯ ಸೌಲಭ್ಯಗಳಿಗೆ ಗ್ರಿಡ್-ಸಂಪರ್ಕಿತ ರೂಫ್‌ಟಾಪ್ ಸೋಲಾರ್‌ಗೆ ₹18,000 / kW ನೀಡುತ್ತದೆ.",
      ghs_structure_label: "ಸಬ್ಸಿಡಿ ರಚನೆ",
      ghs_structure: [
        "ಸ್ಥಾಪಿತ ಕಾಮನ್-ಏರಿಯಾ ಸೋಲಾರ್ ಸಾಮರ್ಥ್ಯದ ₹18,000 / kW.",
        "ಪ್ರತಿ ಸೊಸೈಟಿಗೆ ಗರಿಷ್ಠ 500 kW — ಅಂದರೆ ₹90 ಲಕ್ಷದವರೆಗೆ ಸಬ್ಸಿಡಿ.",
        "ಅನುಮತಿಸಿದ ಸಾಮರ್ಥ್ಯ = 3 kW × ಫ್ಲಾಟ್‌ಗಳ ಸಂಖ್ಯೆ.",
      ],
      ghs_eligibility_label: "ಅರ್ಹತಾ ಚೆಕ್‌ಲಿಸ್ಟ್",
      ghs_eligibility: [
        "CHS / RWA ತನ್ನ PAN ನೊಂದಿಗೆ ಕಾನೂನಾತ್ಮಕವಾಗಿ ನೋಂದಾಯಿಸಿರಬೇಕು.",
        "ಸೊಸೈಟಿಯ ಹೆಸರಿನಲ್ಲಿ ಸಕ್ರಿಯ ಕಾಮನ್-ಸರ್ವಿಸ್ ವಿದ್ಯುತ್ ಮೀಟರ್.",
        "ನೇರ ಸಬ್ಸಿಡಿ ವರ್ಗಾವಣೆಗೆ ಮೀಸಲಾದ ಸೊಸೈಟಿ ಬ್ಯಾಂಕ್ ಖಾತೆ.",
        "ಮೇಕ್-ಇನ್-ಇಂಡಿಯಾ (DCR-ಅನುಸಾರ) ಪ್ಯಾನೆಲ್‌ಗಳು ಮಾತ್ರ.",
      ],
      ghs_box_label: "ಸೊಸೈಟಿಯ ಒಟ್ಟು ಉಳಿತಾಯ",
      ghs_box_amount: "₹90 ಲಕ್ಷದವರೆಗೆ",
      ghs_box_caption: "ಒಂದು ಹೌಸಿಂಗ್ ಸೊಸೈಟಿಗೆ ಗರಿಷ್ಠ PMSGY ಸಬ್ಸಿಡಿ — ₹18,000 / kW ದರದಲ್ಲಿ, 500 kW ನಲ್ಲಿ ಗರಿಷ್ಠ.",
      ghs_example_title: "ಲೆಕ್ಕಾಚಾರ · 100-ಫ್ಲಾಟ್ ಸೊಸೈಟಿ",
      ghs_example_rows: [
        { label: "ಅನುಮತಿಸಿದ ಸಾಮರ್ಥ್ಯ (3 kW × 100)", value: "300 kW" },
        { label: "ಪ್ರಾಜೆಕ್ಟ್ ವೆಚ್ಚ (₹50,000 / kW)", value: "₹1.50 ಕೋಟಿ" },
        { label: "PMSGY ಸಬ್ಸಿಡಿ", value: "₹54 ಲಕ್ಷ" },
        { label: "ವಾರ್ಷಿಕ ಬಿಲ್ ಉಳಿತಾಯ", value: "~₹22 ಲಕ್ಷ" },
      ],
      ghs_example_net_label: "ಸೊಸೈಟಿಯ ನಿವ್ವಳ ವೆಚ್ಚ",
      ghs_example_net_value: "₹96 ಲಕ್ಷ",
      ghs_example_payback: "4–5 ವರ್ಷಗಳಲ್ಲಿ ಪೇಬ್ಯಾಕ್ · 20+ ವರ್ಷ ಉಚಿತ ವಿದ್ಯುತ್",
      ghs_documents_label: "ಸೊಸೈಟಿಯಿಂದ ಬೇಕಾದ ದಾಖಲೆಗಳು",
      ghs_documents: [
        "ಸೊಸೈಟಿ ನೋಂದಣಿ ಪ್ರಮಾಣಪತ್ರ + PAN",
        "ಕಳೆದ 3 ತಿಂಗಳ ಕಾಮನ್-ಏರಿಯಾ ವಿದ್ಯುತ್ ಬಿಲ್",
        "ಸೊಸೈಟಿ ಬ್ಯಾಂಕ್ ಪಾಸ್‌ಬುಕ್",
        "ಪ್ರಾಜೆಕ್ಟ್ ಅನುಮೋದನೆಯ AGM/EGM ನಿರ್ಣಯ",
        "ಕಟ್ಟಡ ಯೋಜನೆ ಮತ್ತು ರೂಫ್‌ಟಾಪ್ ಲೇಔಟ್",
      ],
    },
    calc: {
      eyebrow: "ಉಳಿತಾಯ ಕ್ಯಾಲ್ಕುಲೇಟರ್",
      title_pre: "ನೀವು ಎಷ್ಟು",
      title_hl: "ಉಳಿತಾಯ ಮಾಡುವಿರಿ",
      title_post: " ನೋಡಿ — ಪ್ರಾರಂಭಿಸಲು + ಮತ್ತು − ಒತ್ತಿರಿ.",
      subtitle: "ನಿಮ್ಮ ಉಪಕರಣಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡಿ, ಗಂಟೆಗಳನ್ನು ಸರಿಹೊಂದಿಸಿ — ನಾವು ಸರಿಯಾದ ಗಾತ್ರದ ಸೌರ ವ್ಯವಸ್ಥೆಯನ್ನು ಲೈವ್ ಸಬ್ಸಿಡಿ & ಮರುಪಾವತಿ ಅಂದಾಜಿನೊಂದಿಗೆ ಶಿಫಾರಸು ಮಾಡುತ್ತೇವೆ.",
      tab_res: "ವಸತಿ",
      tab_agri: "ಕೃಷಿ",
      appliances: "ನಿಮ್ಮ ಉಪಕರಣಗಳು",
      pincode_placeholder: "ಪಿನ್‌ಕೋಡ್",
      tariff: "ಟ್ಯಾರಿಫ್ ₹{rate}/ಯೂನಿಟ್",
      detected: "ಗುರುತಿಸಲಾಗಿದೆ",
      default_hours: "ಡೀಫಾಲ್ಟ್ {h}ಗಂ/ದಿನ",
      hours_day: "ಗಂಟೆ/ದಿನ",
      snapshot: "ನಿಮ್ಮ ಸೌರ ಸ್ನ್ಯಾಪ್‌ಶಾಟ್",
      daily: "ದೈನಂದಿನ",
      monthly_units: "ಮಾಸಿಕ ಯೂನಿಟ್‌ಗಳು",
      current_bill: "ಪ್ರಸ್ತುತ ಬಿಲ್",
      recommended: "ಶಿಫಾರಸು",
      investment: "ಹೂಡಿಕೆ ವಿವರ",
      system_cost: "ವ್ಯವಸ್ಥೆ ವೆಚ್ಚ",
      pm_subsidy: "PM ಸೂರ್ಯ ಘರ್ ಸಬ್ಸಿಡಿ",
      kusum_subsidy: "PM-KUSUM ಸಬ್ಸಿಡಿ (~30%)",
      net_investment: "ನಿವ್ವಳ ಹೂಡಿಕೆ",
      monthly_savings: "ಮಾಸಿಕ ಉಳಿತಾಯ",
      payback: "ಮರುಪಾವತಿ",
      co2_yr: "CO₂/ವರ್ಷ",
      cta_quote: "ನನ್ನ {kw} kW ಕೋಟ್",
      disclaimer: "ಅಂದಾಜುಗಳು 4 ಸೂರ್ಯ-ಗಂಟೆ/ದಿನ ಮತ್ತು ₹{rate}/ಯೂನಿಟ್ ಟ್ಯಾರಿಫ್ ಆಧಾರಿತ. ಅಂತಿಮ ವಿನ್ಯಾಸ ಉಚಿತ ಸೈಟ್ ಭೇಟಿಯ ನಂತರ.",
      appliance_labels: {
        fan: "ಸೀಲಿಂಗ್ ಫ್ಯಾನ್",
        tube: "ಟ್ಯೂಬ್ / LED ಲೈಟ್",
        ac: "ಏರ್ ಕಂಡಿಷನರ್ (1.5T)",
        fridge: "ಫ್ರಿಜ್",
        tv: "ಟಿವಿ",
        geyser: "ಗೀಸರ್",
        washer: "ವಾಷಿಂಗ್ ಮೆಷಿನ್",
        microwave: "ಮೈಕ್ರೊವೇವ್",
        computer: "ಕಂಪ್ಯೂಟರ್ / ಲ್ಯಾಪ್‌ಟಾಪ್",
        pump_home: "ನೀರಿನ ಪಂಪ್ (1 HP)",
        pump_3hp: "ನೀರಿನ ಪಂಪ್ (3 HP)",
        pump_5hp: "ನೀರಿನ ಪಂಪ್ (5 HP)",
        pump_75hp: "ನೀರಿನ ಪಂಪ್ (7.5 HP)",
        pump_10hp: "ಸಬ್‌ಮರ್ಸಿಬಲ್ (10 HP)",
        lights: "ಕೃಷಿ ದೀಪ",
        chaffcutter: "ಚಾಫ್ ಕಟರ್",
      },
    },
    workflow: {
      eyebrow: "ನಮ್ಮ ಪ್ರಕ್ರಿಯೆ",
      title_pre: "ಆರು ಹಂತಗಳು.",
      title_hl: "ಶೂನ್ಯ",
      title_post: "ತೊಂದರೆ.",
      subtitle: "ಮೊದಲ ಕರೆಯಿಂದ 25 ವರ್ಷಗಳ ಉಳಿತಾಯದವರೆಗೆ, ಸಂಪೂರ್ಣ ಪ್ರಯಾಣವನ್ನು ಪೂರ್ವೋದಯ ನಿರ್ವಹಿಸುತ್ತದೆ. ಹೀಗೆ ನಡೆಯುತ್ತದೆ:",
      activities: "ಚಟುವಟಿಕೆಗಳು",
      deliverables: "ತಲುಪಿಸುವಿಕೆಗಳು",
      outcome: "ಫಲಿತಾಂಶ",
      steps: [
        { title: "ಸೈಟ್ ಭೇಟಿ ಮತ್ತು ಕಾರ್ಯಸಾಧ್ಯತೆ", activities: "ನೆರಳು ವಿಶ್ಲೇಷಣೆ, ಛಾವಣಿ ಪರಿಶೀಲನೆ, ಲೋಡ್ ಅಧ್ಯಯನ, ವಿದ್ಯುತ್ ಬಿಲ್ ಪರಿಶೀಲನೆ.", deliverables: "ವ್ಯವಸ್ಥೆ ಗಾತ್ರ ಮತ್ತು ಉತ್ಪಾದನೆ ಅಂದಾಜಿನೊಂದಿಗೆ ಕಾರ್ಯಸಾಧ್ಯತೆ ವರದಿ.", outcome: "ನಿಮ್ಮ ಛಾವಣಿ ಸೌರಕ್ಕೆ ಸಿದ್ಧವಿದೆ ಎಂಬ ವಿಶ್ವಾಸ." },
        { title: "ವಿನ್ಯಾಸ ಮತ್ತು ಪ್ರಸ್ತಾವನೆ", activities: "3D ಛಾವಣಿ ಲೇಔಟ್, ರಚನಾತ್ಮಕ + ವಿದ್ಯುತ್ SLD, ಪ್ಯಾನೆಲ್ ಮತ್ತು ಇನ್ವರ್ಟರ್ ಆಯ್ಕೆ.", deliverables: "BOM, ಟೈಮ್‌ಲೈನ್ ಮತ್ತು ಸಬ್ಸಿಡಿ ಅಂದಾಜಿನೊಂದಿಗೆ ವಿವರವಾದ ಪ್ರಸ್ತಾವನೆ.", outcome: "ಪಾರದರ್ಶಕ ಬೆಲೆ, ಯಾವುದೇ ಆಶ್ಚರ್ಯಗಳಿಲ್ಲ." },
        { title: "ಅನುಮೋದನೆ ಮತ್ತು ಸಬ್ಸಿಡಿ ಅರ್ಜಿ", activities: "ನೆಟ್-ಮೀಟರ್ ಅರ್ಜಿ, DISCOM ಸಂಪರ್ಕ, PM ಸೂರ್ಯ ಘರ್ / KUSUM ಫೈಲಿಂಗ್.", deliverables: "ನೆಟ್ ಮೀಟರಿಂಗ್ ಅನುಮೋದನೆ + ಸಬ್ಸಿಡಿ ನೋಂದಣಿ.", outcome: "ದಾಖಲೆಗಳು ಸಂಪೂರ್ಣವಾಗಿ ನಿರ್ವಹಿಸಲ್ಪಟ್ಟವು." },
        { title: "ಸ್ಥಾಪನೆ", activities: "ರಚನೆ ಅಳವಡಿಕೆ, ಪ್ಯಾನೆಲ್ ಇಡುವಿಕೆ, ಕೇಬಲಿಂಗ್, ಇನ್ವರ್ಟರ್ ಕಮಿಷನಿಂಗ್.", deliverables: "ಸಂಪೂರ್ಣ ಸ್ಥಾಪಿತ, ಲೇಬಲ್ ಮತ್ತು ಪರೀಕ್ಷಿತ ಛಾವಣಿ ವ್ಯವಸ್ಥೆ.", outcome: "ಸಾಮಾನ್ಯವಾಗಿ 5–7 ದಿನಗಳಲ್ಲಿ ವಿದ್ಯುತ್." },
        { title: "ಕಮಿಷನಿಂಗ್ ಮತ್ತು ಗೋ-ಲೈವ್", activities: "DISCOM ಪರಿಶೀಲನೆ, ನೆಟ್ ಮೀಟರ್ ಸ್ಥಾಪನೆ, ಆ್ಯಪ್ ಆನ್‌ಬೋರ್ಡಿಂಗ್.", deliverables: "ಹ್ಯಾಂಡೋವರ್ ಪ್ಯಾಕ್ + ಮಾನಿಟರಿಂಗ್ ಆ್ಯಪ್ ಲಾಗಿನ್.", outcome: "ನಿಮ್ಮ ಮೀಟರ್ ಹಿಂದಕ್ಕೆ ತಿರುಗಲು ಪ್ರಾರಂಭಿಸುತ್ತದೆ." },
        { title: "ಮಾರಾಟದ ನಂತರ ಮತ್ತು ಮೇಲ್ವಿಚಾರಣೆ", activities: "ದೂರಸ್ಥ ಕಾರ್ಯಕ್ಷಮತೆ ಮೇಲ್ವಿಚಾರಣೆ, ಕಾಲಕಾಲಕ್ಕೆ ಶುಚಿಗೊಳಿಸುವಿಕೆ, AMC ಬೆಂಬಲ.", deliverables: "ವಾರ್ಷಿಕ ಕಾರ್ಯಕ್ಷಮತೆ ವರದಿಗಳು, ಆದ್ಯತೆ ಸಹಾಯವಾಣಿ.", outcome: "25 ವರ್ಷಗಳ ಚಿಂತೆ-ಮುಕ್ತ ಸೂರ್ಯಕಿರಣ." },
      ],
    },
    faq: {
      eyebrow: "ಆಗಾಗ್ಗೆ ಕೇಳಲಾದ ಪ್ರಶ್ನೆಗಳು",
      title_pre: "ಎಲ್ಲರ ಪ್ರಶ್ನೆಗಳು —",
      title_hl: "ಪ್ರಾಮಾಣಿಕ ಉತ್ತರಗಳು.",
      items: [
        { q: "ಸೌರ ವ್ಯವಸ್ಥೆಗೆ ಎಷ್ಟು ಛಾವಣಿಯ ಜಾಗ ಬೇಕು?", a: "ಸಾಮಾನ್ಯ ನಿಯಮ: ಪ್ರತಿ 1 kW ಗೆ ~80–100 ಚ.ಅ. ನೆರಳು-ರಹಿತ ಪ್ರದೇಶ. 3 kW ಗೃಹ ವ್ಯವಸ್ಥೆ 300 ಚ.ಅ. ಛಾವಣಿಯಲ್ಲಿ ಸುಲಭವಾಗಿ ಕುಳಿತುಕೊಳ್ಳುತ್ತದೆ." },
        { q: "ಮಳೆಗಾಲದಲ್ಲಿ ಅಥವಾ ಮೋಡದ ದಿನಗಳಲ್ಲಿ ಸೌರ ಕೆಲಸ ಮಾಡುತ್ತದೆಯೇ?", a: "ಹೌದು. ಪ್ಯಾನೆಲ್‌ಗಳು ಹರಡಿದ ಬೆಳಕಿನಲ್ಲೂ (ಪೀಕ್‌ನ ~10–25%) ಉತ್ಪಾದಿಸುತ್ತವೆ. ನೆಟ್-ಮೀಟರಿಂಗ್ ಮೂಲಕ ಗ್ರಿಡ್‌ನಿಂದ ತೆಗೆದುಕೊಳ್ಳಬಹುದು." },
        { q: "PM ಸೂರ್ಯ ಘರ್ ಸಬ್ಸಿಡಿ ಪ್ರಕ್ರಿಯೆ ಏನು?", a: "ನೀವು ರಾಷ್ಟ್ರೀಯ ಪೋರ್ಟಲ್‌ನಲ್ಲಿ ನೋಂದಾಯಿಸುತ್ತೀರಿ, ನಾವು ಅರ್ಜಿ ಸಲ್ಲಿಸುತ್ತೇವೆ, MNRE-ನೋಂದಾಯಿತ ಮಾರಾಟಗಾರ (ಪೂರ್ವೋದಯ) ಮೂಲಕ ಸ್ಥಾಪನೆ, DISCOM ಪರಿಶೀಲನೆಯ ನಂತರ ನೇರ ಬ್ಯಾಂಕ್ ವರ್ಗಾವಣೆ." },
        { q: "ಸ್ಥಾಪನೆಗೆ ಎಷ್ಟು ಸಮಯ ಬೇಕು?", a: "ಪ್ರಮಾಣಿತ 3 kW ವಸತಿ ಸ್ಥಾಪನೆ 3–5 ದಿನಗಳು. ಅನುಮೋದನೆ ಸೇರಿದಂತೆ ಒಟ್ಟು 4–6 ವಾರಗಳು." },
        { q: "ಯಾವ ನಿರ್ವಹಣೆ ಬೇಕು?", a: "ಬಹಳ ಕಡಿಮೆ. ಧೂಳಿನ ಪ್ರದೇಶಗಳಲ್ಲಿ 1–2 ತಿಂಗಳಿಗೊಮ್ಮೆ ಶುಚಿಗೊಳಿಸುವಿಕೆ ಮತ್ತು ವಾರ್ಷಿಕ ಪರಿಶೀಲನೆ. ಪ್ರತಿ ಸ್ಥಾಪನೆಯೊಂದಿಗೆ ಉಚಿತ 1 ವರ್ಷದ AMC." },
        { q: "ರೈತರು ನಿಜವಾಗಿಯೂ PM-KUSUM ನಿಂದ ಆದಾಯ ಗಳಿಸಬಹುದೇ?", a: "ಖಂಡಿತವಾಗಿ. ಕಾಂ-A ಮತ್ತು C ಅಡಿಯಲ್ಲಿ ಹೆಚ್ಚುವರಿ ಸೌರ ವಿದ್ಯುತ್ತನ್ನು DISCOM ನಿಗದಿತ ದರದಲ್ಲಿ ಖರೀದಿಸುತ್ತದೆ — ಪಂಪ್‌ಗಳಿಗೆ ಉಚಿತ ಹಗಲಿನ ವಿದ್ಯುತ್ ಜೊತೆಗೆ ಸ್ಥಿರ ಹೆಚ್ಚುವರಿ ಆದಾಯ." },
      ],
    },
    footer: {
      tag: "ಭವಿಷ್ಯವನ್ನು ಪ್ರಕಾಶಮಾನಗೊಳಿಸಿ",
      desc: "ಭಾರತೀಯ ಮನೆ ಮತ್ತು ಜಮೀನುಗಳಿಗೆ ಬಿಲ್ಲುಗಳನ್ನು ಕಡಿಮೆ ಮಾಡಲು ಮತ್ತು ಇಂಧನ ಪರಿವರ್ತನೆಯನ್ನು ವೇಗಗೊಳಿಸಲು ಸಹಾಯ ಮಾಡುವ MNRE-ನೋಂದಾಯಿತ ಛಾವಣಿಯ ಸೌರ EPC ಕಂಪನಿ.",
      explore: "ಅನ್ವೇಷಿಸಿ",
      contact: "ಸಂಪರ್ಕ",
      rights: "© {year} ಪೂರ್ವೋದಯ ಸೋಲಾರ್. ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.",
      built: "ಸ್ವಚ್ಛ ಭಾರತಕ್ಕಾಗಿ ☀ ದೊಂದಿಗೆ ನಿರ್ಮಿಸಲಾಗಿದೆ.",
    },
    lead: {
      badge: "ಉಚಿತ ಮತ್ತು ಬಾಧ್ಯತೆ ಇಲ್ಲ",
      title_default: "ಉಚಿತ ಛಾವಣಿ ಕೋಟ್ ಪಡೆಯಿರಿ",
      subtitle_default: "ನಿಮ್ಮ ವಿವರಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳಿ — ಪೂರ್ವೋದಯ ತಜ್ಞ 24 ಗಂಟೆಗಳಲ್ಲಿ ಕರೆ ಮಾಡುತ್ತಾರೆ.",
      title_calc: "ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಕೋಟ್ ಪಡೆಯಿರಿ",
      title_idle: "ಇನ್ನೂ ನೋಡುತ್ತಿದ್ದೀರಾ? ನಾವು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.",
      subtitle_idle: "ನಿಮ್ಮ ವಿವರಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳಿ — ನಿಮ್ಮ ಫೋನಿಗೆ ಕಸ್ಟಮ್ ಸೌರ ಉಳಿತಾಯ ಕಳುಹಿಸುತ್ತೇವೆ.",
      full_name: "ಪೂರ್ಣ ಹೆಸರು *",
      name_ph: "ನಿಮ್ಮ ಹೆಸರು",
      phone: "ಫೋನ್ *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "ಪಿನ್‌ಕೋಡ್",
      email: "ಇಮೇಲ್",
      email_ph: "you@example.com",
      i_need: "ನನಗೆ ಸೌರ ಬೇಕು",
      seg_home: "ಮನೆ / ವಸತಿ",
      seg_farm: "ಜಮೀನು / ಕೃಷಿ",
      seg_commercial: "ವಾಣಿಜ್ಯ / ಕೈಗಾರಿಕಾ",
      anything_else: "ಬೇರೇನಾದರೂ?",
      msg_ph: "ಛಾವಣಿ ಗಾತ್ರ, ಪ್ರಸ್ತುತ ಬಿಲ್, ಇತ್ಯಾದಿ.",
      submit: "ನನಗೆ ಉಚಿತ ಕಾಲ್-ಬ್ಯಾಕ್ ಬೇಕು",
      submitting: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…",
      thanks_title: "ನೀವು ನಮ್ಮ ಪಟ್ಟಿಯಲ್ಲಿದ್ದೀರಿ!",
      thanks_desc: "ಸೌರ ಸಲಹೆಗಾರ ಶೀಘ್ರದಲ್ಲೇ ಸಂಪರ್ಕಿಸುತ್ತಾರೆ.",
      toast_ok: "ಧನ್ಯವಾದಗಳು! ಶೀಘ್ರದಲ್ಲೇ ಕರೆ ಮಾಡುತ್ತೇವೆ.",
      toast_required: "ಹೆಸರು ಮತ್ತು ಫೋನ್ ಅಗತ್ಯ",
      toast_fail: "ಕಳುಹಿಸಲಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
      consent: "ಸಲ್ಲಿಸುವ ಮೂಲಕ, ಪೂರ್ವೋದಯ ಸೋಲಾರ್‌ನೊಂದಿಗೆ ಸಂಪರ್ಕಕ್ಕೆ ಒಪ್ಪುತ್ತೀರಿ.",
    },
    whatsapp: { label: "ನಮ್ಮೊಂದಿಗೆ ಚಾಟ್ ಮಾಡಿ" },
    common: { language: "ಭಾಷೆ" },
    ts: {
        eyebrow: "ಪುರ್ವೋದಯ ಏಕೆ",
        title_pre: "ಗಂಭೀರ ಪಾಲುದಾರ —",
        title_hl: "ಭಾರತದ ಸೌರ ಪರಿವರ್ತನೆಗಾಗಿ",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "ಎನರ್ಜಿ ಟ್ರಾನ್ಸಿಷನ್ ಪಾರ್ಟ್‌ನರ್",
        p1_title: "ಭಾರತದ ಸೌರ-ಆಧಾರಿತ ಶಕ್ತಿ ಪರಿವರ್ತನೆಯ ವಿಶ್ವಾಸಾರ್ಹ ಪಾಲುದಾರ",
        p1_desc: "ದೇಶದ ವಸತಿ, ಹೌಸಿಂಗ್ ಸೊಸೈಟಿ ಮತ್ತು ಕಾರ್ಪೊರೇಟ್ ಶುದ್ಧ ಶಕ್ತಿ ಪ್ರಯಾಣಕ್ಕಾಗಿ ವಿಶೇಷವಾಗಿ ರೂಪಿಸಲಾಗಿದೆ — ದೀರ್ಘಕಾಲಿಕ ಕಾರ್ಯಕ್ಷಮತೆಗಾಗಿ, ತಾತ್ಕಾಲಿಕ ಪ್ರದರ್ಶನಕ್ಕಾಗಿ ಅಲ್ಲ.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM ಫೈಲರ್",
        p2_title: "ಕೇಂದ್ರ ಸೌರ ಸಬ್ಸಿಡಿಗಳ ಅಧಿಕೃತ ಫೈಲರ್",
        p2_desc: "ನಾವು ವೈಯಕ್ತಿಕ ಮನೆಗಳು ಮತ್ತು ಹೌಸಿಂಗ್ ಸೊಸೈಟಿಗಳಿಗೆ PM Surya Ghar ಮತ್ತು ರೈತರಿಗೆ PM-KUSUM ಸಲ್ಲಿಸುತ್ತೇವೆ — ಎಲ್ಲಾ ಕಾಗದದ ಕೆಲಸವನ್ನು ನಮ್ಮ ತಂಡ ನಿರ್ವಹಿಸುತ್ತದೆ, ಯಾವುದೇ ಮಧ್ಯವರ್ತಿಗಳಿಲ್ಲ.",
        p3_eyebrow: "ಎಂಡ್-ಟು-ಎಂಡ್ EPC + 25-ವರ್ಷದ O&M",
        p3_title: "ಒಬ್ಬ ಜವಾಬ್ದಾರಿಯುತ ಪಾಲುದಾರ — ವಿನ್ಯಾಸದಿಂದ ಸ್ವಿಚ್-ಆನ್ ವರೆಗೆ ಮತ್ತು 25-ವರ್ಷದ ಆರೈಕೆ",
        p3_desc: "MNRE-ಅನುಸಾರ ವಿನ್ಯಾಸ, Tier-1 ಮಾಡ್ಯೂಲ್‌ಗಳು, ಪ್ರಮಾಣೀಕೃತ ಸ್ಥಾಪನಾ ತಂಡಗಳು, DISCOM ಸಂಪರ್ಕ, ಮತ್ತು 25-ವರ್ಷದ ಕಾರ್ಯಕ್ಷಮತೆಯ ಭರವಸೆ — ಎಲ್ಲವೂ ಒಂದೇ ಸೂರಿನ ಕೆಳಗೆ.",
        promise_label: "ನಮ್ಮ ಭರವಸೆ",
        promise1: "Tier-1 ಮಾಡ್ಯೂಲ್‌ಗಳು · 25-ವರ್ಷದ ಪರ್ಫಾರ್ಮೆನ್ಸ್ ವಾರಂಟಿ",
        promise2: "MNRE-ಅನುಸಾರ ಎಂಜಿನಿಯರಿಂಗ್ ಮತ್ತು ಸುರಕ್ಷತಾ ಅಭ್ಯಾಸಗಳು",
        promise3: "ಪಾರದರ್ಶಕ ಕೋಟ್‌ಗಳು · ಯಾವುದೇ ಮರೆಮಾಚಿದ ವೆಚ್ಚಗಳಿಲ್ಲ",
        promise4: "ತ್ರೈಮಾಸಿಕ ಕಾರ್ಯಕ್ಷಮತೆ ಮತ್ತು ಸ್ವಚ್ಛತಾ ವರದಿಗಳು",
      },
    res: {
        hero_badge: "ವಸತಿ ರೂಫ್‌ಟಾಪ್ ಸೋಲಾರ್",
        hero_title_a: "ನಿಮ್ಮ AC, ಫ್ರಿಡ್ಜ್, ಗೀಸರ್ —",
        hero_title_b: "ಸೂರ್ಯನಿಂದ ಉಚಿತವಾಗಿ.",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "ಉಚಿತ ರೂಫ್‌ಟಾಪ್ ಕೋಟ್ ಪಡೆಯಿರಿ",
        cta_calc: "ನಿಮ್ಮ ಉಳಿತಾಯವನ್ನು ಅಂದಾಜು ಮಾಡಿ",
        stat_subsidy: "ಗರಿಷ್ಠ ಕೇಂದ್ರೀಯ ಸಬ್ಸಿಡಿ",
        stat_reduction: "ಬಿಲ್ ಕಡಿತ",
        stat_payback: "ಸಾಮಾನ್ಯ ಪೇಬ್ಯಾಕ್",
        stat_warranty: "ಪರ್ಫಾರ್ಮೆನ್ಸ್ ವಾರಂಟಿ",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "ಮುಖಪುಟಕ್ಕೆ ಹಿಂದಿರುಗಿ",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "ವಾಣಿಜ್ಯ ಮತ್ತು ಕೈಗಾರಿಕಾ ಸೋಲಾರ್",
        hero_title_a: "Power your business with the",
        hero_title_hl: "ಸೂರ್ಯನಿಂದ",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "ವ್ಯಾಪಾರ ಪ್ರಸ್ತಾಪವನ್ನು ವಿನಂತಿಸಿ",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "ಬಿಲ್ ಕಡಿತ",
        stat_payback: "ಸಾಮಾನ್ಯ ಪೇಬ್ಯಾಕ್",
        stat_warranty: "ಪರ್ಫಾರ್ಮೆನ್ಸ್ ವಾರಂಟಿ",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "ಮುಖಪುಟಕ್ಕೆ ಹಿಂದಿರುಗಿ",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "ಹೌಸಿಂಗ್ ಸೊಸೈಟಿ ಸೋಲಾರ್",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "ಸೊಸೈಟಿ ಭೇಟಿಯನ್ನು ವಿನಂತಿಸಿ",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "ಗರಿಷ್ಠ ಕೇಂದ್ರೀಯ ಸಬ್ಸಿಡಿ",
        stat_reduction: "ಬಿಲ್ ಕಡಿತ",
        stat_payback: "ಸಾಮಾನ್ಯ ಪೇಬ್ಯಾಕ್",
        stat_warranty: "ಪರ್ಫಾರ್ಮೆನ್ಸ್ ವಾರಂಟಿ",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "ಮುಖಪುಟಕ್ಕೆ ಹಿಂದಿರುಗಿ",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },

  gu: {
    nav: {
      services: "સેવાઓ",
      how: "આ કેવી રીતે કામ કરે છે",
      subsidy: "સબસિડી",
      savings: "બચત",
      process: "પ્રક્રિયા",
      faq: "પ્રશ્નો",
      admin: "એડમિન",
      cta: "મફત કોટ મેળવો",
      menu: "મેનૂ",
      residential: "રહેણાંક",
      commercial: "વાણિજ્યિક",
      housing: "હાઉસિંગ સોસાયટીઓ",
    },
    hero: {
      badge: "MNRE સૂચિબદ્ધ · PM સૂર્ય ઘર તૈયાર",
      title_pre: "તમારા ઘરને",
      title_highlight: "સૂર્યપ્રકાશ",
      title_post1: "થી ચલાવો અને તમારું",
      title_post2: "વીજળી બિલ ઘટાડો.",
      subtitle_pre:
        "પૂર્વોદય સોલાર સમગ્ર ભારતમાં ઘરો અને ખેતરો માટે છત પરની સોલાર સિસ્ટમો ડિઝાઇન, ઇન્સ્ટોલ અને સેવા આપે છે —",
      subtitle_and: "અને",
      subtitle_post: "સબસિડી માટે સંપૂર્ણ સહાય સાથે.",
      cta_quote: "મફત રૂફટોપ કોટ મેળવો",
      cta_calc: "તમારી બચતની ગણતરી કરો",
      stat1_k: "₹78,000",
      stat1_v: "મહત્તમ સબસિડી (આવાસીય)",
      stat2_k: "25 વર્ષ",
      stat2_v: "પેનલ કામગીરી",
      stat3_k: "4–5 વર્ષ",
      stat3_v: "સામાન્ય ચુકવણી",
      live_label: "સરેરાશ માસિક બચત",
      live_value: "₹4,120",
      live_caption: "સામાન્ય 3 kW ઘર માટે PM સૂર્ય ઘર સબસિડી પછી",
      this_month: "આ વર્ષ",
      saved: "સરેરાશ ઘરની બચત",
      co2: "t CO₂ વાર્ષિક ઘટાડો",
    },
    trust: { heading: "ટિયર-1 પેનલ અને ઇન્વર્ટર બ્રાન્ડ્સ સાથે કામ" },
    services: {
            cta_explore: "લાભો જાણો",
      cta_street_b2b: "B2B જમાવટ માટે વાત કરો →",
eyebrow: "સંપૂર્ણ સોલાર સોલ્યુશન",
      title_pre: "એક ભાગીદાર.",
      title_hl: "દરેક સોલાર જરૂરિયાત",
      title_post: "આવરી લેવાય.",
      subtitle: "છતથી કારખાનાં, EV ચાર્જરથી સ્ટ્રીટ લાઇટ સુધી — પૂર્વોદય સોલાર તમારી જગ્યા અને બજેટ અનુસાર સંપૂર્ણ ક્લીન-એનર્જી સોલ્યુશન પ્રદાન કરે છે.",
      cta: "આ સેવા જુઓ",
      items: [
        { key: "rooftop", title: "આવાસીય રૂફટોપ સોલાર", desc: "ઘરો માટે MNRE-સૂચિબદ્ધ રૂફટોપ ઇન્સ્ટોલેશન — PM સૂર્ય ઘર યોજના હેઠળ ₹78,000 સુધી કેન્દ્રીય સબસિડી મેળવો.", highlight: "PM સૂર્ય ઘર યોજના" },
        { key: "commercial", title: "વાણિજ્યિક અને ઔદ્યોગિક સોલાર", desc: "કારખાનાં, ઑફિસ, ગોદામો માટે MW સ્કેલ રૂફટોપ અને ગ્રાઉન્ડ-માઉન્ટ સિસ્ટમ. વીજળી ખર્ચ 60% ઘટાડો અને 40% ત્વરિત અવમૂલ્યન મેળવો.", highlight: "40% સુધી ટેક્સ બચત" },
        { key: "ev", title: "EV ચાર્જિંગ સોલ્યુશન", desc: "ઘરો, ઑફિસ, મોલ્સ અને હાઈવે પ્લાઝા માટે સોલાર-સંચાલિત AC અને DC ફાસ્ટ EV ચાર્જર — શૂન્ય-ખર્ચ ડ્રાઇવિંગ માટે રૂફટોપ સોલાર સાથે જોડો.", highlight: "AC 7kW · DC 60kW+" },
        { key: "bess", title: "બેટરી એનર્જી સ્ટોરેજ", desc: "બેકઅપ પાવર, પીક શેવિંગ અને સંપૂર્ણ ઑફ-ગ્રીડ સ્વતંત્રતા માટે લિથિયમ-આયન BESS. 5 kWh ઘરથી 1 MWh+ વાણિજ્યિક બેંક સુધી.", highlight: "10 વર્ષ વોરંટી" },
        { key: "street", title: "સોલાર સ્ટ્રીટ લાઇટિંગ", desc: "સોસાયટી, કેમ્પસ, હાઈવે અને ગ્રામીણ રસ્તાઓ માટે સ્ટેન્ડ-અલોન ઑલ-ઇન-વન સોલાર સ્ટ્રીટ લાઇટ — શૂન્ય વાયરિંગ, શૂન્ય બિલ.", highlight: "ઑટો ડસ્ક-ટુ-ડોન" },
        { key: "society", title: "હાઉસિંગ સોસાયટી સોલાર", desc: "એપાર્ટમેન્ટ સંકુલ માટે ગ્રુપ રૂફટોપ અને કોમન-એરિયા સોલાર — વ્યક્તિગત ફ્લેટ માટે PM સૂર્ય ઘર લાભ + સોસાયટી બચત.", highlight: "RWA-ફ્રેન્ડલી" },
        { key: "heater", title: "સોલાર વોટર હીટર", desc: "ઘરો, હોસ્ટેલ અને હોટેલ માટે ઇવેક્યુએટેડ ગ્લાસ-ટ્યુબ સોલાર વોટર હીટર — 365 દિવસ ગરમ પાણી, ગીઝર બિલ સંપૂર્ણ બંધ.", highlight: "100–500 LPD" },
      ],
    },
    how: {
      eyebrow: "રૂફટોપ સોલાર કેવી રીતે કામ કરે છે",
      title_pre: "",
      title_hl: "સૂર્યપ્રકાશ",
      title_mid: "થી બચત સુધી — ચાર સરળ પગલાંમાં.",
      subtitle: "રૂફટોપ સોલાર જાદુ નથી, સ્માર્ટ મીટરિંગ સાથે સરળ ભૌતિકશાસ્ત્ર છે. દરરોજ આપોઆપ આ થાય છે:",
      steps: [
        { title: "સૂર્યપ્રકાશ પેનલ પર પડે છે", desc: "ઉચ્ચ કાર્યક્ષમ મોનો PERC પેનલ દિવસનો પ્રકાશ શોષીને DC વીજળી બનાવે છે." },
        { title: "ઇન્વર્ટર AC માં રૂપાંતરિત કરે છે", desc: "96% કાર્યક્ષમતા પર DC ને AC માં બદલતું સ્માર્ટ ગ્રીડ-ટાઇડ ઇન્વર્ટર." },
        { title: "તમારું ઘર પહેલા તેનો ઉપયોગ કરે છે", desc: "પંખા, લાઇટ, AC, પંપ — દિવસ દરમિયાન સૂર્યમાંથી, ગ્રીડ વપરાશ નાટ્યાત્મક રીતે ઘટે છે." },
        { title: "નેટ મીટર બાકીનું જમા કરે છે", desc: "વધારાની એકમો ગ્રીડ પર પરત જાય છે. મહિનાના અંતે માત્ર નેટ વપરાશનું બિલ." },
      ],
      kpis: [
        { k: "70–90%", v: "ઇન્સ્ટોલેશન પછી માસિક વીજ બિલમાં ઘટાડો." },
        { k: "₹0 EMI", v: "ભાગીદાર બેંકો દ્વારા લવચીક નાણાકીય સહાય." },
        { k: "25 વર્ષ", v: "પેનલ આઉટપુટ વોરંટી (ન્યૂનતમ 80%)." },
      ],
    },
    subsidy: {
      eyebrow: "સરકારી સબસિડી",
      title_pre: "મેળવો",
      title_hl: "₹78,000",
      title_post: " સુધીની છૂટ — પેપરવર્ક અમે સંભાળીશું.",
      subtitle: "ઘર હોય કે ખેતર, ભારત સરકાર સોલાર અપનાવવા માટે ઉદાર સબસિડી આપે છે. પૂર્વોદય દરેક અરજીને શરૂઆતથી ફંડ મળે ત્યાં સુધી ટ્રેક કરે છે.",
      tab_res: "આવાસીય · PM સૂર્ય ઘર",
      tab_agri: "ખેતી · PM-KUSUM",
      res_title: "PM સૂર્ય ઘર: મફત વીજ યોજના",
      res_desc: "ભારત સરકાર દ્વારા શરૂ કરાયેલી આ યોજના, તમારા ઘરે રૂફટોપ સોલાર ઇન્સ્ટોલ થયા પછી સબસિડી સીધી તમારા બેંક ખાતામાં મોકલે છે.",
      slab_labels: ["1 kW સુધી", "2 kW સુધી", "3 kW અને વધુ"],
      slab_notes: ["₹30,000 દર kW", "₹30,000 દર kW", "મહત્તમ મર્યાદા"],
      res_bullets: [
        "આવાસીય ઘરો પર રૂફટોપ PV સિસ્ટમ માટે લાગુ.",
        "લાયક પરિવારોને મહિને 300 એકમ મફત વીજળી.",
        "કમિશનિંગ અને તપાસ પછી સીધું બેંક ટ્રાન્સફર.",
        "અમારી ટીમ રાષ્ટ્રીય પોર્ટલ પર અધિકૃત અરજી કરે છે.",
      ],
      read_official: "અધિકૃત યોજના વાંચો",
      savings_label: "તમારી બચત",
      savings_amount: "₹78,000",
      savings_note: "PM સૂર્ય ઘર હેઠળ 3 kW+ આવાસીય રૂફટોપ પર મહત્તમ કેન્દ્રીય સબસિડી. રાજ્ય પ્રોત્સાહનો વધારાના હોઈ શકે છે.",
      example_label: "ઉદાહરણ · 3 kW સિસ્ટમ",
      example_cost: "સિસ્ટમ ખર્ચ",
      example_subsidy: "સબસિડી",
      example_pay: "તમે ચૂકવો",
      agri_title: "PM-KUSUM યોજના",
      agri_desc: "ખેડૂતોને સોલાર પંપ ઇન્સ્ટોલ અને ખેતી ફીડરને સૌરૈ ઊર્જાથી ચલાવવામાં મદદ કરે છે — ડીઝલ પર નિર્ભરતા ઘટાડીને વિશ્વસનીય દિવસની વીજળી આપે છે.",
      agri_components: [
        { k: "કોમ્પ-A", v: "બંજર જમીન પર ગ્રીડ-જોડાયેલા સોલાર પ્લાન્ટ" },
        { k: "કોમ્પ-B", v: "સ્વતંત્ર સોલાર પાણી પંપ" },
        { k: "કોમ્પ-C", v: "હાલના ગ્રીડ પંપનું સૌરૈ ઊર્જાકરણ" },
      ],
      agri_bullets: [
        "સોલાર પંપ પર 60% સુધી કેન્દ્ર + રાજ્ય સબસિડી.",
        "30% બેંક લોન; ખેડૂતનો ફાળો ફક્ત ~10%.",
        "વધારાની આવક માટે વધારાની વીજળી DISCOM ને વેચો.",
        "ઇન્સ્ટોલેશન અને DBT સહાય પૂર્વોદય દ્વારા.",
      ],
      farmer_benefit: "ખેડૂતનો લાભ",
      farmer_amount: "60% સુધી છૂટ",
      farmer_note: "સોલાર પંપ ઇન્સ્ટોલેશન પર, તેમજ વધારાની વીજળી ગ્રીડ ને વેચીને આવકનો સ્રોત.",
      sub_individual: "વ્યક્તિગત ઘરો",
      sub_society: "ગ્રુપ હાઉસિંગ સોસાયટીઓ",
      ind_box_label: "તમારી કુલ બચત",
      ind_box_amount: "₹78,000",
      ind_box_caption: "PM Surya Ghar હેઠળ 3 kW+ રહેણાંક રૂફટોપ સિસ્ટમ પર મહત્તમ કેન્દ્રીય સબસિડી. રાજ્યના પ્રોત્સાહનો વધારામાં મળી શકે છે.",
      ind_example_title: "ઉદાહરણ · 3 kW સિસ્ટમ",
      ind_example_rows: [
        { label: "સિસ્ટમ ખર્ચ", value: "₹1,80,000" },
        { label: "સબસિડી", value: "₹78,000" },
      ],
      ind_example_net_label: "તમે ચૂકવશો",
      ind_example_net_value: "₹1,02,000",
      ind_extras_label: "તમને બીજું મળશે",
      ind_extras: [
        "પાત્ર પરિવારોને દર મહિને 300 યુનિટ મફત",
        "વધારાના ઉત્પાદન માટે નેટ-મીટરિંગ ક્રેડિટ",
        "25-વર્ષની પેનલ પરફોર્મન્સ વોરંટી",
        "Purvodaya દ્વારા મફત PM Surya Ghar પોર્ટલ ફાઇલિંગ",
      ],
      ghs_title: "ગ્રુપ હાઉસિંગ સોસાયટીઓ (GHS / RWAs)",
      ghs_intro: "PMSGY હેઠળ કેન્દ્ર સરકાર લિફ્ટ, પાણીના પંપ, સ્ટ્રીટલાઇટ, લોબી અને કોરિડોર લાઇટિંગ જેવી સામાન્ય સુવિધાઓ માટે ગ્રિડ-કનેક્ટેડ રૂફટોપ સોલાર પર ₹18,000 / kW આપે છે.",
      ghs_structure_label: "સબસિડી માળખું",
      ghs_structure: [
        "સ્થાપિત કોમન-એરિયા સોલાર ક્ષમતાનું ₹18,000 / kW.",
        "દરેક સોસાયટી માટે મહત્તમ 500 kW — એટલે કે ₹90 લાખ સુધી સબસિડી.",
        "મંજૂર ક્ષમતા = 3 kW × ફ્લેટની સંખ્યા.",
      ],
      ghs_eligibility_label: "પાત્રતા તપાસ",
      ghs_eligibility: [
        "CHS / RWA તેના PAN સાથે કાનૂની રીતે નોંધાયેલ હોવી જોઈએ.",
        "સોસાયટીના નામે સક્રિય કોમન-સર્વિસ વીજળી મીટર.",
        "સીધી સબસિડી ટ્રાન્સફર માટે સમર્પિત સોસાયટી બેંક એકાઉન્ટ.",
        "માત્ર મેક-ઇન-ઇન્ડિયા (DCR-અનુરૂપ) પેનલ.",
      ],
      ghs_box_label: "સોસાયટી-વ્યાપી બચત",
      ghs_box_amount: "₹90 લાખ સુધી",
      ghs_box_caption: "એક હાઉસિંગ સોસાયટી માટે મહત્તમ PMSGY સબસિડી — ₹18,000 / kW ના દરે, 500 kW પર મહત્તમ.",
      ghs_example_title: "ગણતરી · 100-ફ્લેટ સોસાયટી",
      ghs_example_rows: [
        { label: "મંજૂર ક્ષમતા (3 kW × 100)", value: "300 kW" },
        { label: "પ્રોજેક્ટ ખર્ચ (₹50,000 / kW)", value: "₹1.50 કરોડ" },
        { label: "PMSGY સબસિડી", value: "₹54 લાખ" },
        { label: "વાર્ષિક બિલ બચત", value: "~₹22 લાખ" },
      ],
      ghs_example_net_label: "સોસાયટીનો ચોખ્ખો ખર્ચ",
      ghs_example_net_value: "₹96 લાખ",
      ghs_example_payback: "4–5 વર્ષમાં પેબેક · 20+ વર્ષ મફત વીજળી",
      ghs_documents_label: "સોસાયટી પાસેથી જરૂરી દસ્તાવેજો",
      ghs_documents: [
        "સોસાયટી નોંધણી પ્રમાણપત્ર + PAN",
        "છેલ્લા 3 મહિનાનું કોમન-એરિયા વીજળી બિલ",
        "સોસાયટી બેંક પાસબુક",
        "પ્રોજેક્ટ મંજૂરીનો AGM/EGM ઠરાવ",
        "બિલ્ડિંગ પ્લાન અને રૂફટોપ લેઆઉટ",
      ],
    },
    calc: {
      eyebrow: "બચત કૅલ્ક્યુલેટર",
      title_pre: "તમે કેટલી",
      title_hl: "બચત કરશો",
      title_post: " તે જુઓ — શરૂ કરવા + અને − દબાવો.",
      subtitle: "તમારા ઉપકરણો પસંદ કરો, કલાકો ગોઠવો — અમે લાઇવ સબસિડી અને ચુકવણી અંદાજ સાથે યોગ્ય કદની સોલાર સિસ્ટમ સૂચવીશું.",
      tab_res: "આવાસીય",
      tab_agri: "ખેતી",
      appliances: "તમારા ઉપકરણો",
      pincode_placeholder: "પિનકોડ",
      tariff: "ટેરિફ ₹{rate}/એકમ",
      detected: "શોધાયું",
      default_hours: "ડિફૉલ્ટ {h}કલાક/દિવસ",
      hours_day: "કલાક/દિવસ",
      snapshot: "તમારો સોલાર સ્નેપશૉટ",
      daily: "દૈનિક",
      monthly_units: "માસિક એકમો",
      current_bill: "હાલનું બિલ",
      recommended: "ભલામણ",
      investment: "રોકાણ વિગત",
      system_cost: "સિસ્ટમ ખર્ચ",
      pm_subsidy: "PM સૂર્ય ઘર સબસિડી",
      kusum_subsidy: "PM-KUSUM સબસિડી (~30%)",
      net_investment: "ચોખ્ખું રોકાણ",
      monthly_savings: "માસિક બચત",
      payback: "ચુકવણી",
      co2_yr: "CO₂/વર્ષ",
      cta_quote: "મારો {kw} kW કોટ",
      disclaimer: "અંદાજો 4 સૂર્ય-કલાક/દિવસ અને ₹{rate}/એકમ ટેરિફ પર આધારિત. અંતિમ ડિઝાઇન મફત સાઇટ મુલાકાત પછી.",
      appliance_labels: {
        fan: "છત પંખો",
        tube: "ટ્યૂબ / LED લાઇટ",
        ac: "એર કન્ડિશનર (1.5T)",
        fridge: "ફ્રિજ",
        tv: "ટેલિવિઝન",
        geyser: "ગીઝર",
        washer: "વૉશિંગ મશીન",
        microwave: "માઇક્રોવેવ",
        computer: "કમ્પ્યુટર / લૅપટોપ",
        pump_home: "પાણી પંપ (1 HP)",
        pump_3hp: "પાણી પંપ (3 HP)",
        pump_5hp: "પાણી પંપ (5 HP)",
        pump_75hp: "પાણી પંપ (7.5 HP)",
        pump_10hp: "સબમર્સિબલ (10 HP)",
        lights: "ખેતી લાઇટિંગ",
        chaffcutter: "ચાફ કટર",
      },
    },
    workflow: {
      eyebrow: "અમારી પ્રક્રિયા",
      title_pre: "છ પગલાં.",
      title_hl: "શૂન્ય",
      title_post: "મુશ્કેલી.",
      subtitle: "પ્રથમ કૉલથી 25 વર્ષની બચત સુધી, સમગ્ર પ્રવાસ પૂર્વોદય સંભાળે છે. આ રીતે થાય છે:",
      activities: "પ્રવૃત્તિઓ",
      deliverables: "વિતરણો",
      outcome: "પરિણામ",
      steps: [
        { title: "સાઇટ મુલાકાત અને શક્યતા", activities: "છાયા વિશ્લેષણ, છત તપાસ, લોડ અભ્યાસ, વીજ બિલ સમીક્ષા.", deliverables: "સિસ્ટમ કદ અને ઉત્પાદન અંદાજ સાથે શક્યતા અહેવાલ.", outcome: "તમારી છત સોલાર માટે તૈયાર છે તેવો વિશ્વાસ." },
        { title: "ડિઝાઇન અને દરખાસ્ત", activities: "3D રૂફટોપ લેઆઉટ, માળખાકીય + ઇલેક્ટ્રિકલ SLD, પેનલ અને ઇન્વર્ટર પસંદગી.", deliverables: "BOM, સમય-ક્રમ અને સબસિડી અંદાજ સાથે વિગતવાર દરખાસ્ત.", outcome: "પારદર્શક ભાવ, કોઈ આશ્ચર્ય નથી." },
        { title: "મંજૂરી અને સબસિડી અરજી", activities: "નેટ-મીટર અરજી, DISCOM સંપર્ક, PM સૂર્ય ઘર / KUSUM ફાઇલિંગ.", deliverables: "નેટ મીટરિંગ મંજૂરી + સબસિડી નોંધણી.", outcome: "પેપરવર્ક સંપૂર્ણપણે સંભાળવામાં આવ્યું." },
        { title: "ઇન્સ્ટોલેશન", activities: "માળખું માઉન્ટિંગ, પેનલ મૂકવું, કેબલિંગ, ઇન્વર્ટર કમિશનિંગ.", deliverables: "સંપૂર્ણ ઇન્સ્ટોલ, લેબલ અને પરીક્ષિત રૂફટોપ સિસ્ટમ.", outcome: "સામાન્ય રીતે 5–7 દિવસમાં વીજળી." },
        { title: "કમિશનિંગ અને ગો-લાઇવ", activities: "DISCOM તપાસ, નેટ મીટર ઇન્સ્ટોલેશન, ઍપ ઓનબોર્ડિંગ.", deliverables: "હેન્ડઓવર પેક + મોનિટરિંગ ઍપ લૉગિન.", outcome: "તમારું મીટર પાછળની દિશામાં ફરવા લાગે છે." },
        { title: "વેચાણ પછી અને દેખરેખ", activities: "રિમોટ પ્રદર્શન દેખરેખ, સમયાંતરે સફાઈ, AMC સહાય.", deliverables: "વાર્ષિક પ્રદર્શન અહેવાલો, અગ્રતા હેલ્પલાઇન.", outcome: "25 વર્ષનો ચિંતા-મુક્ત સૂર્યપ્રકાશ." },
      ],
    },
    faq: {
      eyebrow: "વારંવાર પૂછાતા પ્રશ્નો",
      title_pre: "દરેકના પ્રશ્નો —",
      title_hl: "પ્રામાણિક જવાબો.",
      items: [
        { q: "સોલાર સિસ્ટમ માટે કેટલી છતની જગ્યા જોઈએ?", a: "સામાન્ય નિયમ: દરેક 1 kW માટે ~80–100 ચો.ફૂ. છાંયાવગરનું ક્ષેત્ર. 3 kW હોમ સિસ્ટમ 300 ચો.ફૂ. છત પર સરળતાથી આવી જાય છે." },
        { q: "ચોમાસામાં કે વાદળછાંયા દિવસોમાં સોલાર કામ કરે છે?", a: "હા. પેનલ વિખરાયેલા પ્રકાશમાં પણ (પીકના ~10–25%) ઉત્પાદન કરે છે. નેટ-મીટરિંગથી ગ્રીડમાંથી લઈ શકો." },
        { q: "PM સૂર્ય ઘર સબસિડી પ્રક્રિયા શું છે?", a: "તમે રાષ્ટ્રીય પોર્ટલ પર નોંધણી કરો, અમે અરજી કરીએ, MNRE-સૂચિબદ્ધ વેન્ડર (પૂર્વોદય) દ્વારા ઇન્સ્ટોલ, DISCOM તપાસ પછી સીધું બેંક ટ્રાન્સફર." },
        { q: "ઇન્સ્ટોલેશનમાં કેટલો સમય લાગે?", a: "માનક 3 kW આવાસીય ઇન્સ્ટોલેશન 3–5 દિવસ. મંજૂરી સાથે કુલ 4–6 અઠવાડિયા." },
        { q: "સોલાર સિસ્ટમની શું જાળવણી જોઈએ?", a: "ખૂબ ઓછી. ધૂળવાળા વિસ્તારોમાં 1–2 મહિને સફાઈ અને વાર્ષિક તપાસ. દરેક ઇન્સ્ટોલેશન સાથે મફત 1-વર્ષનું AMC." },
        { q: "ખેડૂતો ખરેખર PM-KUSUM થી આવક મેળવી શકે?", a: "ચોક્કસ. કોમ્પ-A અને C હેઠળ વધારાની સોલાર વીજળી DISCOM નિશ્ચિત ટેરિફ પર ખરીદે છે — પંપ માટે મફત દિવસની વીજળી સાથે સ્થિર વધારાની આવક." },
      ],
    },
    footer: {
      tag: "ભવિષ્યને ઉજ્જવળ બનાવો",
      desc: "ભારતીય ઘરો અને ખેતરોને બિલ ઘટાડવા અને ઊર્જા પરિવર્તન ઝડપી બનાવવામાં મદદ કરતી MNRE-સૂચિબદ્ધ રૂફટોપ સોલાર EPC કંપની.",
      explore: "શોધો",
      contact: "સંપર્ક",
      rights: "© {year} પૂર્વોદય સોલાર. બધા અધિકારો સુરક્ષિત.",
      built: "સ્વચ્છ ભારત માટે ☀ સાથે બનાવ્યું.",
    },
    lead: {
      badge: "મફત અને કોઈ બંધન નહીં",
      title_default: "મફત રૂફટોપ કોટ મેળવો",
      subtitle_default: "તમારી વિગતો શેર કરો — એક પૂર્વોદય નિષ્ણાત 24 કલાકમાં કૉલ કરશે.",
      title_calc: "તમારો વ્યક્તિગત કોટ મેળવો",
      title_idle: "હજુ જોઈ રહ્યા છો? અમે મદદ કરીએ.",
      subtitle_idle: "તમારી વિગતો શેર કરો — તમારા ફોન પર કસ્ટમ સોલાર બચત મોકલીશું.",
      full_name: "પૂર્ણ નામ *",
      name_ph: "તમારું નામ",
      phone: "ફોન *",
      phone_ph: "+91 9XXXX XXXXX",
      pincode: "પિનકોડ",
      email: "ઇમેઇલ",
      email_ph: "you@example.com",
      i_need: "મને સોલાર જોઈએ",
      seg_home: "ઘર / આવાસીય",
      seg_farm: "ખેતર / ખેતી",
      seg_commercial: "વાણિજ્યિક / ઔદ્યોગિક",
      anything_else: "બીજું કંઈ?",
      msg_ph: "છત કદ, હાલનું બિલ, વગેરે.",
      submit: "મને મફત કૉલ-બેક જોઈએ",
      submitting: "મોકલી રહ્યું છે…",
      thanks_title: "તમે અમારી યાદીમાં છો!",
      thanks_desc: "એક સોલાર સલાહકાર ટૂંક સમયમાં સંપર્ક કરશે.",
      toast_ok: "આભાર! ટૂંક સમયમાં કૉલ કરીશું.",
      toast_required: "નામ અને ફોન આવશ્યક",
      toast_fail: "મોકલી શક્યા નથી. કૃપા કરીને ફરી પ્રયાસ કરો.",
      consent: "સબમિટ કરીને, તમે પૂર્વોદય સોલાર દ્વારા સંપર્ક માટે સંમતિ આપો છો.",
    },
    whatsapp: { label: "અમારી સાથે ચેટ કરો" },
    common: { language: "ભાષા" },
    ts: {
        eyebrow: "પુર્વોદય શા માટે",
        title_pre: "એક ગંભીર ભાગીદાર —",
        title_hl: "ભારતના સૌર સંક્રમણ માટે",
        subtitle: "Purvodaya Solar is built to deliver clean energy across Residential rooftops, Housing Societies and Commercial & Industrial facilities — with end-to-end ownership of design, execution, subsidy filing and 25-year operations.",
        p1_eyebrow: "એનર્જી ટ્રાન્ઝિશન પાર્ટનર",
        p1_title: "ભારતના સૌર-આધારિત ઊર્જા સંક્રમણનો વિશ્વાસપાત્ર ભાગીદાર",
        p1_desc: "દેશની રહેણાંક, હાઉસિંગ સોસાયટી અને કોર્પોરેટ સ્વચ્છ ઊર્જા યાત્રા માટે ખાસ બનાવેલ — લાંબા ગાળાની કામગીરી માટે, ક્ષણિક દેખાવ માટે નહીં.",
        p2_eyebrow: "PM Surya Ghar · PM-KUSUM ફાઈલર",
        p2_title: "કેન્દ્રીય સૌર સબસિડીના અધિકૃત ફાઈલર",
        p2_desc: "અમે વ્યક્તિગત ઘરો અને હાઉસિંગ સોસાયટીઓ માટે PM Surya Ghar અને ખેડૂતો માટે PM-KUSUM ફાઈલ કરીએ છીએ — તમામ કાગળકામ અમારી ટીમ સંભાળે છે, કોઈ વચેટિયા નહીં.",
        p3_eyebrow: "એન્ડ-ટુ-એન્ડ EPC + 25-વર્ષ O&M",
        p3_title: "એક જવાબદાર ભાગીદાર — ડિઝાઇનથી શરૂ થવા સુધી અને 25 વર્ષની સંભાળ",
        p3_desc: "MNRE-અનુરૂપ ડિઝાઇન, Tier-1 મોડ્યુલ્સ, પ્રમાણિત ઇન્સ્ટોલેશન ટીમ, DISCOM સંપર્ક, અને 25-વર્ષની પરફોર્મન્સ ગેરંટી — એક જ છત હેઠળ.",
        promise_label: "અમારું વચન",
        promise1: "Tier-1 મોડ્યુલ્સ · 25-વર્ષની પરફોર્મન્સ વોરંટી",
        promise2: "MNRE-અનુરૂપ ઈન્જિનિયરિંગ અને સુરક્ષા પ્રથાઓ",
        promise3: "પારદર્શક કોટેશન · કોઈ છુપો ખર્ચ નથી",
        promise4: "ત્રિમાસિક પરફોર્મન્સ અને સફાઈ રિપોર્ટ્સ",
      },
    res: {
        hero_badge: "રહેણાંક રૂફટોપ સોલાર",
        hero_title_a: "તમારું AC, ફ્રિજ, ગીઝર —",
        hero_title_b: "સૂર્યથી મફતમાં ચલાવો.",
        hero_subtitle: "India's electricity tariffs rise 4–6% every year. A Purvodaya rooftop slashes your bill by 70–95% from day one — and pays for itself in just 4–5 years.",
        cta_quote: "મફત રૂફટોપ કોટ મેળવો",
        cta_calc: "તમારી બચતનો અંદાજ લગાવો",
        stat_subsidy: "મહત્તમ કેન્દ્રીય સબસિડી",
        stat_reduction: "બિલ ઘટાડો",
        stat_payback: "સામાન્ય પેબેક",
        stat_warranty: "પરફોર્મન્સ વોરંટી",
        app_eyebrow: "Where your bill actually goes",
        app_title_a: "Every appliance,",
        app_title_b: "powered by sunlight.",
        app_subtitle: "A typical Indian home spends ₹4,000–₹8,000/month on these appliances. Solar takes that to near-zero during daylight hours — and net-metering covers your nights.",
        app_consumption: "Consumption",
        app_current_bill: "Current bill",
        app_note: "Estimates basis a ₹7/unit average residential tariff. Solar eliminates the daytime portion of each line item entirely.",
        app_ac: "Air Conditioner (1.5T)",
        app_ac_units: "Up to 12 units/day",
        app_fridge: "Refrigerator",
        app_fridge_units: "3.5 units/day",
        app_geyser: "Geyser",
        app_geyser_units: "4 units/day",
        app_washer: "Washing Machine",
        app_washer_units: "2 units/cycle",
        app_tv: "TV + Set-top",
        app_tv_units: "1.5 units/day",
        app_fans: "Ceiling Fans (4)",
        app_fans_units: "3 units/day",
        app_lights: "LED Lights (10)",
        app_lights_units: "1 unit/day",
        b_eyebrow: "Why Purvodaya for your home",
        b_title_a: "Six homeowner wins,",
        b_title_b: "no fine print.",
        b1_t: "Cut monthly bills by 70–95%",
        b1_d: "Your AC, fridge, geyser, washing machine, fans and lights all run on free sunlight during the day. Most homes see ₹3,000–₹8,000/month drop in their electricity bill from day one.",
        b2_t: "Up to ₹78,000 PM Surya Ghar subsidy",
        b2_d: "The Government of India transfers up to ₹78,000 directly to your bank account after installation. We handle the entire portal application, paperwork and DISCOM liaisoning — no chasing, no middlemen.",
        b3_t: "Free 300 units/month for eligible families",
        b3_d: "Under PM Surya Ghar Muft Bijli Yojana, eligible households also get free electricity benefit on top of generation savings — making solar effectively zero-cost for many homes.",
        b4_t: "25-year warranty, 5-year payback",
        b4_d: "Tier-1 mono PERC panels with 25-year performance warranty and an inverter under 10-year guarantee. After your 4–5 year payback, every unit generated is essentially free electricity for the next two decades.",
        b5_t: "Net-metering — sell extra back",
        b5_d: "Surplus units flow back to the grid and your DISCOM gives you credit. Generate more than you use? You can earn small monthly credits while keeping the lights on through nights and monsoon days.",
        b6_t: "Future-proof your home",
        b6_d: "Add a battery (BESS), an EV charger or a solar water heater later — every Purvodaya rooftop is designed to scale with your needs as you electrify your lifestyle.",
        s_eyebrow: "The Purvodaya 4-step rollout",
        s_title_a: "From",
        s_title_hl: "first call",
        s_title_b: "to switch-on — in 4–6 weeks.",
        s1_t: "Free site visit",
        s1_d: "Our engineer maps your rooftop, shadow analysis & sanctioned load.",
        s2_t: "Quote + subsidy filing",
        s2_d: "We file PM Surya Ghar on your behalf — you sign one digital form.",
        s3_t: "DISCOM & net-meter approval",
        s3_d: "All approvals handled by Purvodaya — typically 3–4 weeks.",
        s4_t: "Install & switch-on",
        s4_d: "3–5 days on site. Same evening — your home runs on sun.",
        f_eyebrow: "Homeowner FAQs",
        f_title_a: "Quick answers for",
        f_title_hl: "first-time buyers.",
        f1_q: "Will solar run my AC and geyser in summer?",
        f1_a: "Yes — and that's where the biggest savings come from. A 5 kW rooftop comfortably runs a 1.5T inverter AC plus geyser plus all daily home loads during daylight hours. We size your system based on your appliance profile.",
        f2_q: "What if I shift homes in 4–5 years?",
        f2_a: "Modern solar systems are modular and portable for properties you own. For most homeowners the bill savings of just 2–3 years already exceed the moving cost. Plus, a solar-equipped home typically commands a 3–5% higher resale.",
        f3_q: "Do I really save ₹78,000 — or is that just marketing?",
        f3_a: "It's a real Direct-Bank-Transfer from the Government of India for 3 kW+ residential systems under PM Surya Ghar. Lower kW sizes get proportionally smaller subsidy. Your money arrives in 30–60 days post-commissioning.",
        final_a: "Stop paying your DISCOM.",
        final_hl: "Start paying yourself.",
        final_sub: "Share your details — our solar advisor will design your home system and file your PM Surya Ghar subsidy, all for free.",
        final_cta: "Book a Free Site Visit",
        back_home: "હોમ પર પાછા જાઓ",
        lead_title: "Free Rooftop Solar Quote",
        lead_subtitle: "Share your details — Purvodaya advisor will call you within 24 hours.",
      },
    com: {
        hero_badge: "વાણિજ્યિક અને ઔદ્યોગિક સોલાર",
        hero_title_a: "Power your business with the",
        hero_title_hl: "સૂર્યથી",
        hero_title_b: "—",
        hero_title_c: "cut energy costs, lock margins.",
        hero_subtitle: "MW-scale rooftop & ground-mount solar for factories, warehouses, offices and hospitality. Engineered for ROI, built for 25 years.",
        cta_quote: "વ્યવસાય દરખાસ્તની વિનંતી કરો",
        cta_calc: "Estimate Your Savings",
        stat_reduction: "બિલ ઘટાડો",
        stat_payback: "સામાન્ય પેબેક",
        stat_warranty: "પરફોર્મન્સ વોરંટી",
        stat_upfront: "Upfront in OPEX model",
        b_eyebrow: "Why Purvodaya for Business",
        b_title_a: "Six measurable benefits,",
        b_title_hl: "delivered.",
        b1_t: "Slash power costs by 50–70%",
        b1_d: "Replace expensive grid power (₹8–12/kWh) with self-generated solar at ₹2.5–3.5/kWh — directly improving your EBITDA margin from day one.",
        b2_t: "40% Accelerated Depreciation",
        b2_d: "Claim 40% AD on the entire project cost in Year 1 under Section 32 of the Income Tax Act. Effective payback in 3–4 years for tax-paying entities.",
        b3_t: "Hedge against tariff hikes",
        b3_d: "DISCOM tariffs rise 4–6% every year. A 25-year solar PPA locks your generation cost, protecting margins through the next two decades.",
        b4_t: "ESG & RE100 reporting",
        b4_d: "Carbon-offset certificates, REC eligibility and ESG-grade documentation — built into every Purvodaya installation for your BRSR and sustainability reports.",
        b5_t: "Zero CAPEX & OPEX models",
        b5_d: "Choose CAPEX ownership or our 25-year OPEX/PPA model with no upfront cost — pay only for the units consumed at a fixed, discounted tariff.",
        b6_t: "End-to-end EPC + O&M",
        b6_d: "Single point of accountability — design, MNRE-empanelled execution, net-metering liaisoning, performance guarantees and 25-year O&M.",
        seg_eyebrow: "Built for Every Sector",
        seg_title_a: "From",
        seg_title_hl: "factory floors",
        seg_title_b: "to corporate campuses.",
        seg1_t: "Manufacturing & MSME",
        seg1_p1: "Captive rooftop & ground-mount solar",
        seg1_p2: "Power for high-load CNC, compressors, HVAC",
        seg1_p3: "Open Access wheeling for 1 MW+ plants",
        seg2_t: "IT Parks & Offices",
        seg2_p1: "Carport + rooftop hybrid systems",
        seg2_p2: "EV-ready charging infrastructure",
        seg2_p3: "LEED & IGBC platinum compliance",
        seg3_t: "Warehousing & Logistics",
        seg3_p1: "Large clear-span rooftop coverage",
        seg3_p2: "Cold-storage compatible BESS",
        seg3_p3: "Refrigeration load offset 80%+",
        seg4_t: "Hospitality & Retail",
        seg4_p1: "Hot water + power combo systems",
        seg4_p2: "Brand-grade aesthetic mounting",
        seg4_p3: "Visible sustainability for guests",
        roi_eyebrow: "A real ROI snapshot",
        roi_title_a: "500 kW factory rooftop.",
        roi_title_hl: "3.2-year payback.",
        roi_subtitle: "Sample model for a tax-paying manufacturing unit consuming ~80,000 units/month. CAPEX route with 40% Accelerated Depreciation in Year-1, MAT-adjusted.",
        roi1_k: "₹2.25 Cr",
        roi1_v: "Project CAPEX",
        roi2_k: "₹90 L",
        roi2_v: "Year-1 AD tax benefit",
        roi3_k: "₹75 L+",
        roi3_v: "Annual energy savings",
        roi4_k: "12,500 t",
        roi4_v: "Lifetime CO₂ offset",
        roi_cta: "Get a Tailored ROI Model",
        roi_year1: "Year-1 cashflow",
        roi_savings: "₹1.65 Cr",
        roi_savings_caption: "savings + AD benefit",
        p_eyebrow: "Our 6-Step Process",
        p_title_a: "From audit to",
        p_title_hl: "25-year operations.",
        p1_t: "Site & Tariff Audit",
        p1_d: "Free shadow analysis, load profile and ROI model for your facility.",
        p2_t: "Custom Proposal",
        p2_d: "CAPEX / OPEX options with bankable financial models and AD benefit.",
        p3_t: "Approvals & Liaisoning",
        p3_d: "DISCOM net-metering, CEIG, fire NOC — handled end-to-end.",
        p4_t: "EPC Execution",
        p4_d: "Tier-1 modules, MNRE-empanelled crew, IEC-certified BoS.",
        p5_t: "Commissioning & Handover",
        p5_d: "Performance test, monitoring app, training for your team.",
        p6_t: "25-Year O&M",
        p6_d: "Quarterly cleaning, generation guarantee, remote monitoring.",
        final_a: "Ready to decarbonise your",
        final_hl: "balance sheet?",
        final_sub: "Share your last-3-month electricity bill — we'll send back a site-specific proposal with CAPEX, OPEX and ROI within 48 hours.",
        final_cta: "Request Business Proposal",
        back_home: "હોમ પર પાછા જાઓ",
        lead_title: "Get a Business Solar Proposal",
        lead_subtitle: "Share your details — we'll send a tailored CAPEX/OPEX proposal within 48 hours.",
      },
    soc: {
        hero_badge: "હાઉસિંગ સોસાયટી સોલાર",
        hero_title_a: "One society, one rooftop,",
        hero_title_hl: "a thousand sunlit homes.",
        hero_subtitle: "Apartment complexes & co-operative societies — cut common-area bills, get PM Surya Ghar subsidy for every flat, and become a green-certified address.",
        cta_quote: "સોસાયટી મુલાકાતની વિનંતી કરો",
        cta_calc: "Estimate Your Savings",
        stat_subsidy: "મહત્તમ કેન્દ્રીય સબસિડી",
        stat_reduction: "બિલ ઘટાડો",
        stat_payback: "સામાન્ય પેબેક",
        stat_warranty: "પરફોર્મન્સ વોરંટી",
        b_eyebrow: "Built for RWAs",
        b_title_a: "Six wins for your",
        b_title_hl: "society & residents.",
        b1_t: "Slash common-area bills by 80–95%",
        b1_d: "Lifts, water pumps, lobby & basement lighting, gym, club-house — all powered by rooftop solar. Society maintenance dues drop, residents win.",
        b2_t: "PM Surya Ghar on every flat",
        b2_d: "Each flat-owner can individually claim up to ₹78,000 central subsidy under PM Surya Ghar for their rooftop share — society-level aggregation makes it effortless.",
        b3_t: "RWA-friendly governance",
        b3_d: "Transparent EGM-ready proposals, society resolution templates, share-of-generation accounting and per-flat billing dashboards.",
        b4_t: "Solar street lights & pathway lighting",
        b4_d: "Replace society compound lighting with stand-alone solar lamps — zero wiring, zero electricity bill, dusk-to-dawn auto operation.",
        b5_t: "Solar water heating for towers",
        b5_d: "Central solar water heating systems for high-rises — endless hot water for residents at a fraction of geyser running costs.",
        b6_t: "ESG-grade green building credits",
        b6_d: "IGBC / GRIHA green-society certification eligibility, carbon-offset reports and a real story for your society's brochures.",
        st_eyebrow: "How it works for societies",
        st_title_a: "From",
        st_title_hl: "first AGM",
        st_title_b: "to switch-on, in 60 days.",
        st1_t: "Initial Society Meeting",
        st1_d: "We present the opportunity to the managing committee with a free shadow analysis.",
        st2_t: "Detailed Proposal & EGM",
        st2_d: "Bankable financial model, voting templates, FAQs prepared — we attend the EGM.",
        st3_t: "Approvals & Subsidy",
        st3_d: "We handle DISCOM, society NOC, and the PM Surya Ghar portal for every interested flat.",
        st4_t: "Installation & Commissioning",
        st4_d: "Phased, non-disruptive installation completed in 30–45 days for a typical society.",
        o_eyebrow: "A full clean-energy stack",
        o_title: "We bundle every solar need a society has.",
        o1: "Common-area & individual flat rooftop solar (with PM Surya Ghar liaisoning)",
        o2: "Solar street & pathway lighting for compounds",
        o3: "Central solar water-heating for towers and clubhouses",
        o4: "EV charging points for residents — solar-powered",
        o5: "Lithium BESS backup for lifts during outages",
        o6: "25-year O&M with quarterly performance reports",
        o_cta: "Book a Society Walkthrough",
        o_badge: "Bundled offering",
        o_badge_title: "Solar + Lights + Water Heating",
        o_badge_caption: "in one society contract",
        fq_eyebrow: "FAQs",
        fq_title_a: "Quick answers for",
        fq_title_hl: "society committees.",
        fq1_q: "Who pays — society or individual flat owners?",
        fq1_a: "Both models work. Common-area load is funded by the society from corpus or via a small per-flat contribution; individual flats can additionally opt in for net-metering and claim PM Surya Ghar subsidy themselves.",
        fq2_q: "Will it work for our 12-storey high-rise with limited rooftop?",
        fq2_a: "Yes — we design hybrid systems combining rooftop, podium, parking shed and façade solutions. For very tall towers, a smaller rooftop array still fully covers the common-area load.",
        fq3_q: "How do you handle the AGM/EGM approvals?",
        fq3_a: "We provide ready-to-circulate technical & financial proposals, voting templates, and our team attends the EGM to answer resident queries directly.",
        fq4_q: "What about maintenance after installation?",
        fq4_a: "Every project comes with a 25-year performance guarantee and a 5-year free O&M contract — quarterly cleaning, annual thermography and remote monitoring.",
        final_a: "Let's make your society",
        final_hl: "India's next solar address.",
        final_sub: "Share your society details — our team will reach out for a free walkthrough and prepare an AGM-ready proposal.",
        final_cta: "Request Society Visit",
        back_home: "હોમ પર પાછા જાઓ",
        lead_title: "Request a Society Walkthrough",
        lead_subtitle: "Share your details — our team will set up a free society visit.",
      },

  },
};
